/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState, useContext, useCallback, useMemo } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { API_URL, SOCKET_URL } from '../utils/runtimeConfig';
import Modal from 'react-modal';

import './css/LoadingSpinner.css';
import './css/IsometricGrid.css';
import { ErrorContext } from '../context/ErrorContext';
import { AuthContext } from '../context/AuthContext';
import { drawBaseTile, drawTile, drawTerritory } from '../utils/drawFunctions';
import useSocket from '../hooks/useSocket';

// tile information - mainly just the size right now to support additions and conflict checks, will include image URL in future
import tileInformation from '../utils/tileInformation.json';

// add tile modals
import AddCityModal from '../modals/tileModals/AddCityModal';
import AddAllianceBuildingModal from '../modals/tileModals/AddAllianceBuildingModal';
import AddEliteMineModal from '../modals/tileModals/AddEliteMineModal';
import AddBenefitBuildingModal from '../modals/tileModals/AddBenefitBuildingModal';

// tile details modals
import TileDetailsModal from '../modals/tileModals/TileDetailsModal';

// navigation and deletion controls
import NavigationModal from '../modals/NavigationModal';
import FindCommanderModal from '../modals/FindCommanderModal';
import images from '../utils/loadImages'; // Assuming you have a module to load images

// other components
import SubMenuBar from "../components/SubMenuBar/SubMenuBar";
import PlanningMode from '../components/PlanningMode/PlanningMode';
import HasPermission from '../utils/HasPermission'
import { TILE_ENTITY_OPTIONS, getBaseTileType } from '../utils/tileEntityOptions';
import {
  buildTileConstraintPlacementIndex,
  findConstraintPlacement,
  getPlacementConstraint
} from '../utils/tilePlacementConstraints';
import { gridToScreen, screenToGrid } from '../utils/isoProjection';
import { FaTools, FaMapMarkedAlt, FaSearch, FaUndo, FaRedo, FaObjectGroup, FaCopy, FaCut, FaMagic, FaPlus, FaTimes, FaKeyboard, FaPen, FaTrashAlt } from 'react-icons/fa';

const apiUrl = API_URL
const socketUrl = SOCKET_URL
const ALLIANCE_TERRITORY_TYPES = ['flag', 'fort', 'allianceHall'];
const OPTIMIZATION_ANCHOR_TYPES = ['flag', 'allianceHall'];
const BENEFIT_SUBTYPE_LABELS = tileInformation?.benefit?.subTypes || {};
const BENEFIT_SUBTYPE_KEYS = Object.keys(BENEFIT_SUBTYPE_LABELS || {});
const DEFAULT_BENEFIT_SUBTYPE = BENEFIT_SUBTYPE_KEYS[0] || 'lab';
const DRAWING_TOOL_OPTIONS = [
  { key: 'line', label: 'Straight Line' },
  { key: 'free', label: 'Free Draw' },
  { key: 'text', label: 'Text Box' },
];
const DRAWING_TEXT_MAX_LENGTH = 128;
const DEFAULT_DRAWING_TEXT_COLOR = '#f8fafc';
const DEFAULT_DRAWING_TEXT_BACKGROUND = '#1e293b';
const BENEFIT_OVERLAY_COLORS = {
  lab: '#0ea5e9',
  militaryArea: '#f97316',
  warFactory: '#ef4444',
  mechanicShop: '#a855f7'
};
const CONFIRMABLE_LOCATION_TYPES = ['city', 'flag', 'allianceHall', 'fort'];
const CONFIRMED_BUILDING_OVERLAY_FILL = '#86efac';
const CONFIRMED_BUILDING_OVERLAY_STROKE = '#16a34a';
const CONFIRMED_TERRITORY_GREEN_RGB = { r: 22, g: 163, b: 74 };
const CONFIRMED_TERRITORY_BLUE_RGB = { r: 37, g: 99, b: 235 };
const IMPORTANT_COORDINATE_OVERLAY_OPTIONS = [
  { key: 'benefit', label: 'Benefit Buildings', color: '#22c55e' },
  { key: 'eliteMine', label: 'Elite Mines', color: '#f59e0b' },
  { key: 'alliance', label: 'Alliance Buildings', color: '#38bdf8' }
];
const IMPORTANT_COORDINATE_OVERLAY_COLORS = IMPORTANT_COORDINATE_OVERLAY_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.color;
  return acc;
}, {});
const IMPORTANT_COORDINATE_ALLIANCE_TYPES = ['flag', 'fort', 'allianceHall'];
const IMPORTANT_COORDINATE_ALLIANCE_LABELS = {
  flag: 'Flag',
  fort: 'Fort',
  allianceHall: 'Main Hall'
};
const IMPORTANT_COORDINATE_COPY_ICON_GAP = 8;
const IMPORTANT_COORDINATE_COPY_ICON_PADDING = 6;
const BORDER_ALLIANCE_BUILDING_TYPES = ['flag', 'allianceHall', 'fort', 'institute'];
const BORDER_FILL_ALPHA = 0.11;
const BORDER_STROKE_ALPHA = 0.9;
const BORDER_VERTEX_RADIUS = 6;
const BORDER_MIDPOINT_RADIUS = 6;
const BORDER_CLOSE_DISTANCE = 1.5;
const BORDER_VISIBILITY_STORAGE_PREFIX = 'aoz_border_visibility_v1';
const TERRITORY_CAPTURE_REFRESH_DEBOUNCE_MS = 450;
const TERRITORY_CAPTURE_CONTESTED_FILL = 'rgba(127, 29, 29, 0.88)';
const TERRITORY_CAPTURE_CONTESTED_STROKE = 'rgba(69, 10, 10, 0.98)';
const ELITE_MINE_LEVELS = [1, 2, 3, 4, 5];
const RESOURCE_DISTRIBUTION_BANDS = [
  {
    key: 'none',
    label: 'Nothing (0:0)',
    minCorner: 0,
    fill: 'rgba(30, 58, 138, 0.22)',
    stroke: 'rgba(96, 165, 250, 0.55)',
    legendColor: '#1e3a8a'
  },
  {
    key: 'level1',
    label: 'Level 1 (140:140)',
    minCorner: 140,
    fill: 'rgba(125, 211, 252, 0.23)',
    stroke: 'rgba(14, 116, 144, 0.5)',
    legendColor: '#7dd3fc'
  },
  {
    key: 'level2',
    label: 'Level 2 (240:240)',
    minCorner: 240,
    fill: 'rgba(34, 197, 94, 0.21)',
    stroke: 'rgba(21, 128, 61, 0.48)',
    legendColor: '#22c55e'
  },
  {
    key: 'level3',
    label: 'Level 3 (340:340)',
    minCorner: 340,
    fill: 'rgba(250, 204, 21, 0.21)',
    stroke: 'rgba(161, 98, 7, 0.5)',
    legendColor: '#facc15'
  },
  {
    key: 'level4',
    label: 'Level 4 (440:440)',
    minCorner: 440,
    fill: 'rgba(251, 146, 60, 0.2)',
    stroke: 'rgba(194, 65, 12, 0.5)',
    legendColor: '#fb923c'
  },
  {
    key: 'level5',
    label: 'Level 5 (530:530)',
    minCorner: 530,
    fill: 'rgba(239, 68, 68, 0.22)',
    stroke: 'rgba(153, 27, 27, 0.55)',
    legendColor: '#ef4444'
  }
];
const TILE_HOTKEY_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='];
const TILE_HOTKEY_ORDER = [
  'city',
  'sandBag',
  'farm',
  'flag',
  'allianceHall',
  'fort',
  'institute',
  'eliteMine',
  'benefit',
  'powerOre',
  'ruins',
  'deadSpace'
];
const TOOL_HOTKEY_DEFAULTS = {
  copy: 'mod+c',
  cut: 'mod+x',
  undo: 'mod+z',
  redo: 'mod+y',
  multiSelect: 'mod+m',
  cancelModes: 'escape'
};
const HOTKEY_STORAGE_KEY = 'aoz_hotkey_config_v1';
const MAP_SELECTED_NATION_STORAGE_KEY = 'aoz_map_selected_nation_v1';
const TILE_WINDOW_PADDING = 56;
const TILE_WINDOW_REFETCH_MARGIN = 12;
const TILE_WINDOW_FETCH_DEBOUNCE_MS = 260;
const SOCKET_PLACEMENT_PARENT_KEY_TTL_MS = 12000;
const POST_MUTATION_REFRESH_DEBOUNCE_MS = 180;
const OPTIMISTIC_PLACEMENT_STALE_MS = 60000;
const HIVE_FOOTPRINT_SIZE = 20;
const HIVE_RESULT_LIMIT = 5;
const HIVE_WEIGHT_DEFAULTS = {
  targetDistance: '0.7',
  centerDistance: '0.3'
};
const BASE_GRID_AUTO_HIDE_ZOOM_THRESHOLD = 0.9;
const DEAD_SPACE_BASE_TERRAIN_ONLY_ZOOM_THRESHOLD = BASE_GRID_AUTO_HIDE_ZOOM_THRESHOLD / 3;
const DEAD_SPACE_FETCH_MODE_ALL = 'all';
const DEAD_SPACE_FETCH_MODE_BASE_ONLY = 'baseOnly';
const HISTORY_MAX_ENTRIES = 100;
const OPTIMIZER_DEFAULTS = {
  eliteMine: '10',
  territory: '0.08',
  reach: '0.03',
  benefits: {
    lab: '6',
    militaryArea: '6',
    mechanicShop: '6',
    warFactory: '6',
  }
};

Modal.setAppElement('#root');

const buildDefaultTileHotkeys = () => {
  const bindings = {};
  TILE_HOTKEY_ORDER.forEach((tileType, index) => {
    const hotkey = TILE_HOTKEY_KEYS[index];
    if (hotkey) {
      bindings[hotkey] = tileType;
    }
  });
  return bindings;
};

const normalizeHotkeyCombo = (event) => {
  const key = String(event.key || '').toLowerCase();
  if (!key) return '';
  if (['control', 'meta', 'alt', 'shift'].includes(key)) return '';

  if (key === 'escape') return 'escape';
  if (key === ' ') return 'space';

  const parts = [];
  if (event.ctrlKey || event.metaKey) parts.push('mod');
  if (event.shiftKey) parts.push('shift');
  if (event.altKey) parts.push('alt');
  parts.push(key);
  return parts.join('+');
};

const isTextInputTarget = (target) => {
  const tagName = String(target?.tagName || '').toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target?.isContentEditable
  );
};

const bytesToHexString = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value.map((byte) => Number(byte).toString(16).padStart(2, '0')).join('');
  }
  if (typeof value === 'object') {
    const numericKeys = Object.keys(value)
      .filter((key) => /^\d+$/.test(key))
      .sort((left, right) => Number(left) - Number(right));
    if (!numericKeys.length) return '';
    return numericKeys
      .map((key) => Number(value[key]).toString(16).padStart(2, '0'))
      .join('');
  }
  return '';
};

const normalizeEntityId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (typeof value.$oid === 'string') return value.$oid;
    if (typeof value.toHexString === 'function') {
      try {
        return value.toHexString();
      } catch (error) {
        // continue to other strategies
      }
    }
    if (value.buffer && value.buffer.data) {
      const fromBufferData = bytesToHexString(value.buffer.data);
      if (fromBufferData) return fromBufferData;
    }
    if (value.id) {
      const fromIdBytes = bytesToHexString(value.id);
      if (fromIdBytes) return fromIdBytes;
    }
  }

  const asString = String(value);
  if (asString && asString !== '[object Object]') return asString;
  return '';
};

const clampDrawingText = (value) => String(value || '').slice(0, DRAWING_TEXT_MAX_LENGTH);
const getParentKeyFromTile = (tile = {}) => {
  const parentX = tile?.parentTile?.x ?? tile?.x;
  const parentY = tile?.parentTile?.y ?? tile?.y;
  return `${parentX},${parentY}`;
};

const buildBorderVisibilityStorageKey = (nationId, planId) => (
  `${BORDER_VISIBILITY_STORAGE_PREFIX}:${String(nationId || '')}:${String(planId || '')}`
);

// Function to generate the initial state for layers
const generateInitialLayersState = () => {
  const initialState = { baseGrid: true, territory: true, drawings: true };
  Object.keys(tileInformation).forEach(key => {
    initialState[key] = true;
  });
  return initialState;
};

const generateInitialMultiSelectFilters = () => {
  return TILE_ENTITY_OPTIONS.reduce((acc, option) => {
    acc[option.key] = true;
    return acc;
  }, {});
};

const generateInitialBenefitOverlayFilters = () => {
  return BENEFIT_SUBTYPE_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});
};

const generateInitialImportantCoordinateOverlayFilters = () => {
  return IMPORTANT_COORDINATE_OVERLAY_OPTIONS.reduce((acc, option) => {
    acc[option.key] = true;
    return acc;
  }, {});
};

const createEmptyTerritoryCaptureContested = () => ({
  benefit: [],
  eliteMine: []
});

const isConfirmableLocationType = (tileType = '') => (
  CONFIRMABLE_LOCATION_TYPES.includes(getBaseTileType(tileType))
);

const isGreenishTerritoryColor = (color = {}) => {
  const r = Number(color?.r);
  const g = Number(color?.g);
  const b = Number(color?.b);
  if (![r, g, b].every(Number.isFinite)) return false;
  return g >= 95 && g >= (r + 20) && g >= (b + 15);
};

const IsometricGrid = () => {
  //axios instance and contexts
  const axios = axiosInstance
  const { showError, showInfo } = useContext(ErrorContext);
  const { user } = useContext(AuthContext);
  const currentUserRole = String(user?.role || '');
  const currentUserNationId = String(user?.nation?._id || user?.nation || '');
  const baseCanWriteTiles = HasPermission("writeTiles");
  const baseCanDeleteTiles = HasPermission("deleteTiles");
  const baseCanWritePlans = HasPermission("writePlans");
  const [selectedNation, setSelectedNation] = useState(null)
  const [nations, setNations] = useState([])

  const canvasRef = useRef(null);
  const [tiles, setTiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const tileSize = 64;
  const [offset, setOffset] = useState({ x: 0, y: 600 * tileSize / 2 });
  const [centerCoords, setCenterCoords] = useState({ gridX: 0, gridY: 0 });
  const [mapStageHeight, setMapStageHeight] = useState(0);

  const [highlightedTile, setHighlightedTile] = useState(null);
  const [isMultiSelectLocked, setIsMultiSelectLocked] = useState(false);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [isCutMode, setIsCutMode] = useState(false);
  const [isQuickAddPanelOpen, setIsQuickAddPanelOpen] = useState(false);
  const [copiedTileTemplates, setCopiedTileTemplates] = useState([]);
  const [copiedSourceParentKeys, setCopiedSourceParentKeys] = useState([]);
  const [cutTileTemplates, setCutTileTemplates] = useState([]);
  const [cutSourceParentKeys, setCutSourceParentKeys] = useState([]);
  const [selectedTileAddType, setSelectedTileAddType] = useState(null);
  const [quickAddBenefitSubType, setQuickAddBenefitSubType] = useState(DEFAULT_BENEFIT_SUBTYPE);
  const [tileHotkeys, setTileHotkeys] = useState(buildDefaultTileHotkeys);
  const [toolHotkeys, setToolHotkeys] = useState(TOOL_HOTKEY_DEFAULTS);
  const [isHotkeyPanelOpen, setIsHotkeyPanelOpen] = useState(false);
  const [hotkeyEditState, setHotkeyEditState] = useState(null);
  const [pendingQuickAddPlacement, setPendingQuickAddPlacement] = useState(null);
  const [cursorPreviewGrid, setCursorPreviewGrid] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [multiSelectFilters, setMultiSelectFilters] = useState(generateInitialMultiSelectFilters());
  const [selectedParentTileKeys, setSelectedParentTileKeys] = useState([]);
  const [multiSelectTool, setMultiSelectTool] = useState('square');
  const [multiSelectDragStart, setMultiSelectDragStart] = useState(null);
  const [multiSelectDragCurrent, setMultiSelectDragCurrent] = useState(null);
  const [multiSelectLassoPath, setMultiSelectLassoPath] = useState([]);
  const [isMultiSelectDragging, setIsMultiSelectDragging] = useState(false);
  const [, setPendingAllianceDeletions] = useState({});
  const pendingAllianceDeletionsRef = useRef({});
  const tilesRef = useRef([]);
  const isQuickAddMode = !!selectedTileAddType;
  const isMultiSelectMode = isMultiSelectLocked && !isCopyMode && !isCutMode && !isQuickAddMode;
  const canCycleBenefitSubTypes = BENEFIT_SUBTYPE_KEYS.length > 1;

  // add tile modals
  const [isAddCityModalOpen, setIsAddCityModalOpen] = useState(false);
  const [isAddAllianceBuildingModalOpen, setIsAddAllianceBuildingModalOpen] = useState(false);
  const [isAddEliteMineModalOpen, setIsAddEliteMineModalOpen] = useState(false);
  const [isAddBenefitBuildingModalOpen, setIsAddBenefitBuildingModalOpen] = useState(false);

  // tile details modals
  const [isTileDetailsModalOpen, setIsTileDetailsModalOpen] = useState(false);
  const [isNavigationModalOpen, setIsNavigationModalOpen] = useState(false); // State for the navigation modal
  const [isFindCommanderModalOpen, setIsFindCommanderModalOpen] = useState(false);

  //set tile type selection
  const [selectedGridPosition, setSelectedGridPosition] = useState(null);
  const [selectedTile, setSelectedTile] = useState(null);

  const [layers, setLayers] = useState(generateInitialLayersState());
  const [drawings, setDrawings] = useState([]);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingInteractionMode, setDrawingInteractionMode] = useState('draw');
  const [activeDrawingTool, setActiveDrawingTool] = useState('line');
  const [drawingColor, setDrawingColor] = useState('#38bdf8');
  const [drawingStrokeWidth, setDrawingStrokeWidth] = useState(2);
  const [drawingTextValue, setDrawingTextValue] = useState('');
  const [drawingTextColor, setDrawingTextColor] = useState(DEFAULT_DRAWING_TEXT_COLOR);
  const [drawingTextBackgroundColor, setDrawingTextBackgroundColor] = useState(DEFAULT_DRAWING_TEXT_BACKGROUND);
  const [drawingDraft, setDrawingDraft] = useState(null);
  const [isDrawingPanelCollapsed, setIsDrawingPanelCollapsed] = useState(false);
  const [selectedDrawingId, setSelectedDrawingId] = useState(null);
  const [selectedDrawingTextValue, setSelectedDrawingTextValue] = useState('');
  const [selectedDrawingTextColor, setSelectedDrawingTextColor] = useState(DEFAULT_DRAWING_TEXT_COLOR);
  const [selectedDrawingTextBackgroundColor, setSelectedDrawingTextBackgroundColor] = useState(DEFAULT_DRAWING_TEXT_BACKGROUND);
  const startDragOffset = useRef({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const drawingStartRef = useRef(null);
  const drawingMoveStateRef = useRef(null);
  const initialPinchDistanceRef = useRef(null); // To track initial pinch distance
  const initialZoomRef = useRef(zoom);
  const gridSize = 1200;
  const gap = 2 * zoom;

  // State to hold the loaded images
  const [loadedImages, setLoadedImages] = useState({});

  // plans and nations
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const selectedPlanId = String(selectedPlan?._id || '');
  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);
  const [isPlanMenuVisible, setIsPlanMenuVisible] = useState(false);
  const [isBenefitOverlayEnabled, setIsBenefitOverlayEnabled] = useState(false);
  const [isConfirmedOverlayEnabled, setIsConfirmedOverlayEnabled] = useState(false);
  const [isResourceDistributionOverlayEnabled, setIsResourceDistributionOverlayEnabled] = useState(false);
  const [isImportantCoordinateOverlayEnabled, setIsImportantCoordinateOverlayEnabled] = useState(false);
  const [isOverlayMenuVisible, setIsOverlayMenuVisible] = useState(false);
  const [isBorderMenuVisible, setIsBorderMenuVisible] = useState(false);
  const [isBorderOverlayEnabled, setIsBorderOverlayEnabled] = useState(true);
  const [isTerritoryCaptureMetricsEnabled, setIsTerritoryCaptureMetricsEnabled] = useState(false);
  const [borderVisibilityByAlliance, setBorderVisibilityByAlliance] = useState({});
  const [benefitOverlayFilters, setBenefitOverlayFilters] = useState(generateInitialBenefitOverlayFilters());
  const [importantCoordinateOverlayFilters, setImportantCoordinateOverlayFilters] = useState(generateInitialImportantCoordinateOverlayFilters());
  const [borders, setBorders] = useState([]);
  const [placedBorderAlliances, setPlacedBorderAlliances] = useState([]);
  const [territoryCaptureRows, setTerritoryCaptureRows] = useState([]);
  const [territoryCaptureContested, setTerritoryCaptureContested] = useState(createEmptyTerritoryCaptureContested());
  const [selectedBorderAllianceId, setSelectedBorderAllianceId] = useState('');
  const [borderInteractionMode, setBorderInteractionMode] = useState('idle');
  const [borderDraftPoints, setBorderDraftPoints] = useState([]);
  const [borderHoverGridPoint, setBorderHoverGridPoint] = useState(null);
  const [borderDragVertexIndex, setBorderDragVertexIndex] = useState(-1);
  const [borderPreviewMetrics, setBorderPreviewMetrics] = useState(null);
  const [isBorderPreviewLoading, setIsBorderPreviewLoading] = useState(false);
  const [mergerPlanningScope, setMergerPlanningScope] = useState({ allowedNationIds: [] });
  const selectedNationId = String(selectedNation?._id || '');
  const scopedPlanningNationIds = useMemo(
    () => (mergerPlanningScope?.allowedNationIds || []).map((id) => String(id)),
    [mergerPlanningScope?.allowedNationIds]
  );
  const selectedPlanningPairNationIds = useMemo(() => {
    if (!selectedNationId) return [];

    if (scopedPlanningNationIds.length > 1 && scopedPlanningNationIds.includes(selectedNationId)) {
      return scopedPlanningNationIds;
    }

    const planningSessions = Array.isArray(mergerPlanningScope?.sessions)
      ? mergerPlanningScope.sessions
      : [];
    const sessionForSelectedNation = planningSessions.find((session) => {
      const sessionNationIds = (session?.nations || []).map((nation) => String(nation?._id || nation));
      return sessionNationIds.includes(selectedNationId) && sessionNationIds.length > 1;
    });

    if (!sessionForSelectedNation) return [];
    return Array.from(
      new Set((sessionForSelectedNation.nations || []).map((nation) => String(nation?._id || nation)))
    );
  }, [mergerPlanningScope?.sessions, scopedPlanningNationIds, selectedNationId]);
  const addCityCommanderNationOptions = useMemo(() => {
    if (!selectedNationId) return [];

    const nationById = new Map((nations || []).map((nation) => [String(nation._id), nation]));
    if (selectedNation?._id && !nationById.has(String(selectedNation._id))) {
      nationById.set(String(selectedNation._id), selectedNation);
    }

    const candidateNationIds = selectedPlanningPairNationIds.length > 1
      ? selectedPlanningPairNationIds
      : [selectedNationId];

    return candidateNationIds
      .map((nationId) => nationById.get(String(nationId)))
      .filter(Boolean)
      .sort((left, right) => Number(left?.number || 0) - Number(right?.number || 0));
  }, [nations, selectedNation, selectedNationId, selectedPlanningPairNationIds]);
  const isNationAdmin = currentUserRole === 'nationAdmin';
  const isSelectedNationInActivePlanningPair = selectedPlanningPairNationIds.length > 1
    && selectedPlanningPairNationIds.includes(selectedNationId);
  const isCurrentUserNationInActivePlanningPair = selectedPlanningPairNationIds.length > 1
    && !!currentUserNationId
    && selectedPlanningPairNationIds.includes(currentUserNationId);
  const canEditSelectedNationInPlanning = isNationAdmin
    && isSelectedNationInActivePlanningPair
    && isCurrentUserNationInActivePlanningPair;
  const canWriteTiles = baseCanWriteTiles || canEditSelectedNationInPlanning;
  const canDeleteTiles = baseCanDeleteTiles || canEditSelectedNationInPlanning;
  const canEditPlans = baseCanWritePlans || canEditSelectedNationInPlanning;
  const showReadOnlyEditReason = !!selectedNationId && !canWriteTiles;
  const canUseCutMode = canWriteTiles && canDeleteTiles;
  const [isTileWindowLoading, setIsTileWindowLoading] = useState(false);
  const [isOptimizePanelOpen, setIsOptimizePanelOpen] = useState(false);
  const [optimizeMode, setOptimizeMode] = useState('territory');
  const [optimizeAllianceId, setOptimizeAllianceId] = useState('');
  const [optimizeFlagCount, setOptimizeFlagCount] = useState('10');
  const [isOptimizingTerritory, setIsOptimizingTerritory] = useState(false);
  const [optimizeStatusMessage, setOptimizeStatusMessage] = useState('');
  const [hiveTargetX, setHiveTargetX] = useState('');
  const [hiveTargetY, setHiveTargetY] = useState('');
  const [isPickingHiveTarget, setIsPickingHiveTarget] = useState(false);
  const [isOptimizingHive, setIsOptimizingHive] = useState(false);
  const [hiveWeights, setHiveWeights] = useState(HIVE_WEIGHT_DEFAULTS);
  const [hiveCandidates, setHiveCandidates] = useState([]);
  const [selectedHiveCandidateIndex, setSelectedHiveCandidateIndex] = useState(-1);
  const [optimizationJobId, setOptimizationJobId] = useState(null);
  const [optimizationPreviewFlags, setOptimizationPreviewFlags] = useState([]);
  const [showOptimizeAdvanced, setShowOptimizeAdvanced] = useState(false);
  const [optimizerWeights, setOptimizerWeights] = useState(OPTIMIZER_DEFAULTS);
  const optimizationWatchdogRef = useRef(null);
  const optimizationGhostIntervalRef = useRef(null);
  const borderPreviewDebounceRef = useRef(null);
  const borderPreviewRequestSeqRef = useRef(0);
  const historySequenceRef = useRef(0);
  const pendingDrawingCreateHistoryRef = useRef([]);
  const createHistoryEntryRef = useRef(null);
  const pushHistoryEntryRef = useRef(null);
  const loadedTileWindowRef = useRef(null);
  const tileWindowFetchSequenceRef = useRef(0);
  const tileFetchAbortControllerRef = useRef(null);
  const tileFetchInFlightWindowRef = useRef(null);
  const fetchUpdatedTileDataRef = useRef(null);
  const fetchDrawingDataRef = useRef(null);
  const fetchBorderDataRef = useRef(null);
  const scheduleTerritoryCaptureRefreshRef = useRef(null);
  const viewportTileWindowRef = useRef(null);
  const pendingSocketPlacementParentKeysRef = useRef(new Map());
  const postMutationRefreshTimeoutRef = useRef(null);
  const territoryCaptureRefreshTimeoutRef = useRef(null);
  const territoryCaptureMetricsEnabledRef = useRef(false);
  const optimisticPlacementIndexRef = useRef(new Map());
  const importantCoordinateLabelHitboxesRef = useRef([]);

  const parentTilesByKey = useMemo(() => {
    const map = new Map();
    tiles.forEach((tile) => {
      const parentX = tile.parentTile?.x ?? tile.x;
      const parentY = tile.parentTile?.y ?? tile.y;
      const parentKey = `${parentX},${parentY}`;
      if (map.has(parentKey)) {
        return;
      }

      if (!tile.type.endsWith('-child')) {
        map.set(parentKey, { ...tile, x: parentX, y: parentY, type: getBaseTileType(tile.type) });
      }
    });

    return map;
  }, [tiles]);

  const selectedParentTiles = useMemo(() => {
    return selectedParentTileKeys
      .map((key) => parentTilesByKey.get(key))
      .filter(Boolean);
  }, [selectedParentTileKeys, parentTilesByKey]);

  const placementConstraintIndex = useMemo(() => (
    buildTileConstraintPlacementIndex(tiles)
  ), [tiles]);

  const cityPlacementByCommanderId = useMemo(() => {
    const lookup = {};
    placementConstraintIndex.forEach((entry) => {
      if (entry.tileType !== 'city') {
        return;
      }
      lookup[entry.entityId] = {
        parentKey: entry.parentKey,
        x: entry.parentX,
        y: entry.parentY
      };
    });
    return lookup;
  }, [placementConstraintIndex]);

  const alliancePlacementByType = useMemo(() => {
    const lookup = {
      allianceHall: {},
      fort: {},
      institute: {}
    };
    placementConstraintIndex.forEach((entry) => {
      if (!lookup[entry.tileType]) {
        return;
      }
      lookup[entry.tileType][entry.entityId] = {
        parentKey: entry.parentKey,
        x: entry.parentX,
        y: entry.parentY
      };
    });
    return lookup;
  }, [placementConstraintIndex]);

  const selectedDrawing = useMemo(() => {
    const selectedId = normalizeEntityId(selectedDrawingId);
    if (!selectedId) return null;
    return drawings.find((drawing) => normalizeEntityId(drawing?._id) === selectedId) || null;
  }, [drawings, selectedDrawingId]);

  const snapshotDrawingForHistory = useCallback((drawing) => {
    if (!drawing) return null;
    const kind = String(drawing.kind || '').trim();
    if (!kind) return null;
    const points = Array.isArray(drawing.points)
      ? drawing.points
        .map((point) => ({
          x: Number(point?.x),
          y: Number(point?.y)
        }))
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
        .map((point) => ({
          x: Number(point.x.toFixed(3)),
          y: Number(point.y.toFixed(3))
        }))
      : [];
    const normalized = {
      kind,
      points,
      text: kind === 'text' ? clampDrawingText(drawing.text || '') : '',
      scale: kind === 'text' ? Math.max(0.4, Math.min(8, Number(drawing.scale || 1))) : 1,
      style: {
        color: String(drawing?.style?.color || '#38bdf8'),
        width: Math.max(1, Math.min(12, Number(drawing?.style?.width || 2))),
        textColor: String(drawing?.style?.textColor || drawing?.style?.color || DEFAULT_DRAWING_TEXT_COLOR),
        backgroundColor: String(drawing?.style?.backgroundColor || DEFAULT_DRAWING_TEXT_BACKGROUND)
      }
    };
    const normalizedId = normalizeEntityId(drawing?._id);
    if (normalizedId) {
      normalized._id = normalizedId;
    }
    return normalized;
  }, []);

  const snapshotBorderForHistory = useCallback((border) => {
    if (!border) return null;
    const alliance = normalizeEntityId(border?.alliance?._id || border?.alliance);
    if (!alliance) return null;
    const points = Array.isArray(border?.points)
      ? border.points
        .map((point) => ({
          x: Math.round(Number(point?.x)),
          y: Math.round(Number(point?.y))
        }))
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
      : [];
    if (points.length < 3) return null;

    const snapshot = {
      alliance,
      points
    };
    const borderId = normalizeEntityId(border?._id);
    if (borderId) {
      snapshot._id = borderId;
    }
    return snapshot;
  }, []);

  const areDrawingSnapshotsEquivalent = useCallback((left, right, { ignoreId = false } = {}) => {
    if (!left || !right) return false;
    if (!ignoreId && normalizeEntityId(left._id) !== normalizeEntityId(right._id)) return false;
    if (String(left.kind || '') !== String(right.kind || '')) return false;
    const leftPoints = Array.isArray(left.points) ? left.points : [];
    const rightPoints = Array.isArray(right.points) ? right.points : [];
    if (leftPoints.length !== rightPoints.length) return false;
    for (let index = 0; index < leftPoints.length; index += 1) {
      if (Number(leftPoints[index].x) !== Number(rightPoints[index].x)) return false;
      if (Number(leftPoints[index].y) !== Number(rightPoints[index].y)) return false;
    }
    if (String(left.text || '') !== String(right.text || '')) return false;
    if (Number(left.scale || 1) !== Number(right.scale || 1)) return false;
    if (String(left?.style?.color || '') !== String(right?.style?.color || '')) return false;
    if (Number(left?.style?.width || 0) !== Number(right?.style?.width || 0)) return false;
    if (String(left?.style?.textColor || '') !== String(right?.style?.textColor || '')) return false;
    if (String(left?.style?.backgroundColor || '') !== String(right?.style?.backgroundColor || '')) return false;
    return true;
  }, []);

  const getDrawingKindLabel = useCallback((kind) => {
    if (kind === 'line') return 'Line';
    if (kind === 'free') return 'Free Draw';
    if (kind === 'text') return 'Text';
    return 'Drawing';
  }, []);

  useEffect(() => {
    if (selectedDrawing?.kind === 'text') {
      setSelectedDrawingTextValue(clampDrawingText(selectedDrawing.text || ''));
      setSelectedDrawingTextColor(String(selectedDrawing?.style?.textColor || selectedDrawing?.style?.color || DEFAULT_DRAWING_TEXT_COLOR));
      setSelectedDrawingTextBackgroundColor(String(selectedDrawing?.style?.backgroundColor || DEFAULT_DRAWING_TEXT_BACKGROUND));
      return;
    }
    setSelectedDrawingTextValue('');
    setSelectedDrawingTextColor(DEFAULT_DRAWING_TEXT_COLOR);
    setSelectedDrawingTextBackgroundColor(DEFAULT_DRAWING_TEXT_BACKGROUND);
  }, [selectedDrawing]);

  const selectedTileTypeCounts = useMemo(() => {
    const counts = TILE_ENTITY_OPTIONS.reduce((acc, option) => {
      acc[option.key] = 0;
      return acc;
    }, {});

    selectedParentTiles.forEach((tile) => {
      const baseType = getBaseTileType(tile.type);
      if (Object.prototype.hasOwnProperty.call(counts, baseType)) {
        counts[baseType] += 1;
      }
    });

    return counts;
  }, [selectedParentTiles]);

  const selectedBenefitSubtypeCounts = useMemo(() => {
    const counts = BENEFIT_SUBTYPE_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});

    selectedParentTiles.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type);
      if (baseType !== 'benefit') {
        return;
      }

      const rawSubtype = tile?.additionalTileInfo?.benefitSubType;
      const normalizedSubtype = BENEFIT_SUBTYPE_KEYS.includes(rawSubtype)
        ? rawSubtype
        : DEFAULT_BENEFIT_SUBTYPE;
      if (!normalizedSubtype) {
        return;
      }

      counts[normalizedSubtype] = (counts[normalizedSubtype] || 0) + 1;
    });

    return counts;
  }, [selectedParentTiles]);

  const selectedEliteMineLevelCounts = useMemo(() => {
    const counts = ELITE_MINE_LEVELS.reduce((acc, level) => {
      acc[level] = 0;
      return acc;
    }, {});

    selectedParentTiles.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type);
      if (baseType !== 'eliteMine') {
        return;
      }

      const parsedLevel = Number.parseInt(tile?.additionalTileInfo?.level, 10);
      if (!ELITE_MINE_LEVELS.includes(parsedLevel)) {
        return;
      }

      counts[parsedLevel] = (counts[parsedLevel] || 0) + 1;
    });

    return counts;
  }, [selectedParentTiles]);

  const benefitSubtypeCounts = useMemo(() => {
    const counts = BENEFIT_SUBTYPE_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});

    parentTilesByKey.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type);
      if (baseType !== 'benefit') {
        return;
      }

      const rawSubtype = tile?.additionalTileInfo?.benefitSubType;
      const normalizedSubtype = BENEFIT_SUBTYPE_KEYS.includes(rawSubtype)
        ? rawSubtype
        : DEFAULT_BENEFIT_SUBTYPE;

      if (!normalizedSubtype) {
        return;
      }

      counts[normalizedSubtype] = (counts[normalizedSubtype] || 0) + 1;
    });

    return counts;
  }, [parentTilesByKey]);

  const eliteMineLevelCounts = useMemo(() => {
    const counts = ELITE_MINE_LEVELS.reduce((acc, level) => {
      acc[level] = 0;
      return acc;
    }, {});

    parentTilesByKey.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type);
      if (baseType !== 'eliteMine') {
        return;
      }

      const parsedLevel = Number.parseInt(tile?.additionalTileInfo?.level, 10);
      if (!ELITE_MINE_LEVELS.includes(parsedLevel)) {
        return;
      }

      counts[parsedLevel] = (counts[parsedLevel] || 0) + 1;
    });

    return counts;
  }, [parentTilesByKey]);

  const quickAddTileGroups = useMemo(() => {
    const groups = Object.entries(tileInformation).reduce((acc, [key, value]) => {
      const category = value?.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({
        key,
        label: value?.niceName || key
      });
      return acc;
    }, {});

    const categoryOrder = ['city', 'alliance', 'mapTile', 'static', 'other'];
    const tileOrderRank = TILE_HOTKEY_ORDER.reduce((acc, tileType, index) => {
      acc[tileType] = index;
      return acc;
    }, {});
    return Object.keys(groups)
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a);
        const bIndex = categoryOrder.indexOf(b);
        const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (normalizedA !== normalizedB) return normalizedA - normalizedB;
        return a.localeCompare(b);
      })
      .map((category) => ({
        category,
        label: category === 'mapTile'
          ? 'Map'
          : category.charAt(0).toUpperCase() + category.slice(1),
        options: groups[category].sort((a, b) => {
          const rankA = tileOrderRank[a.key];
          const rankB = tileOrderRank[b.key];
          if (Number.isFinite(rankA) || Number.isFinite(rankB)) {
            const safeRankA = Number.isFinite(rankA) ? rankA : Number.MAX_SAFE_INTEGER;
            const safeRankB = Number.isFinite(rankB) ? rankB : Number.MAX_SAFE_INTEGER;
            if (safeRankA !== safeRankB) return safeRankA - safeRankB;
          }
          return a.label.localeCompare(b.label);
        })
      }));
  }, []);

  const tileHotkeyByType = useMemo(() => {
    const mapping = {};
    Object.entries(tileHotkeys || {}).forEach(([hotkey, tileType]) => {
      if (!tileType) return;
      mapping[tileType] = hotkey;
    });
    return mapping;
  }, [tileHotkeys]);

  const tileHotkeyRows = useMemo(() => {
    return TILE_HOTKEY_ORDER.map((tileType) => ({
      tileType,
      label: tileInformation[tileType]?.niceName || tileType,
      hotkey: tileHotkeyByType[tileType] || ''
    }));
  }, [tileHotkeyByType]);

  const toolHotkeyRows = useMemo(() => ([
    { action: 'copy', label: 'Copy Mode' },
    { action: 'cut', label: 'Cut Mode' },
    { action: 'undo', label: 'Undo' },
    { action: 'redo', label: 'Redo' },
    { action: 'multiSelect', label: 'Multi Select' },
    { action: 'cancelModes', label: 'Cancel Modes' },
  ]), []);

  const cycleQuickAddBenefitSubType = useCallback((direction = 1) => {
    if (!BENEFIT_SUBTYPE_KEYS.length) {
      return;
    }

    setQuickAddBenefitSubType((previous) => {
      const currentIndex = BENEFIT_SUBTYPE_KEYS.indexOf(previous);
      const safeIndex = currentIndex === -1 ? 0 : currentIndex;
      const nextIndex = (safeIndex + direction + BENEFIT_SUBTYPE_KEYS.length) % BENEFIT_SUBTYPE_KEYS.length;
      return BENEFIT_SUBTYPE_KEYS[nextIndex];
    });
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(HOTKEY_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.tileHotkeys && typeof parsed.tileHotkeys === 'object') {
        const nextTileHotkeys = {};
        const usedTileTypes = new Set();
        Object.entries(parsed.tileHotkeys).forEach(([hotkey, tileType]) => {
          if (!TILE_HOTKEY_KEYS.includes(hotkey)) return;
          if (!tileInformation[tileType]) return;
          if (usedTileTypes.has(tileType)) return;
          nextTileHotkeys[hotkey] = tileType;
          usedTileTypes.add(tileType);
        });
        if (Object.keys(nextTileHotkeys).length) {
          setTileHotkeys(nextTileHotkeys);
        }
      }
      if (parsed?.toolHotkeys && typeof parsed.toolHotkeys === 'object') {
        setToolHotkeys((prev) => ({
          ...prev,
          ...parsed.toolHotkeys
        }));
      }
    } catch (error) {
      // Ignore malformed local hotkey config.
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(HOTKEY_STORAGE_KEY, JSON.stringify({
        tileHotkeys,
        toolHotkeys
      }));
    } catch (error) {
      // Ignore local storage write failures.
    }
  }, [tileHotkeys, toolHotkeys]);

  const assignTileHotkey = useCallback((tileType, hotkey) => {
    if (!TILE_HOTKEY_KEYS.includes(hotkey)) {
      showError(`Tile hotkeys must use: ${TILE_HOTKEY_KEYS.join(' ')}`);
      return false;
    }

    const conflictingType = tileHotkeys[hotkey];
    if (conflictingType && conflictingType !== tileType) {
      showError(`Hotkey ${hotkey} is already assigned to ${tileInformation[conflictingType]?.niceName || conflictingType}.`);
      return false;
    }

    setTileHotkeys((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key] === tileType) {
          delete next[key];
        }
      });
      next[hotkey] = tileType;
      return next;
    });
    return true;
  }, [showError, tileHotkeys]);

  const clearTileHotkey = useCallback((tileType) => {
    setTileHotkeys((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key] === tileType) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  const assignToolHotkey = useCallback((action, combo) => {
    const conflictAction = Object.entries(toolHotkeys).find(([key, value]) => key !== action && value === combo)?.[0];
    if (conflictAction) {
      showError(`Hotkey ${combo} is already assigned to ${conflictAction}.`);
      return false;
    }
    setToolHotkeys((prev) => ({ ...prev, [action]: combo }));
    return true;
  }, [showError, toolHotkeys]);

  const clearToolHotkey = useCallback((action) => {
    setToolHotkeys((prev) => ({ ...prev, [action]: '' }));
  }, []);

  const tileTypeRequiresAdditionalInfo = useCallback((tileType) => (
    ['city', 'flag', 'allianceHall', 'fort', 'institute', 'eliteMine', 'benefit'].includes(tileType)
  ), []);

  const optimizeAlliances = useMemo(() => {
    const nationNumberById = new Map(
      (nations || []).map((nation) => [String(nation?._id || ''), Number(nation?.number || 0)])
    );
    const allianceById = new Map();

    tiles.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type || '');
      if (!OPTIMIZATION_ANCHOR_TYPES.includes(baseType)) {
        return;
      }

      const rawAlliance = tile?.additionalTileInfo?.alliance;
      const allianceId = normalizeEntityId(rawAlliance?._id || rawAlliance);
      if (!allianceId) return;

      const existing = allianceById.get(allianceId) || {
        _id: allianceId,
        acronym: '---',
        name: `Alliance ${allianceId.slice(-6)}`,
        nationId: '',
        nationNumber: null,
        anchorCount: 0
      };

      if (rawAlliance && typeof rawAlliance === 'object') {
        const allianceName = String(rawAlliance?.name || '').trim();
        const allianceAcronym = String(rawAlliance?.acronym || '').trim();
        const allianceNationId = normalizeEntityId(rawAlliance?.nation?._id || rawAlliance?.nation);

        if (allianceName) existing.name = allianceName;
        if (allianceAcronym) existing.acronym = allianceAcronym;
        if (allianceNationId) {
          existing.nationId = allianceNationId;
          if (nationNumberById.has(allianceNationId)) {
            existing.nationNumber = nationNumberById.get(allianceNationId);
          }
        }
      }

      existing.anchorCount += 1;
      allianceById.set(allianceId, existing);
    });

    return Array.from(allianceById.values()).sort((left, right) => {
      const leftNation = Number(left?.nationNumber || 0);
      const rightNation = Number(right?.nationNumber || 0);
      if (leftNation !== rightNation) return leftNation - rightNation;

      const leftLabel = `[${left?.acronym || '---'}] ${left?.name || ''}`.toLowerCase();
      const rightLabel = `[${right?.acronym || '---'}] ${right?.name || ''}`.toLowerCase();
      return leftLabel.localeCompare(rightLabel);
    });
  }, [nations, tiles]);

  const borderAllianceOptions = useMemo(() => {
    const nationNumberById = new Map(
      (nations || []).map((nation) => [String(nation?._id || ''), Number(nation?.number || 0)])
    );
    const scopedNationSet = new Set(selectedPlanningPairNationIds.map((id) => String(id)));
    const allianceById = new Map();

    const upsertAlliance = (rawAlliance, { placedIncrement = 0 } = {}) => {
      const allianceId = normalizeEntityId(rawAlliance?._id || rawAlliance);
      if (!allianceId) return;

      const existing = allianceById.get(allianceId) || {
        _id: allianceId,
        acronym: '---',
        name: `Alliance ${allianceId.slice(-6)}`,
        nationId: '',
        nationNumber: null,
        territoryColor: '',
        placedCount: 0
      };

      if (rawAlliance && typeof rawAlliance === 'object') {
        const allianceName = String(rawAlliance?.name || '').trim();
        const allianceAcronym = String(rawAlliance?.acronym || '').trim();
        const allianceNationId = normalizeEntityId(rawAlliance?.nation?._id || rawAlliance?.nation);
        const territoryColor = String(rawAlliance?.territoryColor || '').trim();
        if (allianceName) existing.name = allianceName;
        if (allianceAcronym) existing.acronym = allianceAcronym;
        if (allianceNationId) {
          existing.nationId = allianceNationId;
          if (nationNumberById.has(allianceNationId)) {
            existing.nationNumber = nationNumberById.get(allianceNationId);
          }
        }
        if (territoryColor) existing.territoryColor = territoryColor;
      }

      existing.placedCount += Math.max(0, Number(placedIncrement) || 0);
      allianceById.set(allianceId, existing);
    };

    placedBorderAlliances.forEach((alliance) => {
      const allianceId = normalizeEntityId(alliance?._id);
      if (!allianceId) return;
      upsertAlliance({
        _id: allianceId,
        name: alliance?.name,
        acronym: alliance?.acronym,
        territoryColor: alliance?.territoryColor,
        nation: alliance?.nation
      }, {
        placedIncrement: Number(alliance?.placedCount || 0)
      });
    });

    tiles.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type || '');
      if (!BORDER_ALLIANCE_BUILDING_TYPES.includes(baseType)) {
        return;
      }
      upsertAlliance(tile?.additionalTileInfo?.alliance, { placedIncrement: 1 });
    });

    borders.forEach((border) => {
      upsertAlliance(border?.alliance, { placedIncrement: 0 });
    });

    return Array.from(allianceById.values())
      .filter((alliance) => {
        if (!scopedNationSet.size) return true;
        if (!alliance.nationId) return true;
        return scopedNationSet.has(String(alliance.nationId));
      })
      .sort((left, right) => {
        const leftNation = Number(left?.nationNumber || 0);
        const rightNation = Number(right?.nationNumber || 0);
        if (leftNation !== rightNation) return leftNation - rightNation;
        const leftLabel = `[${left?.acronym || '---'}] ${left?.name || ''}`.toLowerCase();
        const rightLabel = `[${right?.acronym || '---'}] ${right?.name || ''}`.toLowerCase();
        return leftLabel.localeCompare(rightLabel);
      });
  }, [borders, nations, placedBorderAlliances, selectedPlanningPairNationIds, tiles]);

  const bordersByAllianceId = useMemo(() => {
    const lookup = new Map();
    borders.forEach((border) => {
      const allianceId = normalizeEntityId(border?.alliance?._id || border?.alliance);
      if (!allianceId) return;
      lookup.set(allianceId, border);
    });
    return lookup;
  }, [borders]);

  const selectedBorder = useMemo(
    () => bordersByAllianceId.get(String(selectedBorderAllianceId || '')) || null,
    [bordersByAllianceId, selectedBorderAllianceId]
  );

  const isBorderInteractionActive = borderInteractionMode === 'drawing' || borderInteractionMode === 'editing';
  const activeBorderPoints = useMemo(() => {
    if (!isBorderInteractionActive) return [];
    return (borderDraftPoints || [])
      .map((point) => ({
        x: Math.round(Number(point?.x)),
        y: Math.round(Number(point?.y))
      }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  }, [borderDraftPoints, isBorderInteractionActive]);

  const borderRows = useMemo(() => {
    const rows = borders.map((border) => {
      const allianceId = normalizeEntityId(border?.alliance?._id || border?.alliance);
      const fallback = borderAllianceOptions.find((alliance) => String(alliance._id) === String(allianceId));
      const alliance = border?.alliance && typeof border.alliance === 'object'
        ? border.alliance
        : fallback;
      const acronym = String(alliance?.acronym || fallback?.acronym || '---');
      const name = String(alliance?.name || fallback?.name || `Alliance ${String(allianceId || '').slice(-6)}`);
      const nationId = normalizeEntityId(alliance?.nation?._id || alliance?.nation || fallback?.nationId);
      const nationNumber = (nations || []).find((nation) => String(nation?._id) === nationId)?.number
        || fallback?.nationNumber
        || 0;
      const territoryColor = String(alliance?.territoryColor || fallback?.territoryColor || '');

      return {
        border,
        allianceId,
        acronym,
        name,
        nationId,
        nationNumber,
        territoryColor
      };
    });

    return rows.sort((left, right) => {
      const leftNation = Number(left?.nationNumber || 0);
      const rightNation = Number(right?.nationNumber || 0);
      if (leftNation !== rightNation) return leftNation - rightNation;
      const leftLabel = `[${left?.acronym || '---'}] ${left?.name || ''}`.toLowerCase();
      const rightLabel = `[${right?.acronym || '---'}] ${right?.name || ''}`.toLowerCase();
      return leftLabel.localeCompare(rightLabel);
    });
  }, [borderAllianceOptions, borders, nations]);

  const territoryCaptureRowsView = useMemo(() => {
    const rows = (territoryCaptureRows || []).map((row) => {
      const allianceId = normalizeEntityId(row?.alliance?._id || row?.allianceId);
      const fallback = borderAllianceOptions.find((alliance) => String(alliance._id) === String(allianceId));
      const alliance = row?.alliance && typeof row.alliance === 'object'
        ? row.alliance
        : fallback;
      const acronym = String(alliance?.acronym || fallback?.acronym || '---');
      const name = String(alliance?.name || fallback?.name || `Alliance ${String(allianceId || '').slice(-6)}`);
      const nationId = normalizeEntityId(alliance?.nation?._id || alliance?.nation || fallback?.nationId);
      const nationNumber = (nations || []).find((nation) => String(nation?._id) === nationId)?.number
        || fallback?.nationNumber
        || 0;
      const territoryColor = String(alliance?.territoryColor || fallback?.territoryColor || '');

      return {
        allianceId,
        acronym,
        name,
        nationId,
        nationNumber,
        territoryColor,
        territoryBuildingCount: Number(row?.territoryBuildingCount || 0),
        metrics: row?.metrics || {
          benefitSubTypes: BENEFIT_SUBTYPE_KEYS.reduce((acc, subType) => {
            acc[subType] = 0;
            return acc;
          }, {}),
          eliteMineLevels: ELITE_MINE_LEVELS.reduce((acc, level) => {
            acc[level] = 0;
            return acc;
          }, {}),
          totals: {
            benefit: 0,
            eliteMine: 0
          }
        }
      };
    }).filter((row) => !!row.allianceId);

    return rows.sort((left, right) => {
      const leftNation = Number(left?.nationNumber || 0);
      const rightNation = Number(right?.nationNumber || 0);
      if (leftNation !== rightNation) return leftNation - rightNation;
      const leftLabel = `[${left?.acronym || '---'}] ${left?.name || ''}`.toLowerCase();
      const rightLabel = `[${right?.acronym || '---'}] ${right?.name || ''}`.toLowerCase();
      return leftLabel.localeCompare(rightLabel);
    });
  }, [borderAllianceOptions, nations, territoryCaptureRows]);

  useEffect(() => {
    if (user?.role !== 'superUser') {
      setOptimizeAllianceId('');
      return;
    }

    setOptimizeAllianceId((prev) => {
      if (prev && optimizeAlliances.some((alliance) => String(alliance._id) === String(prev))) {
        return prev;
      }
      return optimizeAlliances[0]?._id || '';
    });
  }, [optimizeAlliances, user?.role]);

  useEffect(() => {
    setSelectedBorderAllianceId((prev) => {
      if (prev && borderAllianceOptions.some((alliance) => String(alliance._id) === String(prev))) {
        return prev;
      }
      if (borderRows.length) {
        const firstWithBorder = borderRows[0]?.allianceId;
        if (firstWithBorder) return firstWithBorder;
      }
      return borderAllianceOptions[0]?._id || '';
    });
  }, [borderAllianceOptions, borderRows]);

  const hivePreviewCandidates = useMemo(() => (
    hiveCandidates.map((candidate, index) => ({
      ...candidate,
      rank: index + 1,
      isSelected: index === selectedHiveCandidateIndex
    }))
  ), [hiveCandidates, selectedHiveCandidateIndex]);

  const optimizationGhostCandidates = useMemo(() => {
    if (!optimizeAllianceId) {
      return [];
    }

    const getAllianceIdFromTile = (tile) => {
      const alliance = tile?.additionalTileInfo?.alliance;
      if (!alliance) return null;
      if (typeof alliance === 'object') return String(alliance._id || '');
      return String(alliance);
    };

    const occupiedCells = new Set();
    const territoryCells = new Set();

    tiles.forEach((tile) => {
      occupiedCells.add(`${tile.x},${tile.y}`);
      const baseType = getBaseTileType(tile.type);
      if (!ALLIANCE_TERRITORY_TYPES.includes(baseType)) return;

      const tileAllianceId = getAllianceIdFromTile(tile);
      if (!tileAllianceId || tileAllianceId !== String(optimizeAllianceId)) return;

      const radius = tileInformation[baseType]?.territorySize || 0;
      for (let dx = -radius; dx <= radius; dx += 1) {
        for (let dy = -radius; dy <= radius; dy += 1) {
          territoryCells.add(`${tile.x + dx},${tile.y + dy}`);
        }
      }
    });

    if (!territoryCells.size) {
      return [];
    }

    const neighborOffsets = [
      [1, 0], [-1, 0], [0, 1], [0, -1],
      [1, 1], [1, -1], [-1, 1], [-1, -1],
    ];
    const candidates = new Set();
    territoryCells.forEach((key) => {
      const [x, y] = key.split(',').map(Number);
      neighborOffsets.forEach(([dx, dy]) => {
        const candidateX = x + dx;
        const candidateY = y + dy;
        const candidateKey = `${candidateX},${candidateY}`;
        if (territoryCells.has(candidateKey)) return;
        if (occupiedCells.has(candidateKey)) return;
        candidates.add(candidateKey);
      });
    });

    return Array.from(candidates).map((key) => {
      const [x, y] = key.split(',').map(Number);
      return { x, y };
    });
  }, [tiles, optimizeAllianceId]);

  const handleTileUpdate = useCallback((tileUpdate) => {
    const action = tileUpdate.action;
    const tileData = tileUpdate.tileData;
    const loadedWindow = loadedTileWindowRef.current;
    const viewportWindow = viewportTileWindowRef.current;
    const pendingPlacementParentKeys = pendingSocketPlacementParentKeysRef.current;
    const optimisticPlacementIndex = optimisticPlacementIndexRef.current;
    const now = Date.now();
    for (const [parentKey, expiresAt] of pendingPlacementParentKeys.entries()) {
      if (expiresAt <= now) {
        pendingPlacementParentKeys.delete(parentKey);
      }
    }

    const isTileInWindow = (tile, window) => {
      if (!window) return false;
      return (
        tile.x >= window.minX
        && tile.x <= window.maxX
        && tile.y >= window.minY
        && tile.y <= window.maxY
      );
    };
    const getRealtimeTileIdentityKey = (tile) => {
      const normalizedId = normalizeEntityId(tile?._id);
      if (normalizedId) {
        return `id:${normalizedId}`;
      }
      const x = Number(tile?.x);
      const y = Number(tile?.y);
      const parentX = Number(tile?.parentTile?.x ?? x);
      const parentY = Number(tile?.parentTile?.y ?? y);
      const type = String(tile?.type || '');
      return `coord:${type}:${x},${y}:${parentX},${parentY}`;
    };

    if (action === "newTile") {
      const rawNextTiles = Array.isArray(tileData)
        ? tileData
        : (
          Array.isArray(tileData?.tiles)
            ? tileData.tiles
            : (Array.isArray(tileData?.data) ? tileData.data : [])
        );
      const nextTiles = rawNextTiles.map((tile) => {
        const normalizedId = normalizeEntityId(tile?._id);
        return {
          ...tile,
          _id: normalizedId || tile?._id,
          parentTile: tile?.parentTile
            ? {
              x: Number(tile.parentTile.x),
              y: Number(tile.parentTile.y)
            }
            : tile?.parentTile
        };
      });
      const socketRequestId = String(tileData?.requestId || '').trim();
      const groupedByParentKey = new Map();
      nextTiles.forEach((tile) => {
        const parentKey = getParentKeyFromTile(tile);
        if (!groupedByParentKey.has(parentKey)) {
          groupedByParentKey.set(parentKey, []);
        }
        groupedByParentKey.get(parentKey).push(tile);
      });
      const incomingParentKeys = new Set(groupedByParentKey.keys());

      const acceptedTiles = [];
      groupedByParentKey.forEach((groupTiles, parentKey) => {
        const hasPendingLocalPlacement = pendingPlacementParentKeys.has(parentKey);
        if (hasPendingLocalPlacement) {
          pendingPlacementParentKeys.delete(parentKey);
        }

        if (!loadedWindow && !viewportWindow) {
          acceptedTiles.push(...groupTiles);
          return;
        }

        const intersectsLoadedWindow = groupTiles.some((tile) => isTileInWindow(tile, loadedWindow));
        const intersectsViewportWindow = groupTiles.some((tile) => isTileInWindow(tile, viewportWindow));
        if (hasPendingLocalPlacement || intersectsLoadedWindow || intersectsViewportWindow) {
          // Keep whole parent group so large object parents/children stay consistent.
          acceptedTiles.push(...groupTiles);
        }
      });

      const settledRequestIds = new Set();
      if (socketRequestId && optimisticPlacementIndex.has(socketRequestId)) {
        settledRequestIds.add(socketRequestId);
      }
      for (const [requestId, pending] of optimisticPlacementIndex.entries()) {
        if (settledRequestIds.has(requestId)) continue;
        const parentKeys = Array.isArray(pending?.parentKeys) ? pending.parentKeys : [];
        if (!parentKeys.length) continue;
        const isResolvedByIncomingParents = parentKeys.every((parentKey) => incomingParentKeys.has(parentKey));
        if (isResolvedByIncomingParents) {
          settledRequestIds.add(requestId);
        }
      }

      const optimisticTileIdsToDrop = new Set();
      settledRequestIds.forEach((requestId) => {
        const pending = optimisticPlacementIndex.get(requestId);
        if (!pending) return;
        (pending.tileIds || []).forEach((tileId) => optimisticTileIdsToDrop.add(String(tileId)));
        optimisticPlacementIndex.delete(requestId);
      });

      setTiles((prevTiles) => {
        const filteredPrevTiles = prevTiles.filter((tile) => {
          if (optimisticTileIdsToDrop.has(String(tile?._id))) {
            return false;
          }
          if (tile?.optimisticPlacement === true) {
            const parentKey = getParentKeyFromTile(tile);
            if (incomingParentKeys.has(parentKey)) {
              return false;
            }
          }
          return true;
        });

        const byId = new Map(filteredPrevTiles.map((tile) => [getRealtimeTileIdentityKey(tile), tile]));
        acceptedTiles.forEach((tile) => {
          byId.set(getRealtimeTileIdentityKey(tile), tile);
        });
        return Array.from(byId.values());
      });
    } else if (action === "deletedTile") {
      if (Array.isArray(tileData)) {
        const deleteIds = new Set(tileData.map((id) => normalizeEntityId(id) || String(id)));
        setTiles((prevTiles) => prevTiles.filter((tile) => !deleteIds.has(normalizeEntityId(tile?._id) || String(tile?._id))));
      } else if (tileData && Array.isArray(tileData.tileIds)) {
        const { tileIds, requestId } = tileData;
        const deleteIds = new Set(tileIds.map((id) => normalizeEntityId(id) || String(id)));
        setTiles((prevTiles) => prevTiles.filter((tile) => !deleteIds.has(normalizeEntityId(tile?._id) || String(tile?._id))));
        if (requestId) {
          setPendingAllianceDeletions((prev) => {
            if (!prev[requestId]) {
              return prev;
            }
            const { [requestId]: _, ...rest } = prev;
            pendingAllianceDeletionsRef.current = rest;
            return rest;
          });
        }
      }
    }

    if (action === "newTile" || action === "deletedTile") {
      scheduleTerritoryCaptureRefreshRef.current?.();
    }
  }, []);

  const handleTileError = useCallback((errorPayload = {}) => {
    const { requestId, message } = errorPayload;
    if (requestId && optimisticPlacementIndexRef.current.has(requestId)) {
      const pendingOptimisticPlacement = optimisticPlacementIndexRef.current.get(requestId);
      optimisticPlacementIndexRef.current.delete(requestId);
      const optimisticTileIds = new Set(
        Array.isArray(pendingOptimisticPlacement?.tileIds)
          ? pendingOptimisticPlacement.tileIds.map((tileId) => String(tileId))
          : []
      );
      if (optimisticTileIds.size) {
        setTiles((prevTiles) => prevTiles.filter((tile) => !optimisticTileIds.has(String(tile?._id))));
      }
    }

    if (requestId && pendingAllianceDeletionsRef.current[requestId]) {
      const pendingTiles = pendingAllianceDeletionsRef.current[requestId];
      setTiles((prevTiles) => {
        const existingIds = new Set(prevTiles.map((tile) => tile._id));
        const restoredTiles = [...prevTiles];
        pendingTiles.forEach((tile) => {
          if (!existingIds.has(tile._id)) {
            restoredTiles.push(tile);
          }
        });
        return restoredTiles;
      });
      setPendingAllianceDeletions((prev) => {
        const { [requestId]: _, ...rest } = prev;
        pendingAllianceDeletionsRef.current = rest;
        return rest;
      });
    }

    if (message) {
      showError(message);
    }
  }, [showError]);

  const buildViewportTileWindow = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const corners = [
      screenToGrid({
        clientX: rect.left,
        clientY: rect.top,
        canvasRect: rect,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
        zoom,
        tileSize,
      }),
      screenToGrid({
        clientX: rect.right,
        clientY: rect.top,
        canvasRect: rect,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
        zoom,
        tileSize,
      }),
      screenToGrid({
        clientX: rect.left,
        clientY: rect.bottom,
        canvasRect: rect,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
        zoom,
        tileSize,
      }),
      screenToGrid({
        clientX: rect.right,
        clientY: rect.bottom,
        canvasRect: rect,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
        zoom,
        tileSize,
      })
    ];

    const clampCoord = (value) => Math.max(0, Math.min(gridSize - 1, value));
    const minX = clampCoord(Math.floor(Math.min(...corners.map((corner) => corner.gridX)) - TILE_WINDOW_PADDING));
    const maxX = clampCoord(Math.ceil(Math.max(...corners.map((corner) => corner.gridX)) + TILE_WINDOW_PADDING));
    const minY = clampCoord(Math.floor(Math.min(...corners.map((corner) => corner.gridY)) - TILE_WINDOW_PADDING));
    const maxY = clampCoord(Math.ceil(Math.max(...corners.map((corner) => corner.gridY)) + TILE_WINDOW_PADDING));
    return { minX, maxX, minY, maxY };
  }, [gridSize, offset.x, offset.y, tileSize, zoom]);

  const isWindowCoveredByBounds = useCallback((loadedWindow, nextWindow, margin = 0) => {
    if (!loadedWindow || !nextWindow) return false;
    return (
      nextWindow.minX >= loadedWindow.minX + margin
      && nextWindow.maxX <= loadedWindow.maxX - margin
      && nextWindow.minY >= loadedWindow.minY + margin
      && nextWindow.maxY <= loadedWindow.maxY - margin
    );
  }, []);

  const isWindowCoveredByLoadedBounds = useCallback((nextWindow) => {
    const loadedWindow = loadedTileWindowRef.current;
    return isWindowCoveredByBounds(loadedWindow, nextWindow, TILE_WINDOW_REFETCH_MARGIN);
  }, [isWindowCoveredByBounds]);

  const fetchUpdatedTileData = useCallback(async ({ force = false } = {}) => {
    if (!selectedPlanId || !selectedNationId) return;

    const tileWindow = buildViewportTileWindow();
    if (!tileWindow) return;
    const deadSpaceMode = zoom <= DEAD_SPACE_BASE_TERRAIN_ONLY_ZOOM_THRESHOLD
      ? DEAD_SPACE_FETCH_MODE_BASE_ONLY
      : DEAD_SPACE_FETCH_MODE_ALL;

    if (!force && loadedTileWindowRef.current?.deadSpaceMode === deadSpaceMode && isWindowCoveredByLoadedBounds(tileWindow)) {
      return;
    }

    if (!force && tileFetchInFlightWindowRef.current?.deadSpaceMode === deadSpaceMode && isWindowCoveredByBounds(tileFetchInFlightWindowRef.current, tileWindow, 0)) {
      return;
    }

    const requestSequence = tileWindowFetchSequenceRef.current + 1;
    tileWindowFetchSequenceRef.current = requestSequence;
    if (tileFetchAbortControllerRef.current) {
      tileFetchAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    tileFetchAbortControllerRef.current = abortController;
    const requestedWindow = { ...tileWindow, deadSpaceMode };
    tileFetchInFlightWindowRef.current = requestedWindow;
    setIsTileWindowLoading(true);

    try {
      const tilesResponse = await axios.get(
        `${apiUrl}/nations/${selectedNationId}/plans/${selectedPlanId}/tiles`,
        {
          params: {
            minX: tileWindow.minX,
            maxX: tileWindow.maxX,
            minY: tileWindow.minY,
            maxY: tileWindow.maxY,
            deadSpaceMode
          },
          signal: abortController.signal
        }
      );

      if (abortController.signal.aborted || tileWindowFetchSequenceRef.current !== requestSequence) {
        return;
      }

      const responseTiles = Array.isArray(tilesResponse.data)
        ? tilesResponse.data
        : (tilesResponse.data?.tiles || []);
      setTiles(responseTiles);
      loadedTileWindowRef.current = requestedWindow;
    } catch (error) {
      const wasCanceled = abortController.signal.aborted
        || error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError';
      if (wasCanceled) {
        return;
      }
      if (force) {
        showError(error?.response?.data?.message || 'Failed to fetch tile window.');
      }
    } finally {
      if (tileFetchAbortControllerRef.current === abortController) {
        tileFetchAbortControllerRef.current = null;
      }
      if (tileFetchInFlightWindowRef.current === requestedWindow) {
        tileFetchInFlightWindowRef.current = null;
      }
      if (tileWindowFetchSequenceRef.current === requestSequence) {
        setIsTileWindowLoading(false);
      }
    }
  }, [axios, buildViewportTileWindow, isWindowCoveredByBounds, isWindowCoveredByLoadedBounds, selectedNationId, selectedPlanId, showError, zoom]);

  const fetchDrawingData = useCallback(async ({ force = false } = {}) => {
    if (!selectedPlanId || !selectedNationId) return;

    try {
      const response = await axios.get(
        `${apiUrl}/nations/${selectedNationId}/plans/${selectedPlanId}/drawings`
      );
      const responseDrawings = Array.isArray(response.data)
        ? response.data
        : (response.data?.drawings || []);
      setDrawings(
        responseDrawings.map((drawing) => ({
          ...drawing,
          _id: normalizeEntityId(drawing?._id)
        }))
      );
    } catch (error) {
      if (force) {
        showError(error?.response?.data?.message || 'Failed to fetch map drawings.');
      }
    }
  }, [axios, selectedNationId, selectedPlanId, showError]);

  const fetchBorderData = useCallback(async ({ force = false } = {}) => {
    if (!selectedPlanId || !selectedNationId) return;

    try {
      const response = await axios.get(
        `${apiUrl}/nations/${selectedNationId}/plans/${selectedPlanId}/borders`
      );
      const responseBorders = Array.isArray(response.data)
        ? response.data
        : (response.data?.borders || []);
      const responsePlacedAlliances = Array.isArray(response.data?.placedAlliances)
        ? response.data.placedAlliances
        : [];
      const responseTerritoryCaptureRows = Array.isArray(response.data?.territoryCapture?.rows)
        ? response.data.territoryCapture.rows
        : [];
      const responseTerritoryCaptureContested = response.data?.territoryCapture?.contested || {};
      const normalizeContestedEntries = (entries = []) => {
        const byKey = new Map();
        (Array.isArray(entries) ? entries : []).forEach((entry) => {
          const x = Math.round(Number(entry?.x));
          const y = Math.round(Number(entry?.y));
          if (!Number.isFinite(x) || !Number.isFinite(y)) return;
          byKey.set(`${x},${y}`, { x, y });
        });
        return Array.from(byKey.values());
      };

      const normalizeTerritoryCaptureMetrics = (metrics = {}) => ({
        benefitSubTypes: BENEFIT_SUBTYPE_KEYS.reduce((acc, subType) => {
          acc[subType] = Number(metrics?.benefitSubTypes?.[subType] || 0);
          return acc;
        }, {}),
        eliteMineLevels: ELITE_MINE_LEVELS.reduce((acc, level) => {
          acc[level] = Number(metrics?.eliteMineLevels?.[level] || 0);
          return acc;
        }, {}),
        totals: {
          benefit: Number(metrics?.totals?.benefit || 0),
          eliteMine: Number(metrics?.totals?.eliteMine || 0)
        }
      });

      setBorders(
        responseBorders.map((border) => ({
          ...border,
          _id: normalizeEntityId(border?._id)
        }))
      );
      setPlacedBorderAlliances(
        responsePlacedAlliances
          .map((alliance) => ({
            ...alliance,
            _id: normalizeEntityId(alliance?._id)
          }))
          .filter((alliance) => !!alliance._id)
      );
      setTerritoryCaptureRows(
        responseTerritoryCaptureRows.map((row) => {
          const alliance = row?.alliance || {};
          return {
            ...row,
            alliance: {
              ...alliance,
              _id: normalizeEntityId(alliance?._id || alliance)
            },
            territoryBuildingCount: Number(row?.territoryBuildingCount || 0),
            metrics: normalizeTerritoryCaptureMetrics(row?.metrics || {})
          };
        }).filter((row) => !!row?.alliance?._id)
      );
      setTerritoryCaptureContested({
        benefit: normalizeContestedEntries(responseTerritoryCaptureContested?.benefit),
        eliteMine: normalizeContestedEntries(responseTerritoryCaptureContested?.eliteMine)
      });
    } catch (error) {
      if (force) {
        showError(error?.response?.data?.message || 'Failed to fetch map borders.');
      }
    }
  }, [axios, selectedNationId, selectedPlanId, showError]);

  useEffect(() => {
    fetchUpdatedTileDataRef.current = fetchUpdatedTileData;
  }, [fetchUpdatedTileData]);

  useEffect(() => {
    fetchDrawingDataRef.current = fetchDrawingData;
  }, [fetchDrawingData]);

  useEffect(() => {
    fetchBorderDataRef.current = fetchBorderData;
  }, [fetchBorderData]);

  useEffect(() => {
    territoryCaptureMetricsEnabledRef.current = isTerritoryCaptureMetricsEnabled;
    if (!isTerritoryCaptureMetricsEnabled && territoryCaptureRefreshTimeoutRef.current) {
      clearTimeout(territoryCaptureRefreshTimeoutRef.current);
      territoryCaptureRefreshTimeoutRef.current = null;
    }
  }, [isTerritoryCaptureMetricsEnabled]);

  const scheduleTerritoryCaptureRefresh = useCallback((delayMs = TERRITORY_CAPTURE_REFRESH_DEBOUNCE_MS) => {
    if (!territoryCaptureMetricsEnabledRef.current) return;
    if (!selectedNationId || !selectedPlanId) return;
    if (territoryCaptureRefreshTimeoutRef.current) {
      clearTimeout(territoryCaptureRefreshTimeoutRef.current);
    }
    territoryCaptureRefreshTimeoutRef.current = setTimeout(() => {
      territoryCaptureRefreshTimeoutRef.current = null;
      fetchBorderDataRef.current?.({ force: false });
    }, delayMs);
  }, [selectedNationId, selectedPlanId]);

  useEffect(() => {
    scheduleTerritoryCaptureRefreshRef.current = scheduleTerritoryCaptureRefresh;
  }, [scheduleTerritoryCaptureRefresh]);

  const schedulePostMutationTileRefresh = useCallback((delayMs = POST_MUTATION_REFRESH_DEBOUNCE_MS) => {
    if (postMutationRefreshTimeoutRef.current) {
      clearTimeout(postMutationRefreshTimeoutRef.current);
    }
    postMutationRefreshTimeoutRef.current = setTimeout(() => {
      postMutationRefreshTimeoutRef.current = null;
      fetchUpdatedTileDataRef.current?.({ force: true });
    }, delayMs);
  }, []);

  useEffect(() => {
    viewportTileWindowRef.current = buildViewportTileWindow();
  }, [buildViewportTileWindow]);

  useEffect(() => {
    if (optimizeMode !== 'territory') {
      setIsOptimizingTerritory(false);
      setOptimizationJobId(null);
      setOptimizationPreviewFlags([]);
    }
    if (optimizeMode !== 'hive') {
      setIsPickingHiveTarget(false);
    }
  }, [optimizeMode]);

  const resetOptimizationWatchdog = useCallback(() => {
    if (optimizationWatchdogRef.current) {
      clearTimeout(optimizationWatchdogRef.current);
    }
    optimizationWatchdogRef.current = setTimeout(() => {
      setIsOptimizingTerritory(false);
      setOptimizationPreviewFlags([]);
      setOptimizationJobId(null);
      setOptimizeStatusMessage('Optimization stream timed out. Refreshing map.');
      fetchUpdatedTileData({ force: true });
    }, 30000);
  }, [fetchUpdatedTileData]);

  const handleOptimizeEvent = useCallback((payload = {}) => {
    if (optimizeMode !== 'territory') return;
    const { type, jobId, message } = payload;
    if (!type) return;

    if (optimizationJobId && jobId && String(optimizationJobId) !== String(jobId)) {
      return;
    }

    if (type === 'start') {
      setOptimizationJobId(jobId || null);
      setOptimizationPreviewFlags([]);
      setIsOptimizingTerritory(true);
      setOptimizeStatusMessage('Optimization running...');
      resetOptimizationWatchdog();
      return;
    }

    if (type === 'complete') {
      if (optimizationWatchdogRef.current) clearTimeout(optimizationWatchdogRef.current);
      setOptimizationPreviewFlags([]);
      setOptimizationJobId(null);
      setIsOptimizingTerritory(false);
      setOptimizeStatusMessage('Optimization completed.');
      fetchUpdatedTileData({ force: true });
      return;
    }

    if (type === 'error') {
      if (optimizationWatchdogRef.current) clearTimeout(optimizationWatchdogRef.current);
      setOptimizationPreviewFlags([]);
      setOptimizationJobId(null);
      setIsOptimizingTerritory(false);
      setOptimizeStatusMessage('');
      showError(message || 'Optimization failed.');
    }
  }, [optimizationJobId, optimizeMode, showError, resetOptimizationWatchdog]);

  const handleDrawingUpdate = useCallback((payload = {}) => {
    const action = payload.action;
    if (action === 'drawingUpserted') {
      const drawing = payload.drawing;
      const drawingId = normalizeEntityId(drawing?._id);
      if (!drawingId) return;
      const normalizedDrawing = snapshotDrawingForHistory({ ...drawing, _id: drawingId });
      if (!normalizedDrawing) return;
      setDrawings((previous) => {
        const existingIndex = previous.findIndex((item) => normalizeEntityId(item?._id) === drawingId);
        if (existingIndex === -1) {
          return [...previous, normalizedDrawing];
        }
        const next = [...previous];
        next[existingIndex] = normalizedDrawing;
        return next;
      });

      const pendingCreates = pendingDrawingCreateHistoryRef.current;
      if (pendingCreates.length) {
        const pendingIndex = pendingCreates.findIndex((entry) => (
          areDrawingSnapshotsEquivalent(entry?.snapshot, normalizedDrawing, { ignoreId: true })
        ));
        if (pendingIndex !== -1) {
          const [matchedEntry] = pendingCreates.splice(pendingIndex, 1);
          const forwardSteps = [{ type: 'drawingUpsert', drawing: normalizedDrawing }];
          const inverseSteps = [{ type: 'drawingDelete', drawingId }];
          const label = matchedEntry?.label || `Draw ${getDrawingKindLabel(normalizedDrawing.kind)}`;
          const createHistoryEntry = createHistoryEntryRef.current;
          const pushHistoryEntry = pushHistoryEntryRef.current;
          if (createHistoryEntry && pushHistoryEntry) {
            pushHistoryEntry(createHistoryEntry(label, forwardSteps, inverseSteps));
          }
        }
      }
      return;
    }

    if (action === 'drawingDeleted') {
      const drawingId = normalizeEntityId(payload.drawingId);
      if (!drawingId) return;
      if (normalizeEntityId(selectedDrawingId) === drawingId) {
        setSelectedDrawingId(null);
      }
      setDrawings((previous) => previous.filter((item) => normalizeEntityId(item?._id) !== drawingId));
    }
  }, [
    areDrawingSnapshotsEquivalent,
    getDrawingKindLabel,
    selectedDrawingId,
    snapshotDrawingForHistory
  ]);

  const handleDrawingError = useCallback((payload = {}) => {
    if (pendingDrawingCreateHistoryRef.current.length) {
      pendingDrawingCreateHistoryRef.current.shift();
    }
    const message = payload?.message || 'There was a problem updating map drawings.';
    showError(message);
  }, [showError]);

  const handleBorderUpdate = useCallback((payload = {}) => {
    const action = payload.action;
    if (action === 'borderDeleted') {
      const deletedBorderId = normalizeEntityId(payload.borderId);
      const deletedAllianceId = String(payload.allianceId || '');
      setBorders((previous) => previous.filter((border) => {
        const borderId = normalizeEntityId(border?._id);
        const allianceId = normalizeEntityId(border?.alliance?._id || border?.alliance);
        if (deletedBorderId && borderId === deletedBorderId) return false;
        if (!deletedBorderId && deletedAllianceId && allianceId === deletedAllianceId) return false;
        return true;
      }));
      return;
    }

    if (action === 'borderUpserted') {
      fetchBorderDataRef.current?.({ force: true });
    }
  }, []);

  const handleBorderError = useCallback((payload = {}) => {
    const message = payload?.message || 'There was a problem updating map borders.';
    showError(message);
  }, [showError]);

  const { emitTile, socketId } = useSocket(socketUrl, selectedNation?._id, selectedPlan?._id, {
    onTileUpdate: handleTileUpdate,
    onTileError: handleTileError,
    onOptimizeEvent: handleOptimizeEvent,
    onDrawingUpdate: handleDrawingUpdate,
    onDrawingError: handleDrawingError,
    onBorderUpdate: handleBorderUpdate,
    onBorderError: handleBorderError
  });

  useEffect(() => {
    if (!isOptimizingTerritory) {
      if (optimizationGhostIntervalRef.current) {
        clearInterval(optimizationGhostIntervalRef.current);
        optimizationGhostIntervalRef.current = null;
      }
      setOptimizationPreviewFlags([]);
      return;
    }

    if (!optimizationGhostCandidates.length) {
      setOptimizationPreviewFlags([]);
      return;
    }

    const updateGhostFlags = () => {
      const maxGhosts = Math.min(4, optimizationGhostCandidates.length);
      const minGhosts = Math.min(2, maxGhosts);
      const count = maxGhosts <= 1
        ? 1
        : Math.floor(Math.random() * (maxGhosts - minGhosts + 1)) + minGhosts;

      const chosenIndexes = new Set();
      while (chosenIndexes.size < count) {
        chosenIndexes.add(Math.floor(Math.random() * optimizationGhostCandidates.length));
      }
      const nextGhosts = Array.from(chosenIndexes).map((index) => ({
        ...optimizationGhostCandidates[index],
        ghost: true
      }));
      setOptimizationPreviewFlags(nextGhosts);
    };

    updateGhostFlags();
    optimizationGhostIntervalRef.current = setInterval(updateGhostFlags, 220);

    return () => {
      if (optimizationGhostIntervalRef.current) {
        clearInterval(optimizationGhostIntervalRef.current);
        optimizationGhostIntervalRef.current = null;
      }
    };
  }, [isOptimizingTerritory, optimizationGhostCandidates]);

  const handleDeleteAllianceTerritory = useCallback((tile) => {
    if (!selectedNation || !selectedPlan) {
      showError('Please select a nation and plan before deleting alliance territory.');
      return;
    }

    const allianceId = tile?.additionalTileInfo?.alliance?._id;
    if (!allianceId) {
      showError('Alliance information is missing for this tile.');
      return;
    }

    const getBaseType = (type) => type?.replace(/-child$/, '');
    const allianceTiles = tiles.filter((currentTile) => {
      const currentAllianceId = currentTile?.additionalTileInfo?.alliance?._id;
      if (!currentAllianceId || currentAllianceId !== allianceId) {
        return false;
      }
      const baseType = getBaseType(currentTile.type);
      return ALLIANCE_TERRITORY_TYPES.includes(baseType);
    });

    if (!allianceTiles.length) {
      showError('No alliance tiles were found to delete.');
      return;
    }

    const requestId = `deleteAlliance-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const tileIdsToRemove = allianceTiles.map((currentTile) => currentTile._id);

    const confirmed = window.confirm(`Are you sure you want to delete ${allianceTiles.length} tiles for this alliance? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    const deletedParentTemplates = collectParentTemplatesFromTiles(allianceTiles);
    if (deletedParentTemplates.length) {
      const forwardSteps = [{ type: 'delete', parents: deletedParentTemplates }];
      const inverseSteps = [{ type: 'place', parents: deletedParentTemplates }];
      pushHistoryEntry(createHistoryEntry('Delete alliance territory', forwardSteps, inverseSteps));
    }

    setPendingAllianceDeletions((prev) => {
      const updated = { ...prev, [requestId]: allianceTiles };
      pendingAllianceDeletionsRef.current = updated;
      return updated;
    });

    setTiles((prevTiles) => prevTiles.filter((currentTile) => !tileIdsToRemove.includes(currentTile._id)));

    emitTile({
      action: "deleteAllianceTerritory",
      tileData: {
        nation: selectedNation._id,
        plan: selectedPlan._id,
        allianceId,
        requestId
      }
    });
    schedulePostMutationTileRefresh();

    setIsTileDetailsModalOpen(false);
    setSelectedTile(null);
  }, [emitTile, schedulePostMutationTileRefresh, selectedNation, selectedPlan, showError, tiles]);

  const updateCenterCoords = () => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const coords = screenToGrid({
      clientX: rect.left + canvas.width / 2,
      clientY: rect.bottom - canvas.height / 2,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    });
    setCenterCoords(coords);
  };

  const isTileTypeFilterEnabled = (type) => {
    return !!multiSelectFilters[getBaseTileType(type)];
  };

  const getImportantCoordinateCategory = useCallback((baseType = '') => {
    if (baseType === 'benefit') return 'benefit';
    if (baseType === 'eliteMine') return 'eliteMine';
    if (IMPORTANT_COORDINATE_ALLIANCE_TYPES.includes(baseType)) return 'alliance';
    return null;
  }, []);

  const getImportantCoordinateLabelText = useCallback((tile = {}) => {
    const baseType = getBaseTileType(tile?.type);
    const x = Number(tile?.x);
    const y = Number(tile?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return '';
    }

    let labelPrefix = '';
    if (baseType === 'benefit') {
      const benefitSubType = tile?.additionalTileInfo?.benefitSubType || DEFAULT_BENEFIT_SUBTYPE;
      labelPrefix = BENEFIT_SUBTYPE_LABELS[benefitSubType] || 'Benefit Building';
    } else if (baseType === 'eliteMine') {
      const parsedLevel = Number.parseInt(tile?.additionalTileInfo?.level, 10);
      labelPrefix = Number.isFinite(parsedLevel) && parsedLevel > 0
        ? `Level ${parsedLevel} Elite Mine`
        : 'Elite Mine';
    } else if (IMPORTANT_COORDINATE_ALLIANCE_TYPES.includes(baseType)) {
      labelPrefix = IMPORTANT_COORDINATE_ALLIANCE_LABELS[baseType] || tileInformation?.[baseType]?.niceName || 'Alliance Building';
    } else {
      return '';
    }

    return `${labelPrefix} ${x}:${y}`;
  }, []);

  const clearMultiSelectDragState = useCallback(() => {
    setMultiSelectDragStart(null);
    setMultiSelectDragCurrent(null);
    setMultiSelectLassoPath([]);
    setIsMultiSelectDragging(false);
  }, []);

  const clearMultiSelectionState = useCallback(() => {
    setSelectedParentTileKeys([]);
    clearMultiSelectDragState();
  }, [clearMultiSelectDragState]);

  const clearCopyModeState = useCallback(() => {
    setCopiedTileTemplates([]);
    setCopiedSourceParentKeys([]);
  }, []);

  const clearCutModeState = useCallback(() => {
    setCutTileTemplates([]);
    setCutSourceParentKeys([]);
  }, []);

  const closeAddMetadataModals = useCallback(() => {
    setIsAddCityModalOpen(false);
    setIsAddAllianceBuildingModalOpen(false);
    setIsAddEliteMineModalOpen(false);
    setIsAddBenefitBuildingModalOpen(false);
  }, []);

  const clearQuickAddState = useCallback(() => {
    setSelectedTileAddType(null);
    setPendingQuickAddPlacement(null);
    setSelectedGridPosition(null);
    closeAddMetadataModals();
  }, [closeAddMetadataModals]);

  const clearDrawingState = useCallback(() => {
    setDrawingDraft(null);
    setSelectedDrawingId(null);
    drawingStartRef.current = null;
    drawingMoveStateRef.current = null;
  }, []);

  const cancelAllMapModes = useCallback(() => {
    setIsCopyMode(false);
    setIsCutMode(false);
    clearCopyModeState();
    clearCutModeState();

    setIsMultiSelectLocked(false);
    clearMultiSelectionState();

    setIsQuickAddPanelOpen(false);
    clearQuickAddState();
    setIsDrawingMode(false);
    setBorderInteractionMode('idle');
    setBorderDraftPoints([]);
    setBorderHoverGridPoint(null);
    setBorderDragVertexIndex(-1);
    setBorderPreviewMetrics(null);
    setIsBorderPreviewLoading(false);
    clearDrawingState();

    setIsTileDetailsModalOpen(false);
    setSelectedTile(null);
    setIsNavigationModalOpen(false);
    setIsFindCommanderModalOpen(false);
    setCursorPreviewGrid(null);

    setIsOptimizePanelOpen(false);
  }, [clearCopyModeState, clearCutModeState, clearMultiSelectionState, clearQuickAddState, clearDrawingState]);

  const getCanvasPointFromClient = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    return {
      canvas,
      canvasX,
      canvasY
    };
  }, []);

  const copyTextToClipboard = useCallback(async (textValue) => {
    const normalizedText = String(textValue || '').trim();
    if (!normalizedText) {
      return false;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedText);
        return true;
      }
    } catch (error) {
      // Continue to fallback path.
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = normalizedText;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return !!copied;
    } catch (error) {
      return false;
    }
  }, []);

  const handleImportantCoordinateLabelClick = useCallback((clientX, clientY) => {
    if (!isImportantCoordinateOverlayEnabled) {
      return false;
    }
    const canvasPoint = getCanvasPointFromClient(clientX, clientY);
    if (!canvasPoint) {
      return false;
    }
    const { canvasX, canvasY } = canvasPoint;
    const hitboxes = importantCoordinateLabelHitboxesRef.current || [];
    for (let index = hitboxes.length - 1; index >= 0; index -= 1) {
      const hitbox = hitboxes[index];
      if (!hitbox) continue;
      if (canvasX >= hitbox.minX && canvasX <= hitbox.maxX && canvasY >= hitbox.minY && canvasY <= hitbox.maxY) {
        copyTextToClipboard(hitbox.copyText).then((didCopy) => {
          if (!didCopy) {
            showError('Failed to copy coordinate label to clipboard.');
            return;
          }
          showInfo(`Copied: ${hitbox.copyText}`);
        });
        return true;
      }
    }
    return false;
  }, [copyTextToClipboard, getCanvasPointFromClient, isImportantCoordinateOverlayEnabled, showError, showInfo]);

  const getGridPointFromClient = useCallback((clientX, clientY) => {
    const canvasPoint = getCanvasPointFromClient(clientX, clientY);
    if (!canvasPoint) return null;
    const { canvas, canvasX, canvasY } = canvasPoint;
    const projectedX = (canvasX - canvas.width / 2 - offset.x) / zoom;
    const projectedY = ((canvasY - canvas.height / 2 - offset.y) / zoom) * -1;

    const rawGridX = ((projectedX / (tileSize / 2)) + (projectedY / (tileSize / 4))) / 2 + 1;
    const rawGridY = ((projectedY / (tileSize / 4)) - (projectedX / (tileSize / 2))) / 2 + 1;

    return {
      x: Number(rawGridX.toFixed(3)),
      y: Number(rawGridY.toFixed(3))
    };
  }, [getCanvasPointFromClient, offset.x, offset.y, tileSize, zoom]);

  const getGridCellFromClient = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const projected = screenToGrid({
      clientX,
      clientY,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    });
    const gridX = Number(projected?.gridX);
    const gridY = Number(projected?.gridY);
    if (!Number.isFinite(gridX) || !Number.isFinite(gridY)) {
      return null;
    }
    return { gridX, gridY };
  }, [offset.x, offset.y, tileSize, zoom]);

  const getScreenPointFromGrid = useCallback((gridPoint = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    // Drawing points are stored in map-grid coordinates (1-based), so project with
    // a one-cell compensation to align pointer placement with rendered output.
    const projectedGridX = Number(gridPoint.x) - 1;
    const projectedGridY = Number(gridPoint.y) - 1;
    return gridToScreen({
      gridX: projectedGridX,
      gridY: projectedGridY,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
  }, [offset.x, offset.y, tileSize, zoom]);

  const getBorderScreenPointFromGrid = useCallback((gridPoint = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return gridToScreen({
      gridX: Number(gridPoint?.x),
      gridY: Number(gridPoint?.y),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
  }, [offset.x, offset.y, tileSize, zoom]);

  const getTextDrawingScreenLayout = useCallback((drawing, context) => {
    if (!drawing || !context) return null;
    const points = Array.isArray(drawing.points) ? drawing.points : [];
    if (!points.length) return null;

    const textValue = clampDrawingText(drawing.text || '');
    const hasText = String(textValue).trim().length > 0;
    if (!hasText) return null;

    const primaryPoint = getScreenPointFromGrid(points[0]);
    if (!primaryPoint) return null;

    let boxX = primaryPoint.x;
    let boxY = primaryPoint.y;
    let baseWidth = 0;
    let baseHeight = 0;

    if (points.length >= 2) {
      const secondaryPoint = getScreenPointFromGrid(points[1]);
      if (!secondaryPoint) return null;
      boxX = Math.min(primaryPoint.x, secondaryPoint.x);
      boxY = Math.min(primaryPoint.y, secondaryPoint.y);
      baseWidth = Math.abs(secondaryPoint.x - primaryPoint.x);
      baseHeight = Math.abs(secondaryPoint.y - primaryPoint.y);
    } else {
      const legacyScale = Math.max(0.4, Math.min(8, Number(drawing.scale || 1)));
      const legacyFontSize = Math.max(11, 14 * legacyScale * Math.max(0.75, zoom));
      const legacyPadding = Math.max(4, 5 * legacyScale * Math.max(0.75, zoom));
      context.font = `600 ${legacyFontSize}px "Segoe UI", sans-serif`;
      const legacyTextWidth = context.measureText(textValue).width;
      baseWidth = legacyTextWidth + (legacyPadding * 2);
      baseHeight = legacyFontSize + (legacyPadding * 2);
      boxY = primaryPoint.y - baseHeight;
    }

    const safeBaseWidth = Math.max(18, baseWidth);
    const safeBaseHeight = Math.max(18, baseHeight);
    const padding = Math.max(3, Math.min(18, Math.round(safeBaseHeight * 0.12)));
    const fontSize = Math.max(11, safeBaseHeight - (padding * 2));

    context.font = `600 ${fontSize}px "Segoe UI", sans-serif`;
    const measuredTextWidth = context.measureText(textValue).width;
    const requiredWidth = measuredTextWidth + (padding * 2);
    const finalWidth = Math.max(safeBaseWidth, requiredWidth);
    const finalHeight = Math.max(safeBaseHeight, fontSize + (padding * 2));

    return {
      boxX,
      boxY,
      boxWidth: finalWidth,
      boxHeight: finalHeight,
      fontSize,
      padding,
      textValue,
      textColor: String(drawing?.style?.textColor || drawing?.style?.color || DEFAULT_DRAWING_TEXT_COLOR),
      backgroundColor: String(drawing?.style?.backgroundColor || DEFAULT_DRAWING_TEXT_BACKGROUND),
      borderColor: String(drawing?.style?.color || drawing?.style?.textColor || DEFAULT_DRAWING_TEXT_COLOR)
    };
  }, [getScreenPointFromGrid, zoom]);

  const distanceFromPointToSegment = useCallback((point, start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) {
      const distX = point.x - start.x;
      const distY = point.y - start.y;
      return Math.sqrt((distX * distX) + (distY * distY));
    }
    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / ((dx * dx) + (dy * dy));
    const clamped = Math.max(0, Math.min(1, t));
    const projectionX = start.x + clamped * dx;
    const projectionY = start.y + clamped * dy;
    const distanceX = point.x - projectionX;
    const distanceY = point.y - projectionY;
    return Math.sqrt((distanceX * distanceX) + (distanceY * distanceY));
  }, []);

  const findDrawingAtClientPoint = useCallback((clientX, clientY) => {
    const canvasPoint = getCanvasPointFromClient(clientX, clientY);
    if (!canvasPoint) return null;
    const { canvas, canvasX, canvasY } = canvasPoint;
    const context = canvas.getContext('2d');
    if (!context) return null;

    const pointer = { x: canvasX, y: canvasY };
    const drawingSearchOrder = [...drawings].reverse();
    for (const drawing of drawingSearchOrder) {
      const points = Array.isArray(drawing?.points) ? drawing.points : [];
      if (!points.length) continue;

      if (drawing.kind === 'text') {
        const layout = getTextDrawingScreenLayout(drawing, context);
        if (!layout) continue;
        const margin = 8;
        if (
          pointer.x >= layout.boxX - margin
          && pointer.x <= layout.boxX + layout.boxWidth + margin
          && pointer.y >= layout.boxY - margin
          && pointer.y <= layout.boxY + layout.boxHeight + margin
        ) {
          return drawing;
        }
        continue;
      }

      if (points.length < 2) continue;
      const screenPoints = points
        .map((point) => getScreenPointFromGrid(point))
        .filter(Boolean);
      if (screenPoints.length < 2) continue;
      const tolerance = Math.max(9, Number(drawing?.style?.width || 2) * Math.max(2, zoom * 2));
      for (let index = 1; index < screenPoints.length; index += 1) {
        const distance = distanceFromPointToSegment(pointer, screenPoints[index - 1], screenPoints[index]);
        if (distance <= tolerance) {
          return drawing;
        }
      }
    }

    return null;
  }, [distanceFromPointToSegment, drawings, getCanvasPointFromClient, getScreenPointFromGrid, getTextDrawingScreenLayout, zoom]);

  const persistDrawing = useCallback((drawingPayload) => {
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before drawing.');
      return;
    }

    emitTile({
      action: 'upsertDrawing',
      tileData: {
        nation: selectedNation._id,
        plan: selectedPlan._id,
        drawing: drawingPayload
      }
    });
  }, [emitTile, selectedNation, selectedPlan, showError]);

  const deleteDrawing = useCallback((drawingId) => {
    if (!selectedNation?._id || !selectedPlan?._id || !drawingId) {
      return;
    }
    emitTile({
      action: 'deleteDrawing',
      tileData: {
        nation: selectedNation._id,
        plan: selectedPlan._id,
        drawingId
      }
    });
  }, [emitTile, selectedNation, selectedPlan]);

  const createDrawingWithHistory = useCallback((drawingPayload, labelOverride = '') => {
    const snapshot = snapshotDrawingForHistory(drawingPayload);
    if (!snapshot) return;
    pendingDrawingCreateHistoryRef.current.push({
      snapshot,
      label: labelOverride || `Draw ${getDrawingKindLabel(snapshot.kind)}`,
      requestedAt: Date.now()
    });
    if (pendingDrawingCreateHistoryRef.current.length > 40) {
      pendingDrawingCreateHistoryRef.current.splice(0, pendingDrawingCreateHistoryRef.current.length - 40);
    }
    persistDrawing(snapshot);
  }, [getDrawingKindLabel, persistDrawing, snapshotDrawingForHistory]);

  const updateDrawingWithHistory = useCallback((previousDrawing, nextDrawing, label = 'Edit Drawing') => {
    const previousSnapshot = snapshotDrawingForHistory(previousDrawing);
    const nextSnapshot = snapshotDrawingForHistory(nextDrawing);
    const drawingId = normalizeEntityId(nextSnapshot?._id || previousSnapshot?._id);
    if (!previousSnapshot || !nextSnapshot || !drawingId) {
      if (nextSnapshot) {
        persistDrawing({ ...nextSnapshot, _id: drawingId || nextSnapshot._id });
      }
      return;
    }

    const committedPrevious = { ...previousSnapshot, _id: drawingId };
    const committedNext = { ...nextSnapshot, _id: drawingId };
    const forwardSteps = [{ type: 'drawingUpsert', drawing: committedNext }];
    const inverseSteps = [{ type: 'drawingUpsert', drawing: committedPrevious }];
    const createHistoryEntry = createHistoryEntryRef.current;
    const pushHistoryEntry = pushHistoryEntryRef.current;
    if (createHistoryEntry && pushHistoryEntry) {
      pushHistoryEntry(createHistoryEntry(label, forwardSteps, inverseSteps));
    }
    persistDrawing(committedNext);
  }, [persistDrawing, snapshotDrawingForHistory]);

  const deleteDrawingWithHistory = useCallback((drawingToDelete, label = 'Delete Drawing') => {
    const snapshot = snapshotDrawingForHistory(drawingToDelete);
    const drawingId = normalizeEntityId(snapshot?._id);
    if (!snapshot || !drawingId) return;

    const forwardSteps = [{ type: 'drawingDelete', drawingId }];
    const inverseSteps = [{ type: 'drawingUpsert', drawing: { ...snapshot, _id: drawingId } }];
    const createHistoryEntry = createHistoryEntryRef.current;
    const pushHistoryEntry = pushHistoryEntryRef.current;
    if (createHistoryEntry && pushHistoryEntry) {
      pushHistoryEntry(createHistoryEntry(label, forwardSteps, inverseSteps));
    }
    deleteDrawing(drawingId);
  }, [deleteDrawing, snapshotDrawingForHistory]);

  const persistSelectedTextDrawingChanges = useCallback(() => {
    if (!selectedDrawing || selectedDrawing.kind !== 'text') return;
    const nextTextValue = clampDrawingText(selectedDrawingTextValue || '');
    const trimmedText = String(nextTextValue).trim();
    if (!trimmedText) {
      showError('Text annotations cannot be empty.');
      return;
    }
    const sanitizedStyle = {
      ...(selectedDrawing.style || {}),
      color: String(selectedDrawingTextColor || DEFAULT_DRAWING_TEXT_COLOR),
      textColor: String(selectedDrawingTextColor || DEFAULT_DRAWING_TEXT_COLOR),
      backgroundColor: String(selectedDrawingTextBackgroundColor || DEFAULT_DRAWING_TEXT_BACKGROUND)
    };
    updateDrawingWithHistory(selectedDrawing, {
      _id: selectedDrawing._id,
      kind: 'text',
      points: selectedDrawing.points || [],
      text: nextTextValue,
      scale: Math.max(0.4, Math.min(8, Number(selectedDrawing.scale || 1))),
      style: sanitizedStyle
    }, 'Edit Text Drawing');
  }, [
    selectedDrawing,
    selectedDrawingTextBackgroundColor,
    selectedDrawingTextColor,
    selectedDrawingTextValue,
    showError,
    updateDrawingWithHistory
  ]);

  const handleDrawingPointerStart = useCallback((clientX, clientY) => {
    if (!isDrawingMode || !canWriteTiles) return;

    if (drawingInteractionMode === 'erase') {
      if (!canDeleteTiles) {
        showError('You are not authorized to delete drawings on this map.');
        return;
      }
      const targetDrawing = findDrawingAtClientPoint(clientX, clientY);
      if (targetDrawing?._id) {
        const targetDrawingId = normalizeEntityId(targetDrawing._id);
        deleteDrawingWithHistory(targetDrawing);
        if (normalizeEntityId(selectedDrawingId) === targetDrawingId) {
          setSelectedDrawingId(null);
        }
      }
      return;
    }

    if (drawingInteractionMode === 'select') {
      const targetDrawing = findDrawingAtClientPoint(clientX, clientY);
      const targetDrawingId = normalizeEntityId(targetDrawing?._id);
      if (!targetDrawingId) {
        setSelectedDrawingId(null);
        drawingMoveStateRef.current = null;
        return;
      }
      setSelectedDrawingId(targetDrawingId);
      const point = getGridPointFromClient(clientX, clientY);
      if (!point) return;
      drawingMoveStateRef.current = {
        drawingId: targetDrawingId,
        startPoint: point,
        initialPoints: Array.isArray(targetDrawing.points)
          ? targetDrawing.points.map((item) => ({ x: Number(item.x), y: Number(item.y) }))
          : [],
        initialSnapshot: snapshotDrawingForHistory(targetDrawing),
        initialDrawing: {
          kind: targetDrawing.kind,
          text: targetDrawing.text || '',
          scale: targetDrawing.scale || 1,
          style: targetDrawing.style || { color: '#38bdf8', width: 2 }
        },
        lastPoints: Array.isArray(targetDrawing.points)
          ? targetDrawing.points.map((item) => ({ x: Number(item.x), y: Number(item.y) }))
          : [],
        moved: false
      };
      return;
    }

    const point = getGridPointFromClient(clientX, clientY);
    if (!point) return;
    setSelectedDrawingId(null);
    drawingMoveStateRef.current = null;

    const baseStyle = {
      color: drawingColor,
      width: drawingStrokeWidth
    };

    if (activeDrawingTool === 'text') {
      const nextTextValue = clampDrawingText(drawingTextValue || '');
      const trimmedText = String(nextTextValue).trim();
      if (!trimmedText) {
        showError('Enter text before placing a text box.');
        return;
      }
      drawingStartRef.current = point;
      setDrawingDraft({
        kind: 'text',
        points: [point, point],
        text: nextTextValue,
        scale: 1,
        style: {
          color: String(drawingTextColor || DEFAULT_DRAWING_TEXT_COLOR),
          width: drawingStrokeWidth,
          textColor: drawingTextColor,
          backgroundColor: drawingTextBackgroundColor
        }
      });
      return;
    }

    const draft = {
      kind: activeDrawingTool,
      points: [point],
      style: baseStyle
    };
    drawingStartRef.current = point;
    setDrawingDraft(draft);
  }, [
    activeDrawingTool,
    canDeleteTiles,
    canWriteTiles,
    createDrawingWithHistory,
    deleteDrawingWithHistory,
    drawingColor,
    drawingInteractionMode,
    drawingStrokeWidth,
    drawingTextBackgroundColor,
    drawingTextColor,
    drawingTextValue,
    findDrawingAtClientPoint,
    getGridPointFromClient,
    isDrawingMode,
    selectedDrawingId,
    showError,
    snapshotDrawingForHistory
  ]);

  const handleDrawingPointerMove = useCallback((clientX, clientY) => {
    if (!isDrawingMode) return;

    if (drawingInteractionMode === 'select') {
      const moveState = drawingMoveStateRef.current;
      if (!moveState?.drawingId) return;
      const point = getGridPointFromClient(clientX, clientY);
      if (!point) return;
      const deltaX = point.x - moveState.startPoint.x;
      const deltaY = point.y - moveState.startPoint.y;
      const distance = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));
      if (!moveState.moved && distance < 0.04) return;

      moveState.moved = true;
      const movedPoints = moveState.initialPoints.map((originalPoint) => ({
        x: Number((originalPoint.x + deltaX).toFixed(3)),
        y: Number((originalPoint.y + deltaY).toFixed(3))
      }));
      moveState.lastPoints = movedPoints;
      setDrawings((previous) => previous.map((drawing) => (
        normalizeEntityId(drawing?._id) === moveState.drawingId
          ? { ...drawing, points: movedPoints }
          : drawing
      )));
      return;
    }

    if (drawingInteractionMode !== 'draw' || !drawingDraft) return;
    const point = getGridPointFromClient(clientX, clientY);
    if (!point) return;

    if (drawingDraft.kind === 'line') {
      const startPoint = drawingDraft.points[0];
      setDrawingDraft((prev) => (
        prev
          ? { ...prev, points: [startPoint, point] }
          : prev
      ));
      return;
    }

    if (drawingDraft.kind === 'text') {
      const startPoint = drawingDraft.points[0];
      setDrawingDraft((prev) => (
        prev
          ? { ...prev, points: [startPoint, point] }
          : prev
      ));
      return;
    }

    if (drawingDraft.kind === 'free') {
      setDrawingDraft((prev) => {
        if (!prev) return prev;
        const points = Array.isArray(prev.points) ? prev.points : [];
        const lastPoint = points[points.length - 1];
        if (!lastPoint) return { ...prev, points: [point] };
        const deltaX = point.x - lastPoint.x;
        const deltaY = point.y - lastPoint.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (distance < 0.18) return prev;
        return { ...prev, points: [...points, point] };
      });
    }
  }, [drawingDraft, drawingInteractionMode, getGridPointFromClient, isDrawingMode]);

  const handleDrawingPointerEnd = useCallback((clientX, clientY) => {
    if (!isDrawingMode) return;

    if (drawingInteractionMode === 'select') {
      const moveState = drawingMoveStateRef.current;
      if (!moveState?.drawingId) return;
      drawingMoveStateRef.current = null;
      if (!moveState.moved) return;
      const pointsToSave = Array.isArray(moveState.lastPoints) ? moveState.lastPoints : moveState.initialPoints;
      updateDrawingWithHistory(moveState.initialSnapshot, {
        _id: moveState.drawingId,
        kind: moveState.initialDrawing?.kind || 'line',
        points: pointsToSave || [],
        text: moveState.initialDrawing?.text || '',
        scale: moveState.initialDrawing?.scale || 1,
        style: moveState.initialDrawing?.style || { color: '#38bdf8', width: 2 }
      }, 'Move Drawing');
      return;
    }

    if (drawingInteractionMode !== 'draw' || !drawingDraft) return;
    const point = getGridPointFromClient(clientX, clientY);
    if (!point) {
      clearDrawingState();
      return;
    }

    if (drawingDraft.kind === 'line') {
      const startPoint = drawingStartRef.current || drawingDraft.points[0];
      const finalPoints = [startPoint, point];
      const deltaX = finalPoints[1].x - finalPoints[0].x;
      const deltaY = finalPoints[1].y - finalPoints[0].y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance >= 0.25) {
        createDrawingWithHistory({
          kind: 'line',
          points: finalPoints,
          style: drawingDraft.style
        }, 'Draw Line');
      }
      clearDrawingState();
      return;
    }

    if (drawingDraft.kind === 'free') {
      const existingPoints = Array.isArray(drawingDraft.points) ? drawingDraft.points : [];
      const lastPoint = existingPoints[existingPoints.length - 1];
      const needsLastPoint = !lastPoint
        || Math.sqrt(((point.x - lastPoint.x) ** 2) + ((point.y - lastPoint.y) ** 2)) >= 0.18;
      const finalPoints = needsLastPoint ? [...existingPoints, point] : existingPoints;
      if (finalPoints.length >= 2) {
        createDrawingWithHistory({
          kind: 'free',
          points: finalPoints,
          style: drawingDraft.style
        }, 'Draw Free Line');
      }
      clearDrawingState();
      return;
    }

    if (drawingDraft.kind === 'text') {
      const startPoint = drawingStartRef.current || drawingDraft.points[0];
      const endPoint = point;
      const normalizedText = clampDrawingText(drawingDraft.text || '');
      if (String(normalizedText).trim()) {
        createDrawingWithHistory({
          kind: 'text',
          points: [startPoint, endPoint],
          text: normalizedText,
          scale: 1,
          style: {
            ...(drawingDraft.style || {}),
            color: String(drawingDraft?.style?.color || drawingDraft?.style?.textColor || DEFAULT_DRAWING_TEXT_COLOR),
            textColor: String(drawingDraft?.style?.textColor || drawingDraft?.style?.color || DEFAULT_DRAWING_TEXT_COLOR),
            backgroundColor: String(drawingDraft?.style?.backgroundColor || DEFAULT_DRAWING_TEXT_BACKGROUND)
          }
        }, 'Draw Text');
      }
      clearDrawingState();
    }
  }, [clearDrawingState, createDrawingWithHistory, drawingDraft, drawingInteractionMode, getGridPointFromClient, isDrawingMode, updateDrawingWithHistory]);

  const cloneTileMetadata = useCallback((additionalTileInfo) => {
    try {
      return additionalTileInfo
        ? JSON.parse(JSON.stringify(additionalTileInfo))
        : {};
    } catch (error) {
      return {};
    }
  }, []);

  const buildParentTemplateFromTile = useCallback((tile) => {
    if (!tile) return null;
    const parentX = tile.parentTile?.x ?? tile.x;
    const parentY = tile.parentTile?.y ?? tile.y;
    const baseType = getBaseTileType(tile.type);
    const size = tileInformation[baseType]?.size;
    if (!size) return null;

    return {
      x: parentX,
      y: parentY,
      type: baseType,
      size,
      additionalTileInfo: cloneTileMetadata(tile.additionalTileInfo)
    };
  }, [cloneTileMetadata]);

  const normalizeParentTemplates = useCallback((templates = []) => {
    const uniqueTemplates = new Map();
    templates.forEach((template) => {
      if (!template) return;
      const key = `${template.x},${template.y}`;
      if (uniqueTemplates.has(key)) return;
      const baseType = getBaseTileType(template.type);
      const size = tileInformation[baseType]?.size || template.size;
      if (!size) return;

      uniqueTemplates.set(key, {
        x: template.x,
        y: template.y,
        type: baseType,
        size,
        additionalTileInfo: cloneTileMetadata(template.additionalTileInfo)
      });
    });

    return Array.from(uniqueTemplates.values());
  }, [cloneTileMetadata]);

  const collectParentTemplatesFromTiles = useCallback((sourceTiles = []) => {
    const templates = [];
    const seenParentKeys = new Set();

    sourceTiles.forEach((tile) => {
      const parentX = tile.parentTile?.x ?? tile.x;
      const parentY = tile.parentTile?.y ?? tile.y;
      const parentKey = `${parentX},${parentY}`;
      if (seenParentKeys.has(parentKey)) return;
      seenParentKeys.add(parentKey);

      const parentTile = parentTilesByKey.get(parentKey) || {
        ...tile,
        x: parentX,
        y: parentY,
        type: getBaseTileType(tile.type)
      };
      templates.push(buildParentTemplateFromTile(parentTile));
    });

    return normalizeParentTemplates(templates);
  }, [buildParentTemplateFromTile, normalizeParentTemplates, parentTilesByKey]);

  const buildTilesToAddFromParentTemplates = useCallback((templates = []) => {
    const tilesToAdd = [];
    templates.forEach((template) => {
      const { x, y, type, size, additionalTileInfo } = template;
      tilesToAdd.push({
        x,
        y,
        type,
        parentTile: { x, y },
        additionalTileInfo: cloneTileMetadata(additionalTileInfo)
      });

      for (let i = 0; i < size; i += 1) {
        for (let j = 0; j < size; j += 1) {
          if (i > 0 || j > 0) {
            tilesToAdd.push({
              x: x + i,
              y: y + j,
              type: `${type}-child`,
              parentTile: { x, y },
              additionalTileInfo: cloneTileMetadata(additionalTileInfo)
            });
          }
        }
      }
    });
    return tilesToAdd;
  }, [cloneTileMetadata]);

  const getTileIdsForParentTemplates = useCallback((templates = []) => {
    const tileIds = new Set();
    const missingParentKeys = [];
    const parentKeys = templates.map((template) => `${template.x},${template.y}`);

    parentKeys.forEach((parentKey) => {
      const [parentX, parentY] = parentKey.split(',').map(Number);
      const matchingIds = tilesRef.current
        .filter((tile) => (tile.parentTile?.x ?? tile.x) === parentX && (tile.parentTile?.y ?? tile.y) === parentY)
        .map((tile) => String(tile._id));

      if (!matchingIds.length) {
        missingParentKeys.push(parentKey);
        return;
      }
      matchingIds.forEach((id) => tileIds.add(id));
    });

    return {
      tileIds: Array.from(tileIds),
      missingParentKeys
    };
  }, []);

  const getClipboardOrigin = useCallback((templates = []) => {
    const normalized = normalizeParentTemplates(templates);
    if (!normalized.length) {
      return null;
    }
    return normalized.reduce((origin, template) => ({
      x: Math.min(origin.x, Number(template.x)),
      y: Math.min(origin.y, Number(template.y))
    }), {
      x: Number(normalized[0].x),
      y: Number(normalized[0].y)
    });
  }, [normalizeParentTemplates]);

  const translateClipboardTemplates = useCallback((templates = [], destinationX, destinationY) => {
    const normalized = normalizeParentTemplates(templates);
    const origin = getClipboardOrigin(normalized);
    if (!origin) {
      return [];
    }
    const targetX = Number(destinationX);
    const targetY = Number(destinationY);
    if (!Number.isFinite(targetX) || !Number.isFinite(targetY)) {
      return [];
    }
    return normalizeParentTemplates(normalized.map((template) => ({
      ...template,
      x: targetX + (Number(template.x) - origin.x),
      y: targetY + (Number(template.y) - origin.y)
    })));
  }, [getClipboardOrigin, normalizeParentTemplates]);

  const collectSelectedParentTemplates = useCallback(() => (
    normalizeParentTemplates(
      selectedParentTiles
        .map((tile) => buildParentTemplateFromTile(tile))
    )
  ), [buildParentTemplateFromTile, normalizeParentTemplates, selectedParentTiles]);

  const prepareCommandDispatches = useCallback((command) => {
    if (!selectedNation?._id || !selectedPlan?._id) {
      return { ok: false, message: 'Please select a nation and plan first.' };
    }

    const commandNationId = String(command?.nationId || '');
    const commandPlanId = String(command?.planId || '');
    if (
      commandNationId !== String(selectedNation._id) ||
      commandPlanId !== String(selectedPlan._id)
    ) {
      return { ok: false, message: 'Undo/redo is only available on the same nation and plan.' };
    }

    const steps = command?.steps || [];
    const pendingDeleteIds = new Set();
    const dispatches = [];

    for (const step of steps) {
      if (step.type === 'drawingUpsert') {
        const drawingSnapshot = snapshotDrawingForHistory(step.drawing);
        if (!drawingSnapshot) {
          return { ok: false, message: 'Invalid drawing payload in undo/redo.' };
        }
        dispatches.push({
          action: 'upsertDrawing',
          tileData: {
            nation: selectedNation._id,
            plan: selectedPlan._id,
            drawing: drawingSnapshot
          }
        });
        continue;
      }

      if (step.type === 'drawingDelete') {
        const drawingId = normalizeEntityId(step.drawingId);
        if (!drawingId) {
          return { ok: false, message: 'Missing drawing id in undo/redo.' };
        }
        dispatches.push({
          action: 'deleteDrawing',
          tileData: {
            nation: selectedNation._id,
            plan: selectedPlan._id,
            drawingId
          }
        });
        continue;
      }

      if (step.type === 'borderUpsert') {
        const borderSnapshot = snapshotBorderForHistory(step.border);
        if (!borderSnapshot) {
          return { ok: false, message: 'Invalid border payload in undo/redo.' };
        }
        dispatches.push({
          action: 'upsertBorder',
          tileData: {
            nation: selectedNation._id,
            plan: selectedPlan._id,
            border: borderSnapshot
          }
        });
        continue;
      }

      if (step.type === 'borderDelete') {
        const borderId = normalizeEntityId(step.borderId);
        const allianceId = normalizeEntityId(step.allianceId);
        if (!borderId && !allianceId) {
          return { ok: false, message: 'Missing border id/alliance in undo/redo.' };
        }
        dispatches.push({
          action: 'deleteBorder',
          tileData: {
            nation: selectedNation._id,
            plan: selectedPlan._id,
            ...(borderId ? { borderId } : {}),
            ...(allianceId ? { allianceId } : {})
          }
        });
        continue;
      }

      const templates = normalizeParentTemplates(step.parents || []);
      if (!templates.length) {
        continue;
      }

      if (step.type === 'delete') {
        const { tileIds, missingParentKeys } = getTileIdsForParentTemplates(templates);
        if (missingParentKeys.length) {
          return {
            ok: false,
            message: `Conflict: tile(s) no longer exist at ${missingParentKeys.join(', ')}`
          };
        }

        tileIds.forEach((id) => pendingDeleteIds.add(String(id)));
        dispatches.push({
          action: 'deleteTile',
          tileData: {
            nation: selectedNation._id,
            plan: selectedPlan._id,
            tilesToDelete: tileIds
          }
        });
        continue;
      }

      if (step.type === 'place') {
        for (const template of templates) {
          if (checkConflict(template.x, template.y, template.type, pendingDeleteIds)) {
            return {
              ok: false,
              message: `Conflict: destination (${template.x}, ${template.y}) is occupied.`
            };
          }
        }

        dispatches.push({
          action: 'placeTile',
          tileData: {
            nation: selectedNation._id,
            plan: selectedPlan._id,
            tilesToAdd: buildTilesToAddFromParentTemplates(templates)
          }
        });
      }
    }

    if (!dispatches.length) {
      return { ok: false, message: 'No undo/redo actions available for this command.' };
    }

    return { ok: true, dispatches };
  }, [
    buildTilesToAddFromParentTemplates,
    checkConflict,
    getTileIdsForParentTemplates,
    normalizeParentTemplates,
    selectedNation,
    selectedPlan,
    snapshotBorderForHistory,
    snapshotDrawingForHistory
  ]);

  const emitCommandDispatches = useCallback((dispatches = []) => {
    let shouldRefreshTileWindow = false;
    const now = Date.now();
    const pendingPlacementParentKeys = pendingSocketPlacementParentKeysRef.current;
    const optimisticPlacementIndex = optimisticPlacementIndexRef.current;
    for (const [parentKey, expiresAt] of pendingPlacementParentKeys.entries()) {
      if (expiresAt <= now) {
        pendingPlacementParentKeys.delete(parentKey);
      }
    }

    const staleOptimisticTileIds = new Set();
    for (const [requestId, pending] of optimisticPlacementIndex.entries()) {
      const createdAt = Number(pending?.createdAt || 0);
      if (createdAt && (now - createdAt) > OPTIMISTIC_PLACEMENT_STALE_MS) {
        (pending.tileIds || []).forEach((tileId) => staleOptimisticTileIds.add(String(tileId)));
        optimisticPlacementIndex.delete(requestId);
      }
    }
    if (staleOptimisticTileIds.size) {
      setTiles((prevTiles) => prevTiles.filter((tile) => !staleOptimisticTileIds.has(String(tile?._id))));
    }

    dispatches.forEach((dispatch, index) => {
      let dispatchToEmit = dispatch;
      if (dispatch?.action === 'placeTile') {
        shouldRefreshTileWindow = true;
        const tilesToAdd = Array.isArray(dispatch?.tileData?.tilesToAdd)
          ? dispatch.tileData.tilesToAdd
          : [];

        const requestId = String(dispatch?.tileData?.requestId || `place-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`);
        dispatchToEmit = {
          ...dispatch,
          tileData: {
            ...dispatch.tileData,
            requestId
          }
        };

        const optimisticTiles = [];
        const optimisticTileIds = [];
        const parentKeys = new Set();
        tilesToAdd.forEach((tile) => {
          const parentKey = getParentKeyFromTile(tile);
          if (!parentKey || parentKey === 'undefined,undefined') return;
          parentKeys.add(parentKey);
          pendingPlacementParentKeys.set(parentKey, Date.now() + SOCKET_PLACEMENT_PARENT_KEY_TTL_MS);
        });

        tilesToAdd.forEach((tile, tileIndex) => {
          const optimisticTileId = `optimistic-${requestId}-${tileIndex}-${Math.random().toString(36).slice(2, 7)}`;
          optimisticTileIds.push(optimisticTileId);
          optimisticTiles.push({
            ...tile,
            _id: optimisticTileId,
            nation: dispatch?.tileData?.nation,
            plan: dispatch?.tileData?.plan,
            optimisticPlacement: true,
            optimisticRequestId: requestId
          });
        });

        if (optimisticTiles.length) {
          setTiles((prevTiles) => {
            const byId = new Map(prevTiles.map((tile) => [String(tile._id), tile]));
            optimisticTiles.forEach((tile) => {
              byId.set(String(tile._id), tile);
            });
            return Array.from(byId.values());
          });
        }

        optimisticPlacementIndex.set(requestId, {
          tileIds: optimisticTileIds,
          parentKeys: Array.from(parentKeys),
          createdAt: Date.now()
        });
      } else if (dispatch?.action === 'deleteTile' || dispatch?.action === 'deleteAllianceTerritory') {
        shouldRefreshTileWindow = true;
      }

      setTimeout(() => {
        emitTile(dispatchToEmit);
      }, index * 120);
    });

    if (shouldRefreshTileWindow) {
      schedulePostMutationTileRefresh();
    }
  }, [emitTile, schedulePostMutationTileRefresh]);

  const executeCommandWithGuards = useCallback((command) => {
    const prepared = prepareCommandDispatches(command);
    if (!prepared.ok) {
      showError(prepared.message);
      return false;
    }

    emitCommandDispatches(prepared.dispatches);
    return true;
  }, [emitCommandDispatches, prepareCommandDispatches, showError]);

  const createHistoryEntry = useCallback((label, forwardSteps, inverseSteps) => {
    if (!selectedNation?._id || !selectedPlan?._id) {
      return null;
    }
    historySequenceRef.current += 1;

    return {
      id: `history-${historySequenceRef.current}`,
      label,
      nationId: String(selectedNation._id),
      planId: String(selectedPlan._id),
      forward: { steps: forwardSteps || [] },
      inverse: { steps: inverseSteps || [] }
    };
  }, [selectedNation, selectedPlan]);

  const pushHistoryEntry = useCallback((entry) => {
    if (!entry) return;
    setUndoStack((prev) => {
      const next = [...prev, entry];
      if (next.length > HISTORY_MAX_ENTRIES) {
        return next.slice(next.length - HISTORY_MAX_ENTRIES);
      }
      return next;
    });
    setRedoStack([]);
  }, []);

  useEffect(() => {
    createHistoryEntryRef.current = createHistoryEntry;
  }, [createHistoryEntry]);

  useEffect(() => {
    pushHistoryEntryRef.current = pushHistoryEntry;
  }, [pushHistoryEntry]);

  const handleUndo = useCallback(() => {
    setUndoStack((prev) => {
      if (!prev.length) {
        showError('Nothing to undo.');
        return prev;
      }

      const next = [...prev];
      const entry = next.pop();
      const command = {
        ...entry.inverse,
        nationId: entry.nationId,
        planId: entry.planId
      };
      const success = executeCommandWithGuards(command);
      if (!success) {
        return prev;
      }

      setRedoStack((redoPrev) => [...redoPrev, entry]);
      return next;
    });
  }, [executeCommandWithGuards, showError]);

  const handleRedo = useCallback(() => {
    setRedoStack((prev) => {
      if (!prev.length) {
        showError('Nothing to redo.');
        return prev;
      }

      const next = [...prev];
      const entry = next.pop();
      const command = {
        ...entry.forward,
        nationId: entry.nationId,
        planId: entry.planId
      };
      const success = executeCommandWithGuards(command);
      if (!success) {
        return prev;
      }

      setUndoStack((undoPrev) => {
        const appended = [...undoPrev, entry];
        if (appended.length > HISTORY_MAX_ENTRIES) {
          return appended.slice(appended.length - HISTORY_MAX_ENTRIES);
        }
        return appended;
      });
      return next;
    });
  }, [executeCommandWithGuards, showError]);

  const toggleMultiSelectFilter = (tileType) => {
    setMultiSelectFilters((prev) => ({
      ...prev,
      [tileType]: !prev[tileType],
    }));
  };

  const handleSingleTileMultiSelect = (gridX, gridY) => {
    const clickedTile = tiles.find((tile) => tile.x === gridX && tile.y === gridY);
    if (!clickedTile) {
      return;
    }
    const parentX = clickedTile.parentTile?.x ?? clickedTile.x;
    const parentY = clickedTile.parentTile?.y ?? clickedTile.y;
    const parentKey = `${parentX},${parentY}`;
    const parentTile = parentTilesByKey.get(parentKey);
    const parentType = getBaseTileType(parentTile?.type || clickedTile.type);
    const allowAddByFilter = isTileTypeFilterEnabled(parentType);

    // Functional toggle avoids stale-closure edge cases when clicking repeatedly.
    setSelectedParentTileKeys((prev) => {
      if (prev.includes(parentKey)) {
        return prev.filter((key) => key !== parentKey);
      }
      if (!allowAddByFilter) {
        return prev;
      }
      return Array.from(new Set([...prev, parentKey]));
    });
  };

  const toggleSelectionForParentKeys = useCallback((keysToToggle = []) => {
    const normalizedKeys = Array.from(new Set((keysToToggle || []).filter(Boolean)));
    if (!normalizedKeys.length) {
      return;
    }
    setSelectedParentTileKeys((prev) => {
      const selectionSet = new Set(prev);
      normalizedKeys.forEach((key) => {
        if (selectionSet.has(key)) {
          selectionSet.delete(key);
          return;
        }
        selectionSet.add(key);
      });
      return Array.from(selectionSet);
    });
  }, []);

  const getFilteredParentEntries = useCallback(() => {
    const entries = [];
    parentTilesByKey.forEach((tile, key) => {
      const parentType = getBaseTileType(tile?.type);
      if (!isTileTypeFilterEnabled(parentType)) {
        return;
      }
      entries.push({ key, tile });
    });
    return entries;
  }, [parentTilesByKey, multiSelectFilters]);

  const getSquareSelectionBounds = useCallback((startPoint, endPoint) => {
    if (!startPoint || !endPoint) {
      return null;
    }
    const startX = Math.round(Number(startPoint.x));
    const startY = Math.round(Number(startPoint.y));
    const endX = Math.round(Number(endPoint.x));
    const endY = Math.round(Number(endPoint.y));
    if (![startX, startY, endX, endY].every(Number.isFinite)) {
      return null;
    }
    return {
      minX: Math.min(startX, endX),
      maxX: Math.max(startX, endX),
      minY: Math.min(startY, endY),
      maxY: Math.max(startY, endY),
    };
  }, []);

  const pointOnSegment = useCallback((point, startPoint, endPoint) => {
    if (!point || !startPoint || !endPoint) return false;
    const epsilon = 1e-6;
    const crossProduct = ((point.y - startPoint.y) * (endPoint.x - startPoint.x))
      - ((point.x - startPoint.x) * (endPoint.y - startPoint.y));
    if (Math.abs(crossProduct) > epsilon) {
      return false;
    }
    const dotProduct = ((point.x - startPoint.x) * (endPoint.x - startPoint.x))
      + ((point.y - startPoint.y) * (endPoint.y - startPoint.y));
    if (dotProduct < -epsilon) {
      return false;
    }
    const segmentLengthSquared = ((endPoint.x - startPoint.x) ** 2) + ((endPoint.y - startPoint.y) ** 2);
    if (dotProduct - segmentLengthSquared > epsilon) {
      return false;
    }
    return true;
  }, []);

  const isPointInPolygonInclusive = useCallback((point, polygon = []) => {
    if (!point || polygon.length < 3) {
      return false;
    }
    for (let index = 0; index < polygon.length; index += 1) {
      const nextIndex = (index + 1) % polygon.length;
      if (pointOnSegment(point, polygon[index], polygon[nextIndex])) {
        return true;
      }
    }
    let isInside = false;
    for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
      const currentPoint = polygon[index];
      const previousPoint = polygon[previousIndex];
      const currentAbove = currentPoint.y > point.y;
      const previousAbove = previousPoint.y > point.y;
      if (currentAbove === previousAbove) {
        continue;
      }
      const denominator = previousPoint.y - currentPoint.y;
      const safeDenominator = Math.abs(denominator) < 1e-9 ? 1e-9 : denominator;
      const intersectionX = (((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) / safeDenominator) + currentPoint.x;
      if (point.x <= intersectionX) {
        isInside = !isInside;
      }
    }
    return isInside;
  }, [pointOnSegment]);

  const getPolygonArea = useCallback((polygon = []) => {
    if (polygon.length < 3) return 0;
    let areaAccumulator = 0;
    for (let index = 0; index < polygon.length; index += 1) {
      const nextIndex = (index + 1) % polygon.length;
      areaAccumulator += (polygon[index].x * polygon[nextIndex].y) - (polygon[nextIndex].x * polygon[index].y);
    }
    return Math.abs(areaAccumulator) / 2;
  }, []);

  const segmentsIntersectInclusive = useCallback((firstStart, firstEnd, secondStart, secondEnd) => {
    const orientation = (p, q, r) => {
      const value = ((q.y - p.y) * (r.x - q.x)) - ((q.x - p.x) * (r.y - q.y));
      if (Math.abs(value) < 1e-9) return 0;
      return value > 0 ? 1 : 2;
    };

    const o1 = orientation(firstStart, firstEnd, secondStart);
    const o2 = orientation(firstStart, firstEnd, secondEnd);
    const o3 = orientation(secondStart, secondEnd, firstStart);
    const o4 = orientation(secondStart, secondEnd, firstEnd);

    if (o1 !== o2 && o3 !== o4) return true;
    if (o1 === 0 && pointOnSegment(secondStart, firstStart, firstEnd)) return true;
    if (o2 === 0 && pointOnSegment(secondEnd, firstStart, firstEnd)) return true;
    if (o3 === 0 && pointOnSegment(firstStart, secondStart, secondEnd)) return true;
    if (o4 === 0 && pointOnSegment(firstEnd, secondStart, secondEnd)) return true;
    return false;
  }, [pointOnSegment]);

  const hasSelfIntersection = useCallback((polygon = []) => {
    if (polygon.length < 4) return false;
    for (let first = 0; first < polygon.length; first += 1) {
      const firstNext = (first + 1) % polygon.length;
      const firstStart = polygon[first];
      const firstEnd = polygon[firstNext];
      for (let second = first + 1; second < polygon.length; second += 1) {
        const secondNext = (second + 1) % polygon.length;
        const secondStart = polygon[second];
        const secondEnd = polygon[secondNext];
        const shareVertex = (
          first === second
          || first === secondNext
          || firstNext === second
          || firstNext === secondNext
        );
        if (shareVertex) continue;
        if (segmentsIntersectInclusive(firstStart, firstEnd, secondStart, secondEnd)) {
          return true;
        }
      }
    }
    return false;
  }, [segmentsIntersectInclusive]);

  const applySquareSelectionToggle = useCallback((startPoint, endPoint) => {
    const bounds = getSquareSelectionBounds(startPoint, endPoint);
    if (!bounds) return;
    const keysToToggle = [];
    getFilteredParentEntries().forEach(({ key, tile }) => {
      const anchorX = Number(tile?.x);
      const anchorY = Number(tile?.y);
      if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) {
        return;
      }
      if (anchorX >= bounds.minX && anchorX <= bounds.maxX && anchorY >= bounds.minY && anchorY <= bounds.maxY) {
        keysToToggle.push(key);
      }
    });
    toggleSelectionForParentKeys(keysToToggle);
  }, [getFilteredParentEntries, getSquareSelectionBounds, toggleSelectionForParentKeys]);

  const applyLassoSelectionToggle = useCallback((polygon = []) => {
    if (polygon.length < 3 || getPolygonArea(polygon) < 0.5) {
      return;
    }
    const keysToToggle = [];
    getFilteredParentEntries().forEach(({ key, tile }) => {
      const anchorX = Number(tile?.x);
      const anchorY = Number(tile?.y);
      if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) {
        return;
      }
      if (isPointInPolygonInclusive({ x: anchorX, y: anchorY }, polygon)) {
        keysToToggle.push(key);
      }
    });
    toggleSelectionForParentKeys(keysToToggle);
  }, [getFilteredParentEntries, getPolygonArea, isPointInPolygonInclusive, toggleSelectionForParentKeys]);

  const getSnappedGridPointFromClient = useCallback((clientX, clientY) => {
    const cell = getGridCellFromClient(clientX, clientY);
    if (!cell) return null;
    return {
      x: Math.round(Number(cell.gridX)),
      y: Math.round(Number(cell.gridY))
    };
  }, [getGridCellFromClient]);

  const normalizeBorderPoints = useCallback((points = []) => (
    (Array.isArray(points) ? points : [])
      .map((point) => ({
        x: Math.round(Number(point?.x)),
        y: Math.round(Number(point?.y))
      }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
      .filter((point, index, source) => {
        if (!index) return true;
        const previous = source[index - 1];
        return previous.x !== point.x || previous.y !== point.y;
      })
  ), []);

  const validateBorderPolygon = useCallback((points = []) => {
    const normalized = normalizeBorderPoints(points);
    if (normalized.length > 1) {
      const firstPoint = normalized[0];
      const lastPoint = normalized[normalized.length - 1];
      if (firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y) {
        normalized.pop();
      }
    }
    if (normalized.length < 3) {
      return { ok: false, message: 'Border requires at least 3 points.', points: normalized };
    }
    if (hasSelfIntersection(normalized)) {
      return { ok: false, message: 'Border cannot self-intersect.', points: normalized };
    }
    if (getPolygonArea(normalized) < 0.5) {
      return { ok: false, message: 'Border area is too small.', points: normalized };
    }
    return { ok: true, points: normalized };
  }, [getPolygonArea, hasSelfIntersection, normalizeBorderPoints]);

  const isNearFirstBorderPoint = useCallback((points = [], candidatePoint = null) => {
    if (!points.length || !candidatePoint) return false;
    const firstPoint = points[0];
    const distance = Math.hypot(Number(candidatePoint.x) - Number(firstPoint.x), Number(candidatePoint.y) - Number(firstPoint.y));
    return distance <= BORDER_CLOSE_DISTANCE;
  }, []);

  const startBorderDrawing = useCallback(() => {
    if (!selectedBorderAllianceId) {
      showError('Select an alliance before drawing a border.');
      return;
    }
    setIsDrawingMode(false);
    clearDrawingState();
    setIsCopyMode(false);
    setIsCutMode(false);
    clearCopyModeState();
    clearCutModeState();
    setIsMultiSelectLocked(false);
    clearMultiSelectionState();
    clearQuickAddState();
    setIsOptimizePanelOpen(false);
    setBorderInteractionMode('drawing');
    setBorderDraftPoints([]);
    setBorderHoverGridPoint(null);
    setBorderDragVertexIndex(-1);
    setBorderPreviewMetrics(null);
  }, [
    clearCopyModeState,
    clearCutModeState,
    clearDrawingState,
    clearMultiSelectionState,
    clearQuickAddState,
    selectedBorderAllianceId,
    showError
  ]);

  const startBorderEditing = useCallback((allianceIdOverride = '') => {
    const targetAllianceId = String(allianceIdOverride || selectedBorderAllianceId || '');
    if (!targetAllianceId) {
      showError('Select an alliance with a border first.');
      return;
    }
    const existingBorder = bordersByAllianceId.get(targetAllianceId);
    if (!existingBorder) {
      showError('No border exists for this alliance.');
      return;
    }

    const snapshot = snapshotBorderForHistory(existingBorder);
    if (!snapshot) {
      showError('Unable to edit this border.');
      return;
    }

    setSelectedBorderAllianceId(targetAllianceId);
    setIsDrawingMode(false);
    clearDrawingState();
    setIsCopyMode(false);
    setIsCutMode(false);
    clearCopyModeState();
    clearCutModeState();
    setIsMultiSelectLocked(false);
    clearMultiSelectionState();
    clearQuickAddState();
    setIsOptimizePanelOpen(false);
    setBorderInteractionMode('editing');
    setBorderDraftPoints(snapshot.points);
    setBorderHoverGridPoint(null);
    setBorderDragVertexIndex(-1);
    setBorderPreviewMetrics(existingBorder?.metrics || null);
  }, [
    bordersByAllianceId,
    clearCopyModeState,
    clearCutModeState,
    clearDrawingState,
    clearMultiSelectionState,
    clearQuickAddState,
    selectedBorderAllianceId,
    showError,
    snapshotBorderForHistory
  ]);

  const cancelBorderInteraction = useCallback(() => {
    setBorderInteractionMode('idle');
    setBorderDraftPoints([]);
    setBorderHoverGridPoint(null);
    setBorderDragVertexIndex(-1);
    setBorderPreviewMetrics(null);
    setIsBorderPreviewLoading(false);
  }, []);

  const saveBorderDraft = useCallback((pointsOverride = null) => {
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before saving borders.');
      return;
    }
    if (!selectedBorderAllianceId) {
      showError('Please select an alliance before saving borders.');
      return;
    }

    const candidatePoints = Array.isArray(pointsOverride) && pointsOverride.length
      ? pointsOverride
      : activeBorderPoints;
    const validation = validateBorderPolygon(candidatePoints);
    if (!validation.ok) {
      showError(validation.message);
      return;
    }

    const existingBorder = bordersByAllianceId.get(String(selectedBorderAllianceId || ''));
    const nextBorderSnapshot = {
      alliance: selectedBorderAllianceId,
      points: validation.points
    };
    const previousBorderSnapshot = existingBorder
      ? snapshotBorderForHistory(existingBorder)
      : null;

    if (previousBorderSnapshot && (
      JSON.stringify(previousBorderSnapshot.points) === JSON.stringify(nextBorderSnapshot.points)
    )) {
      cancelBorderInteraction();
      return;
    }

    const forwardSteps = [{ type: 'borderUpsert', border: nextBorderSnapshot }];
    const inverseSteps = previousBorderSnapshot
      ? [{ type: 'borderUpsert', border: previousBorderSnapshot }]
      : [{ type: 'borderDelete', allianceId: selectedBorderAllianceId }];

    const command = {
      nationId: String(selectedNation._id),
      planId: String(selectedPlan._id),
      steps: forwardSteps
    };
    const executed = executeCommandWithGuards(command);
    if (!executed) return;

    const label = previousBorderSnapshot ? 'Update alliance border' : 'Create alliance border';
    pushHistoryEntry(createHistoryEntry(label, forwardSteps, inverseSteps));
    cancelBorderInteraction();
  }, [
    activeBorderPoints,
    bordersByAllianceId,
    cancelBorderInteraction,
    createHistoryEntry,
    executeCommandWithGuards,
    pushHistoryEntry,
    selectedBorderAllianceId,
    selectedNation,
    selectedPlan,
    showError,
    snapshotBorderForHistory,
    validateBorderPolygon
  ]);

  const handleDeleteBorder = useCallback((allianceIdOverride = '') => {
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before deleting borders.');
      return;
    }

    const targetAllianceId = String(allianceIdOverride || selectedBorderAllianceId || '');
    if (!targetAllianceId) {
      showError('Select an alliance border to delete.');
      return;
    }

    const existingBorder = bordersByAllianceId.get(targetAllianceId);
    if (!existingBorder) {
      showError('No border exists for this alliance.');
      return;
    }

    const previousSnapshot = snapshotBorderForHistory(existingBorder);
    if (!previousSnapshot) {
      showError('Unable to delete this border.');
      return;
    }

    const confirmed = window.confirm('Delete this alliance border?');
    if (!confirmed) return;

    const forwardSteps = [{ type: 'borderDelete', allianceId: targetAllianceId, borderId: normalizeEntityId(existingBorder?._id) }];
    const inverseSteps = [{ type: 'borderUpsert', border: previousSnapshot }];
    const command = {
      nationId: String(selectedNation._id),
      planId: String(selectedPlan._id),
      steps: forwardSteps
    };
    const executed = executeCommandWithGuards(command);
    if (!executed) return;

    pushHistoryEntry(createHistoryEntry('Delete alliance border', forwardSteps, inverseSteps));
    if (String(selectedBorderAllianceId || '') === targetAllianceId) {
      cancelBorderInteraction();
    }
  }, [
    bordersByAllianceId,
    cancelBorderInteraction,
    createHistoryEntry,
    executeCommandWithGuards,
    pushHistoryEntry,
    selectedBorderAllianceId,
    selectedNation,
    selectedPlan,
    showError,
    snapshotBorderForHistory
  ]);

  useEffect(() => {
    if (!isBorderInteractionActive) {
      setBorderPreviewMetrics(null);
      setIsBorderPreviewLoading(false);
      if (borderPreviewDebounceRef.current) {
        clearTimeout(borderPreviewDebounceRef.current);
        borderPreviewDebounceRef.current = null;
      }
      return;
    }

    const validation = validateBorderPolygon(activeBorderPoints);
    if (!validation.ok) {
      setBorderPreviewMetrics(null);
      setIsBorderPreviewLoading(false);
      if (borderPreviewDebounceRef.current) {
        clearTimeout(borderPreviewDebounceRef.current);
        borderPreviewDebounceRef.current = null;
      }
      return;
    }

    if (!selectedNationId || !selectedPlanId) {
      setBorderPreviewMetrics(null);
      setIsBorderPreviewLoading(false);
      return;
    }

    if (borderPreviewDebounceRef.current) {
      clearTimeout(borderPreviewDebounceRef.current);
    }

    borderPreviewDebounceRef.current = setTimeout(async () => {
      const requestSeq = borderPreviewRequestSeqRef.current + 1;
      borderPreviewRequestSeqRef.current = requestSeq;
      setIsBorderPreviewLoading(true);
      try {
        const response = await axios.post(
          `${apiUrl}/nations/${selectedNationId}/plans/${selectedPlanId}/borders/preview-metrics`,
          {
            allianceId: selectedBorderAllianceId || undefined,
            points: validation.points
          }
        );
        if (borderPreviewRequestSeqRef.current !== requestSeq) {
          return;
        }
        setBorderPreviewMetrics(response?.data?.metrics || null);
      } catch (error) {
        if (borderPreviewRequestSeqRef.current !== requestSeq) {
          return;
        }
        setBorderPreviewMetrics(null);
      } finally {
        if (borderPreviewRequestSeqRef.current === requestSeq) {
          setIsBorderPreviewLoading(false);
        }
      }
    }, 180);

    return () => {
      if (borderPreviewDebounceRef.current) {
        clearTimeout(borderPreviewDebounceRef.current);
        borderPreviewDebounceRef.current = null;
      }
    };
  }, [
    activeBorderPoints,
    axios,
    isBorderInteractionActive,
    selectedBorderAllianceId,
    selectedNationId,
    selectedPlanId,
    validateBorderPolygon
  ]);

  const fetchInitialMapData = async () => {
    const imageObjects = {};
    if (!currentUserRole) return

    for (const [key, value] of Object.entries(images)) {
      const img = new Image();
      img.src = value;
      imageObjects[key] = img;
    }
    setLoadedImages(imageObjects);

    const [allNationsResponse, scopeResponse] = await Promise.all([
      axios.get(`${apiUrl}/nations`),
      axios.get(`${apiUrl}/merger/planning-session`).catch(() => ({ data: { allowedNationIds: [] } }))
    ]);

    const allNations = allNationsResponse.data || [];
    const scope = scopeResponse?.data || { allowedNationIds: [] };
    setMergerPlanningScope(scope);

    const allowedNationIds = (scope.allowedNationIds || []).map((id) => String(id));
    const isGlobalRole = currentUserRole === 'superUser' || currentUserRole === 'supporter';
    const canCrossSelectFromMerge = !isGlobalRole && allowedNationIds.length > 1;

    let visibleNations;
    if (isGlobalRole) {
      visibleNations = allNations;
    } else if (canCrossSelectFromMerge) {
      visibleNations = allNations.filter((nation) => allowedNationIds.includes(String(nation._id)));
    } else {
      // Non-global roles default to their own nation when not in merge planning scope.
      visibleNations = allNations.filter((nation) => String(nation._id) === currentUserNationId);
    }

    setNations(visibleNations);

    const fallbackNation = visibleNations.find((nation) => String(nation._id) === currentUserNationId) || visibleNations[0] || null;
    let storedPreferredNationId = '';
    try {
      storedPreferredNationId = String(localStorage.getItem(MAP_SELECTED_NATION_STORAGE_KEY) || '');
    } catch (error) {
      storedPreferredNationId = '';
    }

    setSelectedNation((previous) => {
      const previousNationId = String(previous?._id || '');
      const preferredNationId = previousNationId || storedPreferredNationId;
      if (preferredNationId) {
        const stillVisible = visibleNations.find((nation) => String(nation._id) === preferredNationId);
        if (stillVisible) {
          // Preserve explicit map nation selection across auth/session refreshes/remounts.
          return stillVisible;
        }
      }
      return fallbackNation;
    });
  }

  const fetchDefaultPlans = async () => {
    try {
      if (!selectedNation) return
      const plansResponse = await axios.get(`${apiUrl}/nations/${selectedNation._id}/plans`)
      setPlans(plansResponse.data)

      plansResponse.data.forEach(plan => {
        if (plan.name === "current") {
          setSelectedPlan(plan)
        }
      });
    } catch (error) {
      showError(error)
    }
  }

  const handleOptimizeTerritory = async () => {
    if (user?.role !== 'superUser') {
      return;
    }

    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before running optimization.');
      return;
    }
    if (!optimizeAllianceId) {
      showError('Please select an alliance.');
      return;
    }
    if (!optimizeAlliances.some((alliance) => String(alliance._id) === String(optimizeAllianceId))) {
      showError('Please select an alliance with a placed alliance hall or flag on this map.');
      return;
    }

    const parsedFlagCount = Number.parseInt(optimizeFlagCount, 10);
    if (!Number.isFinite(parsedFlagCount) || parsedFlagCount < 1 || parsedFlagCount > 50) {
      showError('Flag count must be between 1 and 50.');
      return;
    }

    const parseWeight = (value, fallback, min = 0, max = 1000) => {
      const parsed = Number.parseFloat(value);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(max, Math.max(min, parsed));
    };

    const weights = {
      eliteMine: parseWeight(optimizerWeights.eliteMine, 10),
      territory: parseWeight(optimizerWeights.territory, 0.08, 0, 100),
      reach: parseWeight(optimizerWeights.reach, 0.03, 0, 100),
      benefits: {
        lab: parseWeight(optimizerWeights.benefits.lab, 6),
        militaryArea: parseWeight(optimizerWeights.benefits.militaryArea, 6),
        mechanicShop: parseWeight(optimizerWeights.benefits.mechanicShop, 6),
        warFactory: parseWeight(optimizerWeights.benefits.warFactory, 6),
      }
    };

    try {
      setIsOptimizingTerritory(true);
      setOptimizationPreviewFlags([]);
      setOptimizeStatusMessage('');
      const response = await axios.post(`${apiUrl}/merger/optimizeTerritory`, {
        nationId: selectedNation._id,
        planId: selectedPlan._id,
        allianceId: optimizeAllianceId,
        maxFlags: parsedFlagCount,
        weights,
        requesterSocketId: socketId || undefined
      });

      const jobId = response?.data?.jobId || null;
      const streaming = !!response?.data?.streaming;
      setOptimizationJobId(jobId);

      if (streaming && jobId) {
        setOptimizeStatusMessage('Optimization running...');
        resetOptimizationWatchdog();
      } else {
        setOptimizeStatusMessage('Optimization started. Refreshing map shortly...');
        setTimeout(() => { fetchUpdatedTileData({ force: true }); }, 3000);
        setTimeout(() => { fetchUpdatedTileData({ force: true }); }, 7000);
        setTimeout(() => { setIsOptimizingTerritory(false); }, 8000);
      }
    } catch (error) {
      showError(error?.response?.data?.error || error?.response?.data?.message || 'Failed to start territory optimization.');
      setOptimizeStatusMessage('');
      setIsOptimizingTerritory(false);
      setOptimizationPreviewFlags([]);
      setOptimizationJobId(null);
    }
  };

  const focusHiveCandidate = useCallback((candidate, index) => {
    if (!candidate) return;
    const centerX = Number(candidate?.origin?.x) + ((HIVE_FOOTPRINT_SIZE - 1) / 2);
    const centerY = Number(candidate?.origin?.y) + ((HIVE_FOOTPRINT_SIZE - 1) / 2);
    if (Number.isFinite(centerX) && Number.isFinite(centerY)) {
      const newOffsetX = (centerX - centerY) * (tileSize / 2) * zoom * -1;
      const newOffsetY = (centerX + centerY) * (tileSize / 4) * zoom;
      setOffset({ x: newOffsetX, y: newOffsetY });
      setHighlightedTile({ gridX: centerX, gridY: centerY });
      setTimeout(() => {
        setHighlightedTile(null);
      }, 3000);
    }
    if (Number.isInteger(index) && index >= 0) {
      setSelectedHiveCandidateIndex(index);
    }
  }, [tileSize, zoom]);

  const handleOptimizeHiveSearch = useCallback(async () => {
    if (user?.role !== 'superUser') {
      return;
    }
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before searching hive locations.');
      return;
    }

    const hasTargetX = String(hiveTargetX).trim() !== '';
    const hasTargetY = String(hiveTargetY).trim() !== '';
    if (hasTargetX !== hasTargetY) {
      showError('Provide both target X and target Y, or leave both blank.');
      return;
    }

    let target = null;
    if (hasTargetX && hasTargetY) {
      const parsedTargetX = Number(hiveTargetX);
      const parsedTargetY = Number(hiveTargetY);
      if (!Number.isFinite(parsedTargetX) || !Number.isFinite(parsedTargetY)) {
        showError('Target coordinates must be numeric.');
        return;
      }
      target = { x: parsedTargetX, y: parsedTargetY };
    }

    const parseWeight = (value, fallback) => {
      const parsed = Number.parseFloat(value);
      if (!Number.isFinite(parsed) || parsed < 0) return fallback;
      return parsed;
    };
    const requestWeights = {
      targetDistance: parseWeight(hiveWeights.targetDistance, 0.7),
      centerDistance: parseWeight(hiveWeights.centerDistance, 0.3)
    };

    try {
      setIsOptimizingHive(true);
      setIsPickingHiveTarget(false);
      setOptimizeStatusMessage('Searching hive locations...');
      const response = await axios.post(`${apiUrl}/merger/optimizeHive`, {
        nationId: selectedNation._id,
        planId: selectedPlan._id,
        target,
        resultLimit: HIVE_RESULT_LIMIT,
        weights: requestWeights
      });

      const fetchedCandidates = Array.isArray(response?.data?.candidates)
        ? response.data.candidates
        : [];
      setHiveCandidates(fetchedCandidates);
      setSelectedHiveCandidateIndex(fetchedCandidates.length ? 0 : -1);

      if (fetchedCandidates.length) {
        setOptimizeStatusMessage(`Found ${fetchedCandidates.length} hive candidates.`);
      } else {
        setOptimizeStatusMessage('No valid 20x20 hive area found in this plan.');
      }
    } catch (error) {
      setHiveCandidates([]);
      setSelectedHiveCandidateIndex(-1);
      setOptimizeStatusMessage('');
      showError(error?.response?.data?.error || error?.response?.data?.message || 'Failed to search hive locations.');
    } finally {
      setIsOptimizingHive(false);
    }
  }, [axios, hiveTargetX, hiveTargetY, hiveWeights.centerDistance, hiveWeights.targetDistance, selectedNation, selectedPlan, showError, user?.role]);

  function checkConflict(gridX, gridY, type, ignoredTileIds = null) {
    const ignoredIds = ignoredTileIds instanceof Set ? ignoredTileIds : new Set();
    const size = tileInformation[type].size
    for (let i = gridX; i < gridX + size; i++) {
      for (let j = gridY; j < gridY + size; j++) {
        if (tilesRef.current.some(tile => tile.x === i && tile.y === j && !ignoredIds.has(String(tile._id)))) {
          return true
        }
      }
    }
  }

  const updateCursorPreviewFromPointer = (event) => {
    if (!(isCopyMode || isCutMode || isQuickAddMode)) {
      setCursorPreviewGrid((prev) => (prev ? null : prev));
      return;
    }

    const activeTemplate = isQuickAddMode
      ? {
        type: selectedTileAddType,
        size: tileInformation[selectedTileAddType]?.size || 1,
        additionalTileInfo: {}
      }
      : (isCutMode ? cutTileTemplates : copiedTileTemplates);
    const hasActiveTemplate = Array.isArray(activeTemplate)
      ? activeTemplate.length > 0
      : !!activeTemplate;
    if (!hasActiveTemplate) {
      setCursorPreviewGrid((prev) => (prev ? null : prev));
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const { gridX, gridY } = screenToGrid({
      clientX: event.clientX,
      clientY: event.clientY,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    });

    setCursorPreviewGrid((prev) => {
      if (prev?.gridX === gridX && prev?.gridY === gridY) {
        return prev;
      }
      return { gridX, gridY };
    });
  };

  const handleBeginMultiSelectDrag = useCallback((clientX, clientY) => {
    if (!isMultiSelectMode) {
      return;
    }
    const startCell = getGridCellFromClient(clientX, clientY);
    if (!startCell) {
      return;
    }

    setMultiSelectDragStart({ x: startCell.gridX, y: startCell.gridY });
    setMultiSelectDragCurrent({ x: startCell.gridX, y: startCell.gridY });
    setIsMultiSelectDragging(true);

    if (multiSelectTool === 'lasso') {
      const startPoint = getGridPointFromClient(clientX, clientY);
      setMultiSelectLassoPath(startPoint ? [{ x: startPoint.x, y: startPoint.y }] : [{ x: startCell.gridX, y: startCell.gridY }]);
    } else {
      setMultiSelectLassoPath([]);
    }
  }, [getGridCellFromClient, getGridPointFromClient, isMultiSelectMode, multiSelectTool]);

  const handleUpdateMultiSelectDrag = useCallback((clientX, clientY) => {
    if (!isMultiSelectMode || !isMultiSelectDragging) {
      return;
    }

    if (multiSelectTool === 'lasso') {
      const nextPoint = getGridPointFromClient(clientX, clientY);
      if (!nextPoint) {
        return;
      }
      setMultiSelectDragCurrent({ x: nextPoint.x, y: nextPoint.y });
      setMultiSelectLassoPath((prevPath) => {
        if (!prevPath.length) {
          return [{ x: nextPoint.x, y: nextPoint.y }];
        }
        const lastPoint = prevPath[prevPath.length - 1];
        const distance = Math.hypot(nextPoint.x - lastPoint.x, nextPoint.y - lastPoint.y);
        if (distance < 0.35) {
          return prevPath;
        }
        return [...prevPath, { x: nextPoint.x, y: nextPoint.y }];
      });
      return;
    }

    const nextCell = getGridCellFromClient(clientX, clientY);
    if (!nextCell) {
      return;
    }
    setMultiSelectDragCurrent({ x: nextCell.gridX, y: nextCell.gridY });
  }, [getGridCellFromClient, getGridPointFromClient, isMultiSelectDragging, isMultiSelectMode, multiSelectTool]);

  const handleFinalizeMultiSelectDrag = useCallback((clientX, clientY) => {
    if (!isMultiSelectMode || !isMultiSelectDragging || !multiSelectDragStart) {
      clearMultiSelectDragState();
      return;
    }

    if (multiSelectTool === 'lasso') {
      const endPoint = getGridPointFromClient(clientX, clientY)
        || multiSelectDragCurrent
        || multiSelectDragStart;
      const candidatePath = Array.isArray(multiSelectLassoPath) ? multiSelectLassoPath : [];
      const finalizedPath = [...candidatePath];
      if (endPoint) {
        const lastPoint = finalizedPath[finalizedPath.length - 1];
        if (!lastPoint || Math.hypot(endPoint.x - lastPoint.x, endPoint.y - lastPoint.y) >= 0.2) {
          finalizedPath.push({ x: endPoint.x, y: endPoint.y });
        }
      }

      const hasShapeArea = finalizedPath.length >= 3 && getPolygonArea(finalizedPath) >= 0.5;
      if (hasShapeArea) {
        applyLassoSelectionToggle(finalizedPath);
      } else {
        const finalCell = getGridCellFromClient(clientX, clientY)
          || {
            gridX: Math.round(Number(multiSelectDragStart.x)),
            gridY: Math.round(Number(multiSelectDragStart.y)),
          };
        if (finalCell) {
          handleSingleTileMultiSelect(finalCell.gridX, finalCell.gridY);
        }
      }
      clearMultiSelectDragState();
      return;
    }

    const endCell = getGridCellFromClient(clientX, clientY)
      || {
        gridX: Math.round(Number(multiSelectDragCurrent?.x ?? multiSelectDragStart.x)),
        gridY: Math.round(Number(multiSelectDragCurrent?.y ?? multiSelectDragStart.y)),
      };

    const startX = Math.round(Number(multiSelectDragStart.x));
    const startY = Math.round(Number(multiSelectDragStart.y));
    const endX = Math.round(Number(endCell?.gridX));
    const endY = Math.round(Number(endCell?.gridY));
    const isClickSelection = [startX, startY, endX, endY].every(Number.isFinite)
      && startX === endX
      && startY === endY;

    if (isClickSelection) {
      handleSingleTileMultiSelect(startX, startY);
      clearMultiSelectDragState();
      return;
    }

    applySquareSelectionToggle(
      { x: startX, y: startY },
      { x: endX, y: endY }
    );
    clearMultiSelectDragState();
  }, [applyLassoSelectionToggle, applySquareSelectionToggle, clearMultiSelectDragState, getGridCellFromClient, getGridPointFromClient, getPolygonArea, handleSingleTileMultiSelect, isMultiSelectDragging, isMultiSelectMode, multiSelectDragCurrent, multiSelectDragStart, multiSelectLassoPath, multiSelectTool]);

  const applyCopyClipboardTemplates = useCallback((templates = [], sourceParentKeys = []) => {
    const normalizedTemplates = normalizeParentTemplates(templates);
    setCopiedTileTemplates(normalizedTemplates);
    setCopiedSourceParentKeys(Array.from(new Set((sourceParentKeys || []).filter(Boolean))));
  }, [normalizeParentTemplates]);

  const applyCutClipboardTemplates = useCallback((templates = [], sourceParentKeys = []) => {
    const normalizedTemplates = normalizeParentTemplates(templates);
    setCutTileTemplates(normalizedTemplates);
    setCutSourceParentKeys(Array.from(new Set((sourceParentKeys || []).filter(Boolean))));
  }, [normalizeParentTemplates]);

  const captureSelectedTemplatesForClipboard = useCallback((mode = 'copy') => {
    const selectedTemplates = collectSelectedParentTemplates();
    if (!selectedTemplates.length) {
      return false;
    }
    const sourceKeys = Array.from(new Set((selectedParentTileKeys || []).filter(Boolean)));
    if (mode === 'cut') {
      applyCutClipboardTemplates(selectedTemplates, sourceKeys);
    } else {
      applyCopyClipboardTemplates(selectedTemplates, sourceKeys);
    }
    return true;
  }, [applyCopyClipboardTemplates, applyCutClipboardTemplates, collectSelectedParentTemplates, selectedParentTileKeys]);

  const handleCanvasClick = (event) => {
    if (handleImportantCoordinateLabelClick(event.clientX, event.clientY)) {
      return;
    }

    const gridCell = getGridCellFromClient(event.clientX, event.clientY);
    if (!gridCell) {
      return;
    }
    const { gridX, gridY } = gridCell;

    const clickedTile = tiles.find(tile => tile.x === gridX && tile.y === gridY);

    if (isPickingHiveTarget) {
      setHiveTargetX(String(gridX));
      setHiveTargetY(String(gridY));
      setIsPickingHiveTarget(false);
      setOptimizeStatusMessage(`Hive target set to (${gridX}, ${gridY}).`);
      return;
    }

    const canPickHiveCandidateOnMap = (
      isOptimizePanelOpen
      && optimizeMode === 'hive'
      && hiveCandidates.length > 0
      && !isDrawingMode
      && !isCopyMode
      && !isCutMode
      && !isQuickAddMode
      && !isMultiSelectMode
      && !selectedTileAddType
    );
    if (canPickHiveCandidateOnMap) {
      const candidateIndex = hiveCandidates.findIndex((candidate) => {
        const minX = Number(candidate?.bounds?.minX);
        const maxX = Number(candidate?.bounds?.maxX);
        const minY = Number(candidate?.bounds?.minY);
        const maxY = Number(candidate?.bounds?.maxY);
        if (![minX, maxX, minY, maxY].every(Number.isFinite)) return false;
        return gridX >= minX && gridX <= maxX && gridY >= minY && gridY <= maxY;
      });
      if (candidateIndex !== -1) {
        focusHiveCandidate(hiveCandidates[candidateIndex], candidateIndex);
        return;
      }
    }

    if (isCopyMode || isCutMode) {
      const activeTemplates = isCutMode ? cutTileTemplates : copiedTileTemplates;
      const hasActiveTemplates = Array.isArray(activeTemplates) && activeTemplates.length > 0;
      const shouldCaptureSource = !!clickedTile && (
        !hasActiveTemplates
        || (isCopyMode && activeTemplates.length === 1)
      );

      if (shouldCaptureSource) {
        const parentX = clickedTile.parentTile?.x ?? clickedTile.x;
        const parentY = clickedTile.parentTile?.y ?? clickedTile.y;
        const parentKey = `${parentX},${parentY}`;
        const parentTile = parentTilesByKey.get(parentKey) || {
          ...clickedTile,
          x: parentX,
          y: parentY,
          type: getBaseTileType(clickedTile.type),
        };
        const sourceType = getBaseTileType(parentTile.type || clickedTile.type);
        const sourceSize = tileInformation[sourceType]?.size;

        if (!sourceSize) {
          showError('This tile type cannot be copied.');
          return;
        }

        let copiedMetadata = {};
        try {
          copiedMetadata = parentTile.additionalTileInfo
            ? JSON.parse(JSON.stringify(parentTile.additionalTileInfo))
            : {};
        } catch (error) {
          copiedMetadata = {};
        }

        const nextTemplate = {
          x: parentX,
          y: parentY,
          type: sourceType,
          size: sourceSize,
          additionalTileInfo: copiedMetadata,
        };

        if (isCutMode) {
          applyCutClipboardTemplates([nextTemplate], [parentKey]);
        } else {
          applyCopyClipboardTemplates([nextTemplate], [parentKey]);
        }

        setIsTileDetailsModalOpen(false);
        clearQuickAddState();
        return;
      }

      if (!hasActiveTemplates) {
        showError(`${isCutMode ? 'Cut' : 'Copy'} mode active. Click an existing tile first.`);
        return;
      }

      if (!selectedNation?._id || !selectedPlan?._id) {
        showError('Please select a nation and plan before pasting copied tiles.');
        return;
      }

      const destinationTemplates = translateClipboardTemplates(activeTemplates, gridX, gridY);
      if (!destinationTemplates.length) {
        showError('Unable to paste this tile type.');
        return;
      }

      let forwardSteps;
      let inverseSteps;
      let historyLabel;

      if (isCutMode) {
        const sourceTemplates = normalizeParentTemplates(cutTileTemplates);
        if (!sourceTemplates.length) {
          showError('Cut source is no longer available.');
          return;
        }

        forwardSteps = [
          { type: 'delete', parents: sourceTemplates },
          { type: 'place', parents: destinationTemplates }
        ];
        inverseSteps = [
          { type: 'delete', parents: destinationTemplates },
          { type: 'place', parents: sourceTemplates }
        ];
        historyLabel = sourceTemplates.length === 1
          ? `Move ${sourceTemplates[0].type}`
          : `Move ${sourceTemplates.length} map objects`;
      } else {
        forwardSteps = [{ type: 'place', parents: destinationTemplates }];
        inverseSteps = [{ type: 'delete', parents: destinationTemplates }];
        historyLabel = destinationTemplates.length === 1
          ? `Paste ${destinationTemplates[0].type}`
          : `Paste ${destinationTemplates.length} map objects`;
      }

      const command = {
        nationId: String(selectedNation._id),
        planId: String(selectedPlan._id),
        steps: forwardSteps
      };
      const executed = executeCommandWithGuards(command);
      if (!executed) {
        return;
      }

      pushHistoryEntry(createHistoryEntry(historyLabel, forwardSteps, inverseSteps));
      if (isCutMode) {
        clearCutModeState();
      }
      return;
    }

    if (selectedTileAddType) {
      if (!canWriteTiles) {
        showError('You are not authorized to place tiles.');
        return;
      }
      if (!selectedNation?._id || !selectedPlan?._id) {
        showError('Please select a nation and plan before placing tiles.');
        return;
      }
      if (clickedTile) {
        showError('Error: Tile already occupied!');
        return;
      }
      if (checkConflict(gridX, gridY, selectedTileAddType)) {
        showError('Error: Tile already occupied!');
        return;
      }

      const nextPlacement = { gridX, gridY, type: selectedTileAddType };
      setSelectedGridPosition({ gridX, gridY });

      if (selectedTileAddType === 'benefit') {
        handleAddTile(
          { benefitSubType: quickAddBenefitSubType || DEFAULT_BENEFIT_SUBTYPE },
          nextPlacement,
          { keepInHand: true }
        );
        return;
      }

      if (tileTypeRequiresAdditionalInfo(selectedTileAddType)) {
        setPendingQuickAddPlacement(nextPlacement);
        if (selectedTileAddType === 'city') {
          setIsAddCityModalOpen(true);
        } else if (selectedTileAddType === 'flag' || selectedTileAddType === 'allianceHall' || selectedTileAddType === 'fort' || selectedTileAddType === 'institute') {
          setIsAddAllianceBuildingModalOpen(true);
        } else if (selectedTileAddType === 'eliteMine') {
          setIsAddEliteMineModalOpen(true);
        } else if (selectedTileAddType === 'benefit') {
          setIsAddBenefitBuildingModalOpen(true);
        }
        return;
      }

      handleAddTile({}, nextPlacement, { keepInHand: true });
      return;
    }

    if (clickedTile) {
      const parentTile = tiles.find(tile => tile.x === clickedTile.parentTile.x && tile.y === clickedTile.parentTile.y);
      handleTileClick(parentTile);
    }
  };

  const handleQuickAddTypeToggle = useCallback((type) => {
    if (!canWriteTiles) {
      return;
    }
    setSelectedTileAddType((prev) => {
      const nextType = prev === type ? null : type;
      if (nextType) {
        setIsQuickAddPanelOpen(false);
        setIsCopyMode(false);
        setIsCutMode(false);
        setIsDrawingMode(false);
        clearDrawingState();
        clearCopyModeState();
        clearCutModeState();
        setIsMultiSelectLocked(false);
        clearMultiSelectionState();
        setIsTileDetailsModalOpen(false);
        if (nextType === 'benefit' && !BENEFIT_SUBTYPE_KEYS.includes(quickAddBenefitSubType)) {
          setQuickAddBenefitSubType(DEFAULT_BENEFIT_SUBTYPE);
        }
      }
      setPendingQuickAddPlacement(null);
      setSelectedGridPosition(null);
      closeAddMetadataModals();
      return nextType;
    });
  }, [canWriteTiles, clearCopyModeState, clearCutModeState, clearDrawingState, clearMultiSelectionState, closeAddMetadataModals, quickAddBenefitSubType]);

  const handleCancelPendingQuickAddPlacement = useCallback(() => {
    closeAddMetadataModals();
    setPendingQuickAddPlacement(null);
    setSelectedGridPosition(null);
  }, [closeAddMetadataModals]);

  const handleQuickAddPanelToggle = useCallback(() => {
    setIsQuickAddPanelOpen((prev) => {
      const next = !prev;
      if (!next) {
        clearQuickAddState();
      }
      return next;
    });
  }, [clearQuickAddState]);

  // tile type adding function
  // this function has been made generic to accomadate adding any type of tile based on the "type", and the "size"
  const handleAddTile = async (extraTileInfo, placementOverride = null, options = {}) => {
    const sourcePlacement = placementOverride || selectedGridPosition;
    const type = placementOverride?.type || selectedTileAddType;
    if (!sourcePlacement || !type) return;

    if (type === 'benefit') {
      const selectedSubType = extraTileInfo?.benefitSubType;
      if (selectedSubType && BENEFIT_SUBTYPE_KEYS.includes(selectedSubType)) {
        setQuickAddBenefitSubType(selectedSubType);
      }
    }

    const { gridX, gridY } = sourcePlacement;
    const size = tileInformation[type].size;
    const parentTemplate = normalizeParentTemplates([{
      x: gridX,
      y: gridY,
      type,
      size,
      additionalTileInfo: extraTileInfo
    }])[0];
    if (!parentTemplate) {
      showError('Unable to add this tile type.');
      return;
    }

    let forwardSteps = [{ type: 'place', parents: [parentTemplate] }];
    let inverseSteps = [{ type: 'delete', parents: [parentTemplate] }];
    let historyLabel = `Place ${type}`;

    const constraint = getPlacementConstraint(type);
    if (constraint) {
      const existingPlacement = findConstraintPlacement({
        placementIndex: placementConstraintIndex,
        tileType: type,
        additionalTileInfo: extraTileInfo
      });

      if (existingPlacement) {
        const destinationParentKey = `${parentTemplate.x},${parentTemplate.y}`;
        const isSameDestination = existingPlacement.parentKey === destinationParentKey;
        if (isSameDestination) {
          showError(`${constraint.label} is already placed at (${existingPlacement.parentX}, ${existingPlacement.parentY}).`);
          return;
        }

        if (!options.allowRelocateExisting) {
          showError(`${constraint.label} already exists at (${existingPlacement.parentX}, ${existingPlacement.parentY}).`);
          return;
        }

        const existingParentTile = parentTilesByKey.get(existingPlacement.parentKey);
        const existingTemplate = buildParentTemplateFromTile(existingParentTile);
        if (!existingTemplate) {
          showError(`Unable to move existing ${constraint.label.toLowerCase()}.`);
          return;
        }

        forwardSteps = [
          { type: 'delete', parents: [existingTemplate] },
          { type: 'place', parents: [parentTemplate] }
        ];
        inverseSteps = [
          { type: 'delete', parents: [parentTemplate] },
          { type: 'place', parents: [existingTemplate] }
        ];
        historyLabel = `Move ${type}`;
      }
    }

    const command = {
      nationId: String(selectedNation._id),
      planId: String(selectedPlan._id),
      steps: forwardSteps
    };
    const executed = executeCommandWithGuards(command);
    if (executed) {
      pushHistoryEntry(createHistoryEntry(historyLabel, forwardSteps, inverseSteps));
    }

    if (!options.keepInHand) {
      setSelectedTileAddType(null);
    }
    setSelectedGridPosition(null);
    setPendingQuickAddPlacement(null);
  };

  const handleConfirmPendingQuickAddPlacement = useCallback((extraTileInfo = {}, options = {}) => {
    if (!pendingQuickAddPlacement) return;
    handleAddTile(extraTileInfo, pendingQuickAddPlacement, { keepInHand: true, ...options });
    closeAddMetadataModals();
    setPendingQuickAddPlacement(null);
  }, [closeAddMetadataModals, handleAddTile, pendingQuickAddPlacement]);

  useEffect(() => {
    if (selectedTileAddType === 'benefit' && !BENEFIT_SUBTYPE_KEYS.includes(quickAddBenefitSubType)) {
      setQuickAddBenefitSubType(DEFAULT_BENEFIT_SUBTYPE);
    }
  }, [quickAddBenefitSubType, selectedTileAddType]);

  const benefitQuickAddTipPosition = useMemo(() => {
    if (!(isQuickAddMode && selectedTileAddType === 'benefit' && cursorPreviewGrid)) {
      return null;
    }

    const canvas = canvasRef.current;
    if (!canvas) return null;

    const { x, y } = gridToScreen({
      gridX: cursorPreviewGrid.gridX,
      gridY: cursorPreviewGrid.gridY,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });

    return {
      left: x,
      top: y + (tileSize * zoom * 0.58),
    };
  }, [cursorPreviewGrid, isQuickAddMode, offset.x, offset.y, selectedTileAddType, tileSize, zoom]);

  const handleDeleteTile = async () => {
    try {
      const parentTemplate = buildParentTemplateFromTile(selectedTile);
      if (!parentTemplate) {
        showError('Unable to delete this tile.');
        return;
      }

      const forwardSteps = [{ type: 'delete', parents: [parentTemplate] }];
      const inverseSteps = [{ type: 'place', parents: [parentTemplate] }];
      const command = {
        nationId: String(selectedNation._id),
        planId: String(selectedPlan._id),
        steps: forwardSteps
      };
      const executed = executeCommandWithGuards(command);
      if (executed) {
        pushHistoryEntry(createHistoryEntry(`Delete ${parentTemplate.type}`, forwardSteps, inverseSteps));
        setIsTileDetailsModalOpen(false);
      }
    } catch (errorMessage) {
      showError(errorMessage);
    }
  };

  const handleToggleTileConfirmed = async () => {
    if (!canWriteTiles) {
      showError('You are not authorized to confirm tile locations.');
      return;
    }
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before confirming a location.');
      return;
    }

    const tileId = normalizeEntityId(selectedTile?._id);
    const baseType = getBaseTileType(selectedTile?.type || '');
    if (!tileId || !isConfirmableLocationType(baseType)) {
      showError('This tile type cannot be confirmed.');
      return;
    }

    const nextConfirmed = !Boolean(selectedTile?.confirmed);

    try {
      await axios.patch(
        `${apiUrl}/nations/${selectedNation._id}/plans/${selectedPlan._id}/tiles/${tileId}`,
        { confirmed: nextConfirmed }
      );

      setTiles((prevTiles) => prevTiles.map((tile) => (
        String(tile?._id) === tileId
          ? { ...tile, confirmed: nextConfirmed }
          : tile
      )));
      setSelectedTile((previous) => (
        String(previous?._id) === tileId
          ? { ...previous, confirmed: nextConfirmed }
          : previous
      ));
      showInfo(nextConfirmed ? 'Location confirmed.' : 'Location unconfirmed.');
      setIsTileDetailsModalOpen(false);
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update confirmed location state.');
    }
  };

  const findBorderVertexIndexAtClientPoint = useCallback((clientX, clientY) => {
    if (!(isBorderInteractionActive && borderInteractionMode === 'editing' && activeBorderPoints.length)) {
      return -1;
    }
    const canvasPoint = getCanvasPointFromClient(clientX, clientY);
    if (!canvasPoint) return -1;
    const pointerX = Number(canvasPoint.canvasX);
    const pointerY = Number(canvasPoint.canvasY);
    const renderedRadius = Math.max(4, BORDER_VERTEX_RADIUS * Math.max(0.7, zoom));
    const thresholdPx = Math.max(18, renderedRadius * 2.2);
    for (let index = activeBorderPoints.length - 1; index >= 0; index -= 1) {
      const screenPoint = getBorderScreenPointFromGrid(activeBorderPoints[index]);
      if (!screenPoint) continue;
      const dx = pointerX - Number(screenPoint.x);
      const dy = pointerY - Number(screenPoint.y);
      if (Math.hypot(dx, dy) <= thresholdPx) {
        return index;
      }
    }
    return -1;
  }, [activeBorderPoints, borderInteractionMode, getBorderScreenPointFromGrid, getCanvasPointFromClient, isBorderInteractionActive, zoom]);

  const findBorderMidpointIndexAtClientPoint = useCallback((clientX, clientY) => {
    if (!(isBorderInteractionActive && borderInteractionMode === 'editing' && activeBorderPoints.length >= 2)) {
      return -1;
    }

    const canvasPoint = getCanvasPointFromClient(clientX, clientY);
    if (!canvasPoint) return -1;
    const pointerX = Number(canvasPoint.canvasX);
    const pointerY = Number(canvasPoint.canvasY);
    const renderedRadius = Math.max(4, BORDER_MIDPOINT_RADIUS * Math.max(0.7, zoom));
    const thresholdPx = Math.max(16, renderedRadius * 2.2);
    for (let index = activeBorderPoints.length - 1; index >= 0; index -= 1) {
      const nextIndex = (index + 1) % activeBorderPoints.length;
      const startPoint = getBorderScreenPointFromGrid(activeBorderPoints[index]);
      const endPoint = getBorderScreenPointFromGrid(activeBorderPoints[nextIndex]);
      if (!startPoint || !endPoint) continue;
      const midpointX = (Number(startPoint.x) + Number(endPoint.x)) / 2;
      const midpointY = (Number(startPoint.y) + Number(endPoint.y)) / 2;
      const dx = pointerX - midpointX;
      const dy = pointerY - midpointY;
      if (Math.hypot(dx, dy) <= thresholdPx) {
        return index;
      }
    }

    return -1;
  }, [activeBorderPoints, borderInteractionMode, getBorderScreenPointFromGrid, getCanvasPointFromClient, isBorderInteractionActive, zoom]);

  const handleBorderPointPlacement = useCallback((clientX, clientY) => {
    const snappedPoint = getSnappedGridPointFromClient(clientX, clientY);
    if (!snappedPoint) return false;

    if (!selectedBorderAllianceId) {
      showError('Select an alliance before drawing a border.');
      return true;
    }

    const existingPoints = normalizeBorderPoints(borderDraftPoints);
    if (!existingPoints.length) {
      setBorderDraftPoints([snappedPoint]);
      setBorderHoverGridPoint(snappedPoint);
      return true;
    }

    if (isNearFirstBorderPoint(existingPoints, snappedPoint) && existingPoints.length >= 3) {
      setBorderDraftPoints(existingPoints);
      setBorderHoverGridPoint(existingPoints[0]);
      showInfo('Border loop closed. Click Save (or press Enter) to persist.');
      return true;
    }

    const lastPoint = existingPoints[existingPoints.length - 1];
    if (lastPoint && lastPoint.x === snappedPoint.x && lastPoint.y === snappedPoint.y) {
      return true;
    }

    const nextPoints = [...existingPoints, snappedPoint];
    if (nextPoints.length >= 4 && hasSelfIntersection(nextPoints)) {
      showError('Border cannot self-intersect.');
      return true;
    }

    setBorderDraftPoints(nextPoints);
    setBorderHoverGridPoint(snappedPoint);
    return true;
  }, [
    borderDraftPoints,
    hasSelfIntersection,
    isNearFirstBorderPoint,
    normalizeBorderPoints,
    selectedBorderAllianceId,
    showInfo,
    showError,
    getSnappedGridPointFromClient
  ]);

  const handleBorderPointerDown = useCallback((clientX, clientY) => {
    if (!isBorderInteractionActive) {
      return false;
    }

    if (borderInteractionMode === 'editing') {
      const nextIndex = findBorderVertexIndexAtClientPoint(clientX, clientY);
      const snappedPoint = getSnappedGridPointFromClient(clientX, clientY);
      if (nextIndex >= 0) {
        setBorderDragVertexIndex(nextIndex);
        setBorderHoverGridPoint(snappedPoint);
        return true;
      }

      const midpointIndex = findBorderMidpointIndexAtClientPoint(clientX, clientY);
      if (midpointIndex >= 0) {
        const currentPoints = normalizeBorderPoints(borderDraftPoints);
        const startPoint = currentPoints[midpointIndex];
        const endPoint = currentPoints[(midpointIndex + 1) % currentPoints.length];
        if (startPoint && endPoint) {
          const samePoint = (left, right) => (
            Number(left?.x) === Number(right?.x) && Number(left?.y) === Number(right?.y)
          );
          let insertedPoint = snappedPoint
            ? { x: Math.round(Number(snappedPoint.x)), y: Math.round(Number(snappedPoint.y)) }
            : {
              x: Math.round((Number(startPoint.x) + Number(endPoint.x)) / 2),
              y: Math.round((Number(startPoint.y) + Number(endPoint.y)) / 2)
            };

          if (samePoint(insertedPoint, startPoint) || samePoint(insertedPoint, endPoint)) {
            const midpointX = (Number(startPoint.x) + Number(endPoint.x)) / 2;
            const midpointY = (Number(startPoint.y) + Number(endPoint.y)) / 2;
            const fallbackPoint = [
              { x: Math.floor(midpointX), y: Math.floor(midpointY) },
              { x: Math.ceil(midpointX), y: Math.ceil(midpointY) },
              { x: Math.round(midpointX), y: Math.round(midpointY) }
            ].find((candidate) => !samePoint(candidate, startPoint) && !samePoint(candidate, endPoint));
            if (!fallbackPoint) {
              return true;
            }
            insertedPoint = fallbackPoint;
          }

          const insertIndex = midpointIndex === currentPoints.length - 1
            ? currentPoints.length
            : midpointIndex + 1;
          const nextPoints = [
            ...currentPoints.slice(0, insertIndex),
            insertedPoint,
            ...currentPoints.slice(insertIndex)
          ];
          if (nextPoints.length >= 4 && hasSelfIntersection(nextPoints)) {
            showError('Border cannot self-intersect.');
            return true;
          }

          setBorderDraftPoints(nextPoints);
          setBorderDragVertexIndex(insertIndex);
          setBorderHoverGridPoint(insertedPoint);
          return true;
        }
      }

      return false;
    }

    if (borderInteractionMode === 'drawing') {
      return handleBorderPointPlacement(clientX, clientY);
    }

    return false;
  }, [
    borderDraftPoints,
    borderInteractionMode,
    findBorderMidpointIndexAtClientPoint,
    findBorderVertexIndexAtClientPoint,
    getSnappedGridPointFromClient,
    handleBorderPointPlacement,
    hasSelfIntersection,
    isBorderInteractionActive,
    normalizeBorderPoints,
    showError
  ]);

  const handleBorderPointerMove = useCallback((clientX, clientY) => {
    if (!isBorderInteractionActive) {
      return false;
    }
    const snappedPoint = getSnappedGridPointFromClient(clientX, clientY);
    setBorderHoverGridPoint(snappedPoint);

    if (borderInteractionMode === 'drawing') {
      return true;
    }

    if (borderInteractionMode !== 'editing' || borderDragVertexIndex < 0 || !snappedPoint) {
      return false;
    }

    setBorderDraftPoints((previous) => {
      if (!Array.isArray(previous) || !previous.length) return previous;
      if (!previous[borderDragVertexIndex]) return previous;
      if (previous[borderDragVertexIndex].x === snappedPoint.x && previous[borderDragVertexIndex].y === snappedPoint.y) {
        return previous;
      }

      const next = previous.map((point, index) => (
        index === borderDragVertexIndex
          ? { x: snappedPoint.x, y: snappedPoint.y }
          : point
      ));
      if (next.length >= 4 && hasSelfIntersection(next)) {
        return previous;
      }
      return next;
    });

    return true;
  }, [
    borderDragVertexIndex,
    borderInteractionMode,
    getSnappedGridPointFromClient,
    hasSelfIntersection,
    isBorderInteractionActive
  ]);

  const handleBorderPointerUp = useCallback(() => {
    if (!isBorderInteractionActive) return false;
    if (borderInteractionMode === 'editing') {
      if (borderDragVertexIndex >= 0) {
        setBorderDragVertexIndex(-1);
        return true;
      }
      return false;
    }
    return true;
  }, [borderDragVertexIndex, borderInteractionMode, isBorderInteractionActive]);

  // movement functions__________________________________________________________________
  const handleMouseDown = (event) => {
    if (handleBorderPointerDown(event.clientX, event.clientY)) {
      setDragging(false);
      return;
    }
    if (isDrawingMode) {
      handleDrawingPointerStart(event.clientX, event.clientY);
      return;
    }
    if (isMultiSelectMode) {
      handleBeginMultiSelectDrag(event.clientX, event.clientY);
      return;
    }
    setDragging(true);
    startDragOffset.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    dragStart.current = { x: event.clientX, y: event.clientY };
  };

  const handleMouseMove = (event) => {
    if (handleBorderPointerMove(event.clientX, event.clientY)) {
      return;
    }
    if (isDrawingMode) {
      handleDrawingPointerMove(event.clientX, event.clientY);
      return;
    }
    if (isMultiSelectMode) {
      if (isMultiSelectDragging) {
        handleUpdateMultiSelectDrag(event.clientX, event.clientY);
      }
      return;
    }
    if (dragging) {
      setOffset({
        x: event.clientX - startDragOffset.current.x,
        y: event.clientY - startDragOffset.current.y,
      });
    }
    updateCursorPreviewFromPointer(event);
  };

  const handleMouseUp = (event) => {
    if (handleBorderPointerUp()) {
      return;
    }
    if (isBorderInteractionActive && borderInteractionMode === 'editing') {
      setDragging(false);
      return;
    }
    if (isDrawingMode) {
      handleDrawingPointerEnd(event.clientX, event.clientY);
      return;
    }
    if (isMultiSelectMode) {
      if (isMultiSelectDragging) {
        handleFinalizeMultiSelectDrag(event.clientX, event.clientY);
      }
      setDragging(false);
      return;
    }
    setDragging(false);
    const dragDistance = Math.sqrt(Math.pow(event.clientX - dragStart.current.x, 2) + Math.pow(event.clientY - dragStart.current.y, 2));
    if (dragDistance < 5) { // Consider it a click if the drag distance is less than 5 pixels
      handleCanvasClick(event);
    }
  };

  const handleMouseLeave = () => {
    setCursorPreviewGrid(null);
    if (isBorderInteractionActive) {
      setBorderHoverGridPoint(null);
      setBorderDragVertexIndex(-1);
    }
    if (isMultiSelectDragging) {
      clearMultiSelectDragState();
    }
    if (isDrawingMode) {
      clearDrawingState();
    }
  };

  const handleWheel = (event) => {
    if (isQuickAddMode && selectedTileAddType === 'benefit' && event.shiftKey) {
      event.preventDefault();
      const direction = event.deltaY > 0 ? 1 : -1;
      cycleQuickAddBenefitSubType(direction);
      return;
    }

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const mouseX = (event.clientX - rect.left - canvas.width / 2 - offset.x) / zoom;
    const mouseY = (event.clientY - rect.top - canvas.height / 2 - offset.y) / zoom;

    const zoomFactor = 1.1;
    const newZoom = event.deltaY > 0 ? zoom / zoomFactor : zoom * zoomFactor;

    const newOffsetX = offset.x - mouseX * (newZoom - zoom);
    const newOffsetY = offset.y - mouseY * (newZoom - zoom);
    if (newZoom < BASE_GRID_AUTO_HIDE_ZOOM_THRESHOLD && layers.baseGrid) {
      toggleLayer("baseGrid")
    }
    if (newZoom > BASE_GRID_AUTO_HIDE_ZOOM_THRESHOLD && !layers.baseGrid) {
      toggleLayer("baseGrid")
    }
    setZoom(newZoom);
    setOffset({ x: newOffsetX, y: newOffsetY });
  };

  const handleTileClick = (tile) => {
    setSelectedTile(tile);
    setIsTileDetailsModalOpen(true);
  };

  const handleToggleCopyMode = () => {
    if (!canWriteTiles) {
      return;
    }

    const hadSelection = selectedParentTileKeys.length > 0;
    if (!isCopyMode && hadSelection) {
      captureSelectedTemplatesForClipboard('copy');
    }

    setIsCopyMode((prev) => {
      const nextMode = !prev;
      if (nextMode) {
        setIsMultiSelectLocked(false);
        setIsCutMode(false);
        clearCutModeState();
        clearMultiSelectionState();
        clearQuickAddState();
        setIsDrawingMode(false);
        setBorderInteractionMode('idle');
        setBorderDraftPoints([]);
        setBorderHoverGridPoint(null);
        setBorderDragVertexIndex(-1);
        setBorderPreviewMetrics(null);
        setIsBorderPreviewLoading(false);
        clearDrawingState();
        setIsTileDetailsModalOpen(false);
      } else {
        clearCopyModeState();
      }
      return nextMode;
    });
  };

  const handleToggleCutMode = () => {
    if (!canUseCutMode) {
      return;
    }

    const hadSelection = selectedParentTileKeys.length > 0;
    if (!isCutMode && hadSelection) {
      captureSelectedTemplatesForClipboard('cut');
    }

    setIsCutMode((prev) => {
      const nextMode = !prev;
      if (nextMode) {
        setIsMultiSelectLocked(false);
        setIsCopyMode(false);
        clearCopyModeState();
        clearMultiSelectionState();
        clearQuickAddState();
        setIsDrawingMode(false);
        setBorderInteractionMode('idle');
        setBorderDraftPoints([]);
        setBorderHoverGridPoint(null);
        setBorderDragVertexIndex(-1);
        setBorderPreviewMetrics(null);
        setIsBorderPreviewLoading(false);
        clearDrawingState();
        setIsTileDetailsModalOpen(false);
      } else {
        clearCutModeState();
      }
      return nextMode;
    });
  };

  const handleToggleDrawingMode = () => {
    if (!canWriteTiles) {
      showError('You are not authorized to draw on this map.');
      return;
    }

    setIsDrawingMode((previous) => {
      const nextMode = !previous;
      if (nextMode) {
        setIsDrawingPanelCollapsed(window.innerWidth <= 1024);
        clearDrawingState();
        setIsCopyMode(false);
        setIsCutMode(false);
        clearCopyModeState();
        clearCutModeState();
        setIsMultiSelectLocked(false);
        clearMultiSelectionState();
        setIsQuickAddPanelOpen(false);
        clearQuickAddState();
        setBorderInteractionMode('idle');
        setBorderDraftPoints([]);
        setBorderHoverGridPoint(null);
        setBorderDragVertexIndex(-1);
        setBorderPreviewMetrics(null);
        setIsBorderPreviewLoading(false);
        setIsTileDetailsModalOpen(false);
      } else {
        setIsDrawingPanelCollapsed(false);
        clearDrawingState();
      }
      return nextMode;
    });
  };

  const handlePendingDeleteSelection = () => {
    if (!selectedNation || !selectedPlan || !selectedParentTileKeys.length) {
      return;
    }

    const selectedTemplates = normalizeParentTemplates(
      selectedParentTileKeys
        .map((parentKey) => parentTilesByKey.get(parentKey))
        .filter(Boolean)
        .map((tile) => buildParentTemplateFromTile(tile))
    );

    if (!selectedTemplates.length) {
      return;
    }

    const confirmDelete = window.confirm(
      `Delete ${selectedParentTileKeys.length} selected map object(s)? This cannot be undone.`
    );
    if (!confirmDelete) {
      return;
    }

    const forwardSteps = [{ type: 'delete', parents: selectedTemplates }];
    const inverseSteps = [{ type: 'place', parents: selectedTemplates }];
    const command = {
      nationId: String(selectedNation._id),
      planId: String(selectedPlan._id),
      steps: forwardSteps
    };
    const executed = executeCommandWithGuards(command);
    if (!executed) {
      return;
    }
    pushHistoryEntry(createHistoryEntry('Delete multi selection', forwardSteps, inverseSteps));

    clearMultiSelectionState();
  };

  const handleMultiSelectControlClick = () => {
    const hasActiveSelectionState =
      isMultiSelectMode || isMultiSelectLocked || selectedParentTileKeys.length > 0;

    if (!hasActiveSelectionState) {
      setIsMultiSelectLocked(true);
      setMultiSelectTool('square');
      setIsCopyMode(false);
      setIsCutMode(false);
        setIsDrawingMode(false);
        setBorderInteractionMode('idle');
        setBorderDraftPoints([]);
        setBorderHoverGridPoint(null);
        setBorderDragVertexIndex(-1);
        setBorderPreviewMetrics(null);
        setIsBorderPreviewLoading(false);
        clearDrawingState();
        clearCopyModeState();
        clearCutModeState();
      clearQuickAddState();
      setIsTileDetailsModalOpen(false);
      return;
    }

    setIsMultiSelectLocked(false);
    clearMultiSelectionState();
  };

  // Handle navigation and set highlighted tile
  const handleNavigate = (gridX, gridY) => {
    const newOffsetX = (gridX - gridY) * (tileSize / 2) * zoom * -1;
    const newOffsetY = (gridX + gridY) * (tileSize / 4) * zoom;
    setOffset({ x: newOffsetX, y: newOffsetY });

    // Set the highlighted tile
    setHighlightedTile({ gridX, gridY });

    // Clear the highlight after 2 seconds
    setTimeout(() => {
      setHighlightedTile(null);
    }, 3000); // Highlight duration (2 seconds)

    setIsNavigationModalOpen(false); // Close the navigation modal
    setIsFindCommanderModalOpen(false);
  };

  const handleSearchCommanderLocations = useCallback(async (queryText) => {
    const normalizedQuery = String(queryText || '').trim();
    if (!normalizedQuery || normalizedQuery.length < 2) {
      return [];
    }
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a nation and plan before searching commander locations.');
      return [];
    }

    try {
      const response = await axios.get(
        `${apiUrl}/nations/${selectedNation._id}/plans/${selectedPlan._id}/tiles/find-commander`,
        {
          params: {
            name: normalizedQuery,
            limit: 25
          }
        }
      );
      const resultRows = Array.isArray(response?.data?.results) ? response.data.results : [];
      return resultRows.map((row) => ({
        commanderId: normalizeEntityId(row?.commanderId),
        commanderName: String(row?.commanderName || ''),
        x: Number(row?.x),
        y: Number(row?.y),
        alliance: row?.alliance || null
      })).filter((row) => (
        row.commanderId
        && row.commanderName
        && Number.isFinite(row.x)
        && Number.isFinite(row.y)
      ));
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to search commander map locations.');
      return [];
    }
  }, [axios, selectedNation, selectedPlan, showError]);

  const toggleLayer = (layer) => {
    setLayers((prevLayers) => ({
      ...prevLayers,
      [layer]: !prevLayers[layer],
    }));
  };

  const drawSelectionOutline = (context, gridX, gridY, size = 1, strokeStyle = '#ff3333', lineWidth = 4) => {
    const canvas = canvasRef.current;
    // Build the footprint from projected grid corners so large (n x n) tiles
    // follow the same isometric geometry as drawn map tiles.
    const bottom = gridToScreen({
      gridX: gridX - 1,
      gridY: gridY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const left = gridToScreen({
      gridX: gridX - 1,
      gridY: gridY + (size - 1),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const top = gridToScreen({
      gridX: gridX + (size - 1),
      gridY: gridY + (size - 1),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const right = gridToScreen({
      gridX: gridX + (size - 1),
      gridY: gridY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });

    context.beginPath();
    context.moveTo(bottom.x, bottom.y - gap);
    context.lineTo(left.x + gap * 2, left.y);
    context.lineTo(top.x, top.y + gap);
    context.lineTo(right.x - gap * 2, right.y);
    context.closePath();
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.stroke();
  };

  const drawFilledTileOverlay = (
    context,
    gridX,
    gridY,
    size = 1,
    fillStyle = '#0ea5e9',
    strokeStyle = '#0369a1',
    fillAlpha = 0.34,
    strokeAlpha = 0.95,
    lineWidth = 2
  ) => {
    const canvas = canvasRef.current;
    const bottom = gridToScreen({
      gridX: gridX - 1,
      gridY: gridY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const left = gridToScreen({
      gridX: gridX - 1,
      gridY: gridY + (size - 1),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const top = gridToScreen({
      gridX: gridX + (size - 1),
      gridY: gridY + (size - 1),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const right = gridToScreen({
      gridX: gridX + (size - 1),
      gridY: gridY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });

    context.save();
    context.beginPath();
    context.moveTo(bottom.x, bottom.y - gap);
    context.lineTo(left.x + gap * 2, left.y);
    context.lineTo(top.x, top.y + gap);
    context.lineTo(right.x - gap * 2, right.y);
    context.closePath();
    context.fillStyle = fillStyle;
    context.globalAlpha = fillAlpha;
    context.fill();
    context.globalAlpha = strokeAlpha;
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.stroke();
    context.restore();
  };

  const getIsometricFootprintPoints = (gridX, gridY, size = 1) => {
    const canvas = canvasRef.current;
    if (!canvas || !Number.isFinite(size) || size <= 0) {
      return null;
    }
    const bottom = gridToScreen({
      gridX: gridX - 1,
      gridY: gridY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const left = gridToScreen({
      gridX: gridX - 1,
      gridY: gridY + (size - 1),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const top = gridToScreen({
      gridX: gridX + (size - 1),
      gridY: gridY + (size - 1),
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const right = gridToScreen({
      gridX: gridX + (size - 1),
      gridY: gridY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    return [bottom, left, top, right];
  };

  const appendDiamondPath = (context, points) => {
    if (!Array.isArray(points) || points.length !== 4) {
      return;
    }
    context.moveTo(points[0].x, points[0].y);
    context.lineTo(points[1].x, points[1].y);
    context.lineTo(points[2].x, points[2].y);
    context.lineTo(points[3].x, points[3].y);
    context.closePath();
  };

  const drawResourceDistributionOverlay = (context) => {
    if (!isResourceDistributionOverlayEnabled) {
      return;
    }

    const maxGridCoordinate = gridSize - 1;
    const bandsWithBounds = RESOURCE_DISTRIBUTION_BANDS.map((band) => {
      const min = Math.max(0, Math.min(maxGridCoordinate, Number(band.minCorner)));
      const max = Math.max(min, maxGridCoordinate - min);
      return {
        ...band,
        min,
        max,
      };
    });

    bandsWithBounds.forEach((band, index) => {
      const outerSize = (band.max - band.min) + 1;
      const outerPoints = getIsometricFootprintPoints(band.min, band.min, outerSize);
      if (!outerPoints) {
        return;
      }

      context.save();
      context.beginPath();
      appendDiamondPath(context, outerPoints);

      const innerBand = bandsWithBounds[index + 1];
      if (innerBand) {
        const innerSize = (innerBand.max - innerBand.min) + 1;
        const innerPoints = getIsometricFootprintPoints(innerBand.min, innerBand.min, innerSize);
        appendDiamondPath(context, innerPoints);
      }

      context.fillStyle = band.fill;
      context.fill('evenodd');

      context.beginPath();
      appendDiamondPath(context, outerPoints);
      context.strokeStyle = band.stroke;
      context.lineWidth = 1.25;
      context.stroke();
      context.restore();
    });
  };

  const parseAllianceTerritoryColor = useCallback((rawColor = '') => {
    const colorText = String(rawColor || '').trim();
    if (!colorText) return { r: 56, g: 189, b: 248 };

    if (colorText.startsWith('#')) {
      const hex = colorText.slice(1);
      if (hex.length === 6) {
        const r = Number.parseInt(hex.slice(0, 2), 16);
        const g = Number.parseInt(hex.slice(2, 4), 16);
        const b = Number.parseInt(hex.slice(4, 6), 16);
        if ([r, g, b].every(Number.isFinite)) return { r, g, b };
      }
    }

    const rgbParts = colorText.split(',').map((part) => Number.parseInt(part.trim(), 10));
    if (rgbParts.length === 3 && rgbParts.every((value) => Number.isFinite(value))) {
      return { r: rgbParts[0], g: rgbParts[1], b: rgbParts[2] };
    }

    return { r: 56, g: 189, b: 248 };
  }, []);

  const getConfirmedTerritoryColor = useCallback((rawColor = '') => {
    const parsed = parseAllianceTerritoryColor(rawColor);
    const nextColor = isGreenishTerritoryColor(parsed)
      ? CONFIRMED_TERRITORY_BLUE_RGB
      : CONFIRMED_TERRITORY_GREEN_RGB;
    return `${nextColor.r},${nextColor.g},${nextColor.b}`;
  }, [parseAllianceTerritoryColor]);

  const drawBorderPolygon = useCallback((context, points = [], color = { r: 56, g: 189, b: 248 }, options = {}) => {
    if (!Array.isArray(points) || points.length < 3) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const screenPoints = points
      .map((point) => gridToScreen({
        gridX: Number(point?.x),
        gridY: Number(point?.y),
        tileSize,
        zoom,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
      }))
      .filter((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y));

    if (screenPoints.length < 3) return;

    const fillAlpha = Number.isFinite(options.fillAlpha) ? options.fillAlpha : BORDER_FILL_ALPHA;
    const strokeAlpha = Number.isFinite(options.strokeAlpha) ? options.strokeAlpha : BORDER_STROKE_ALPHA;
    const lineWidth = Number.isFinite(options.lineWidth)
      ? options.lineWidth
      : Math.max(1.5, 2.4 * Math.max(0.7, zoom));

    context.save();
    context.beginPath();
    context.moveTo(screenPoints[0].x, screenPoints[0].y);
    for (let index = 1; index < screenPoints.length; index += 1) {
      context.lineTo(screenPoints[index].x, screenPoints[index].y);
    }
    context.closePath();
    context.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${fillAlpha})`;
    context.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${strokeAlpha})`;
    context.lineWidth = lineWidth;
    if (options.dashed) {
      context.setLineDash([8, 6]);
    }
    context.fill();
    context.stroke();
    context.restore();
  }, [offset.x, offset.y, tileSize, zoom]);

  const drawBorderOverlay = (context) => {
    if (!isBorderOverlayEnabled && !isBorderInteractionActive) {
      return;
    }

    if (isBorderOverlayEnabled) {
      borderRows.forEach((row) => {
        const allianceId = String(row.allianceId || '');
        if (!allianceId) return;
        if (borderVisibilityByAlliance[allianceId] === false) return;
        const borderPoints = Array.isArray(row?.border?.points) ? row.border.points : [];
        if (borderPoints.length < 3) return;
        const color = parseAllianceTerritoryColor(row?.territoryColor);
        drawBorderPolygon(context, borderPoints, color, {
          fillAlpha: BORDER_FILL_ALPHA,
          strokeAlpha: BORDER_STROKE_ALPHA,
          lineWidth: Math.max(1.4, 2.1 * Math.max(0.7, zoom))
        });
      });
    }

    if (!isBorderInteractionActive || !activeBorderPoints.length) {
      return;
    }

    const draftColor = { r: 59, g: 130, b: 246 };
    if (activeBorderPoints.length >= 3) {
      drawBorderPolygon(context, activeBorderPoints, draftColor, {
        fillAlpha: 0.12,
        strokeAlpha: 0.95,
        lineWidth: Math.max(1.6, 2.5 * Math.max(0.7, zoom)),
        dashed: true
      });
    } else {
      const canvas = canvasRef.current;
      if (canvas) {
        context.save();
        context.beginPath();
        const first = gridToScreen({
          gridX: Number(activeBorderPoints[0]?.x),
          gridY: Number(activeBorderPoints[0]?.y),
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });
        context.moveTo(first.x, first.y);
        for (let index = 1; index < activeBorderPoints.length; index += 1) {
          const screen = gridToScreen({
            gridX: Number(activeBorderPoints[index]?.x),
            gridY: Number(activeBorderPoints[index]?.y),
            tileSize,
            zoom,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            offsetX: offset.x,
            offsetY: offset.y,
          });
          context.lineTo(screen.x, screen.y);
        }
        context.strokeStyle = 'rgba(59, 130, 246, 0.9)';
        context.lineWidth = Math.max(1.4, 2.2 * Math.max(0.7, zoom));
        context.setLineDash([8, 6]);
        context.stroke();
        context.restore();
      }
    }

    if (borderInteractionMode === 'drawing' && borderHoverGridPoint && activeBorderPoints.length) {
      const canvas = canvasRef.current;
      if (canvas) {
        const hoverStart = activeBorderPoints[activeBorderPoints.length - 1];
        const startPoint = gridToScreen({
          gridX: Number(hoverStart?.x),
          gridY: Number(hoverStart?.y),
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });
        const nextPoint = gridToScreen({
          gridX: Number(borderHoverGridPoint?.x),
          gridY: Number(borderHoverGridPoint?.y),
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });
        context.save();
        context.beginPath();
        context.moveTo(startPoint.x, startPoint.y);
        context.lineTo(nextPoint.x, nextPoint.y);
        context.strokeStyle = 'rgba(147, 197, 253, 0.95)';
        context.lineWidth = Math.max(1.2, 2 * Math.max(0.7, zoom));
        context.setLineDash([5, 4]);
        context.stroke();
        context.restore();
      }
    }

    if (borderInteractionMode === 'editing') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (activeBorderPoints.length >= 2) {
        activeBorderPoints.forEach((point, index) => {
          const nextIndex = (index + 1) % activeBorderPoints.length;
          const nextPoint = activeBorderPoints[nextIndex];
          if (!nextPoint) return;

          const startScreen = getBorderScreenPointFromGrid(point);
          const endScreen = getBorderScreenPointFromGrid(nextPoint);
          if (!startScreen || !endScreen) return;

          const midpointX = (Number(startScreen.x) + Number(endScreen.x)) / 2;
          const midpointY = (Number(startScreen.y) + Number(endScreen.y)) / 2;
          const midpointRadius = Math.max(4, BORDER_MIDPOINT_RADIUS * Math.max(0.7, zoom));
          const plusHalfSize = Math.max(2, midpointRadius * 0.48);

          context.save();
          context.beginPath();
          context.arc(midpointX, midpointY, midpointRadius, 0, Math.PI * 2);
          context.fillStyle = 'rgba(239, 246, 255, 0.94)';
          context.strokeStyle = 'rgba(37, 99, 235, 0.95)';
          context.lineWidth = Math.max(1, 1.4 * Math.max(0.7, zoom));
          context.fill();
          context.stroke();

          context.beginPath();
          context.moveTo(midpointX - plusHalfSize, midpointY);
          context.lineTo(midpointX + plusHalfSize, midpointY);
          context.moveTo(midpointX, midpointY - plusHalfSize);
          context.lineTo(midpointX, midpointY + plusHalfSize);
          context.strokeStyle = 'rgba(30, 64, 175, 0.96)';
          context.lineWidth = Math.max(1, 1.3 * Math.max(0.7, zoom));
          context.stroke();
          context.restore();
        });
      }

      activeBorderPoints.forEach((point, index) => {
        const screenPoint = gridToScreen({
          gridX: Number(point?.x),
          gridY: Number(point?.y),
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });
        const radius = Math.max(4, BORDER_VERTEX_RADIUS * Math.max(0.7, zoom));
        context.save();
        context.beginPath();
        context.arc(screenPoint.x, screenPoint.y, radius, 0, Math.PI * 2);
        context.fillStyle = index === borderDragVertexIndex
          ? 'rgba(191, 219, 254, 0.95)'
          : 'rgba(255, 255, 255, 0.92)';
        context.strokeStyle = 'rgba(30, 64, 175, 0.95)';
        context.lineWidth = Math.max(1, 1.6 * Math.max(0.7, zoom));
        context.fill();
        context.stroke();
        context.restore();
      });
    }
  };

  const drawTerritoryCaptureContestedOverlay = (context) => {
    if (!isTerritoryCaptureMetricsEnabled) {
      return;
    }

    const contestedBenefitParents = Array.isArray(territoryCaptureContested?.benefit)
      ? territoryCaptureContested.benefit
      : [];
    const contestedEliteMineParents = Array.isArray(territoryCaptureContested?.eliteMine)
      ? territoryCaptureContested.eliteMine
      : [];

    if (layers.benefit) {
      const benefitSize = tileInformation?.benefit?.size || 2;
      contestedBenefitParents.forEach((entry) => {
        const x = Number(entry?.x);
        const y = Number(entry?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        drawFilledTileOverlay(
          context,
          x,
          y,
          benefitSize,
          TERRITORY_CAPTURE_CONTESTED_FILL,
          TERRITORY_CAPTURE_CONTESTED_STROKE,
          0.78,
          1,
          2.2
        );
      });
    }

    if (layers.eliteMine) {
      const eliteMineSize = tileInformation?.eliteMine?.size || 3;
      contestedEliteMineParents.forEach((entry) => {
        const x = Number(entry?.x);
        const y = Number(entry?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        drawFilledTileOverlay(
          context,
          x,
          y,
          eliteMineSize,
          TERRITORY_CAPTURE_CONTESTED_FILL,
          TERRITORY_CAPTURE_CONTESTED_STROKE,
          0.78,
          1,
          2.2
        );
      });
    }
  };

  const drawImportantCoordinateOverlay = (context) => {
    const hitboxes = [];
    importantCoordinateLabelHitboxesRef.current = hitboxes;
    if (!isImportantCoordinateOverlayEnabled) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const fontSize = Math.max(10, Math.min(14, Math.round(12 * zoom)));
    const paddingX = 6;
    const boxHeight = fontSize + 8;
    const iconSize = Math.max(10, Math.min(13, Math.round(fontSize * 0.92)));
    const iconStrokeWidth = Math.max(1, Math.round(zoom));
    const iconStackOffset = Math.max(2, Math.round(iconSize * 0.24));

    context.save();
    context.font = `600 ${fontSize}px Arial`;
    context.textAlign = 'left';
    context.textBaseline = 'middle';

    parentTilesByKey.forEach((tile) => {
      const baseType = getBaseTileType(tile?.type);
      const category = getImportantCoordinateCategory(baseType);
      if (!category) {
        return;
      }
      if (!importantCoordinateOverlayFilters[category]) {
        return;
      }
      if (!layers[baseType]) {
        return;
      }

      const copyText = getImportantCoordinateLabelText(tile);
      if (!copyText) {
        return;
      }

      const labelAnchor = gridToScreen({
        gridX: Number(tile.x) + 0.5,
        gridY: Number(tile.y) - 1.5,
        tileSize,
        zoom,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
      });

      const textWidth = context.measureText(copyText).width;
      const boxWidth = textWidth + (paddingX * 2) + IMPORTANT_COORDINATE_COPY_ICON_GAP + IMPORTANT_COORDINATE_COPY_ICON_PADDING + iconSize;
      const boxX = labelAnchor.x - (boxWidth / 2);
      const boxY = labelAnchor.y - (boxHeight / 2);

      if (boxX > canvas.width || (boxX + boxWidth) < 0 || boxY > canvas.height || (boxY + boxHeight) < 0) {
        return;
      }

      const accentColor = IMPORTANT_COORDINATE_OVERLAY_COLORS[category] || '#38bdf8';
      context.fillStyle = 'rgba(15, 23, 42, 0.84)';
      context.fillRect(boxX, boxY, boxWidth, boxHeight);
      context.strokeStyle = accentColor;
      context.lineWidth = 1.5;
      context.strokeRect(boxX, boxY, boxWidth, boxHeight);
      context.fillStyle = '#f8fafc';
      context.fillText(copyText, boxX + paddingX, boxY + (boxHeight / 2));

      const dividerX = boxX + boxWidth - iconSize - IMPORTANT_COORDINATE_COPY_ICON_PADDING - (IMPORTANT_COORDINATE_COPY_ICON_GAP / 2);
      context.beginPath();
      context.moveTo(dividerX, boxY + 2);
      context.lineTo(dividerX, boxY + boxHeight - 2);
      context.strokeStyle = 'rgba(148, 163, 184, 0.75)';
      context.lineWidth = 1;
      context.stroke();

      const iconX = boxX + boxWidth - iconSize - IMPORTANT_COORDINATE_COPY_ICON_PADDING;
      const iconY = boxY + ((boxHeight - iconSize) / 2);
      const iconRectSize = iconSize - iconStackOffset;
      context.lineWidth = iconStrokeWidth;
      context.strokeStyle = '#e2e8f0';
      context.strokeRect(iconX + iconStackOffset, iconY, iconRectSize, iconRectSize);
      context.strokeRect(iconX, iconY + iconStackOffset, iconRectSize, iconRectSize);

      hitboxes.push({
        minX: boxX,
        maxX: boxX + boxWidth,
        minY: boxY,
        maxY: boxY + boxHeight,
        copyText
      });
    });

    context.restore();
  };

  const drawPerimeterSelection = (context) => {
    if (!(isMultiSelectMode && isMultiSelectDragging && multiSelectTool === 'square')) {
      return;
    }
    const bounds = getSquareSelectionBounds(multiSelectDragStart, multiSelectDragCurrent);
    if (!bounds) {
      return;
    }
    const { minX, maxX, minY, maxY } = bounds;
    const canvas = canvasRef.current;

    // Draw one continuous perimeter around the full selected rectangle.
    const bottom = gridToScreen({
      gridX: minX - 1,
      gridY: minY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const left = gridToScreen({
      gridX: minX - 1,
      gridY: maxY,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const top = gridToScreen({
      gridX: maxX,
      gridY: maxY,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });
    const right = gridToScreen({
      gridX: maxX,
      gridY: minY - 1,
      tileSize,
      zoom,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
    });

    context.beginPath();
    context.moveTo(bottom.x, bottom.y - gap);
    context.lineTo(left.x + gap * 2, left.y);
    context.lineTo(top.x, top.y + gap);
    context.lineTo(right.x - gap * 2, right.y);
    context.closePath();
    context.strokeStyle = '#00b0ff';
    context.lineWidth = 3;
    context.stroke();
  };

  const drawLassoSelectionPreview = (context) => {
    if (!(isMultiSelectMode && isMultiSelectDragging && multiSelectTool === 'lasso')) {
      return;
    }
    if (!Array.isArray(multiSelectLassoPath) || multiSelectLassoPath.length < 2) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const previewPoints = multiSelectLassoPath
      .map((point) => {
        const x = Number(point?.x);
        const y = Number(point?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        return gridToScreen({
          gridX: x,
          gridY: y,
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });
      })
      .filter(Boolean);

    if (previewPoints.length < 2) {
      return;
    }

    context.save();
    context.beginPath();
    context.moveTo(previewPoints[0].x, previewPoints[0].y);
    for (let index = 1; index < previewPoints.length; index += 1) {
      context.lineTo(previewPoints[index].x, previewPoints[index].y);
    }
    if (previewPoints.length >= 3) {
      context.closePath();
      context.fillStyle = 'rgba(56, 189, 248, 0.16)';
      context.fill();
    }
    context.strokeStyle = '#00b0ff';
    context.lineWidth = 3;
    context.stroke();
    context.restore();
  };

  const drawSingleDrawing = useCallback((context, drawing, { isDraft = false } = {}) => {
    if (!drawing) return;

    const kind = String(drawing.kind || '');
    const points = Array.isArray(drawing.points) ? drawing.points : [];
    if (!points.length) return;

    const strokeColor = drawing?.style?.color || '#38bdf8';
    const strokeWidth = Number(drawing?.style?.width || 2);
    const lineWidth = Math.max(1, strokeWidth * Math.max(0.65, zoom));
    const screenPoints = points
      .map((point) => getScreenPointFromGrid(point))
      .filter(Boolean);

    context.save();
    context.strokeStyle = strokeColor;
    context.lineWidth = lineWidth;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    if (isDraft) {
      context.setLineDash([8, 6]);
      context.globalAlpha = 0.85;
    }

    if (kind === 'text') {
      const layout = getTextDrawingScreenLayout(drawing, context);
      if (!layout) {
        context.restore();
        return;
      }
      context.font = `600 ${layout.fontSize}px "Segoe UI", sans-serif`;
      context.textBaseline = 'middle';
      context.textAlign = 'left';
      context.fillStyle = layout.backgroundColor;
      context.fillRect(layout.boxX, layout.boxY, layout.boxWidth, layout.boxHeight);
      context.strokeStyle = layout.borderColor;
      context.strokeRect(layout.boxX, layout.boxY, layout.boxWidth, layout.boxHeight);
      context.fillStyle = layout.textColor;
      context.fillText(
        layout.textValue,
        layout.boxX + layout.padding,
        layout.boxY + (layout.boxHeight / 2)
      );
      context.restore();
      return;
    }

    if (screenPoints.length < 2) {
      context.restore();
      return;
    }

    context.beginPath();
    context.moveTo(screenPoints[0].x, screenPoints[0].y);
    for (let index = 1; index < screenPoints.length; index += 1) {
      context.lineTo(screenPoints[index].x, screenPoints[index].y);
    }
    context.stroke();
    context.restore();
  }, [getScreenPointFromGrid, getTextDrawingScreenLayout, zoom]);

  // this is the use effect that will run on initial plan load
  useEffect(() => {
    // Refreshing auth can replace `user` object references; only re-bootstrap when actual identity scope changes.
    fetchInitialMapData()
  }, [currentUserRole, currentUserNationId]);

  // this use effect will run any time the selected nation is changed
  // this only occurs on load for nonSuper users. but for super users we need the default plan any time the nation changes
  useEffect(() => {
    fetchDefaultPlans()
  }, [selectedNation]);

  useEffect(() => {
    try {
      const nationId = String(selectedNation?._id || '');
      if (nationId) {
        localStorage.setItem(MAP_SELECTED_NATION_STORAGE_KEY, nationId);
      } else {
        localStorage.removeItem(MAP_SELECTED_NATION_STORAGE_KEY);
      }
    } catch (error) {
      // Ignore local storage write failures.
    }
  }, [selectedNationId]);

  useEffect(() => {
    if (!selectedNationId || !selectedPlanId) {
      setIsBorderOverlayEnabled(true);
      setBorderVisibilityByAlliance({});
      return;
    }
    try {
      const storageKey = buildBorderVisibilityStorageKey(selectedNationId, selectedPlanId);
      const rawValue = localStorage.getItem(storageKey);
      if (!rawValue) {
        setIsBorderOverlayEnabled(true);
        setBorderVisibilityByAlliance({});
        return;
      }
      const parsed = JSON.parse(rawValue);
      setIsBorderOverlayEnabled(parsed?.globalVisible !== false);
      setBorderVisibilityByAlliance(
        parsed?.allianceVisibility && typeof parsed.allianceVisibility === 'object'
          ? parsed.allianceVisibility
          : {}
      );
    } catch (error) {
      setIsBorderOverlayEnabled(true);
      setBorderVisibilityByAlliance({});
    }
  }, [selectedNationId, selectedPlanId]);

  useEffect(() => {
    if (!selectedNationId || !selectedPlanId) {
      return;
    }
    try {
      const storageKey = buildBorderVisibilityStorageKey(selectedNationId, selectedPlanId);
      localStorage.setItem(storageKey, JSON.stringify({
        globalVisible: isBorderOverlayEnabled,
        allianceVisibility: borderVisibilityByAlliance
      }));
    } catch (error) {
      // Ignore local storage write failures.
    }
  }, [borderVisibilityByAlliance, isBorderOverlayEnabled, selectedNationId, selectedPlanId]);

  useEffect(() => {
    const validAllianceIds = new Set(borderRows.map((row) => String(row.allianceId || '')));
    setBorderVisibilityByAlliance((previous) => {
      const next = {};
      let changed = false;
      Object.entries(previous || {}).forEach(([key, value]) => {
        if (validAllianceIds.has(String(key))) {
          next[key] = value;
        } else {
          changed = true;
        }
      });
      if (!changed && Object.keys(next).length === Object.keys(previous || {}).length) {
        return previous;
      }
      return next;
    });
  }, [borderRows]);

  // Force-refresh tile window when nation or plan changes.
  useEffect(() => {
    if (!selectedNationId || !selectedPlanId) {
      if (tileFetchAbortControllerRef.current) {
        tileFetchAbortControllerRef.current.abort();
        tileFetchAbortControllerRef.current = null;
      }
      pendingDrawingCreateHistoryRef.current = [];
      setTiles([]);
      setDrawings([]);
      setBorders([]);
      setPlacedBorderAlliances([]);
      setTerritoryCaptureRows([]);
      setTerritoryCaptureContested(createEmptyTerritoryCaptureContested());
      setBorderDraftPoints([]);
      setBorderHoverGridPoint(null);
      setBorderDragVertexIndex(-1);
      setBorderPreviewMetrics(null);
      setIsBorderPreviewLoading(false);
      setBorderInteractionMode('idle');
      loadedTileWindowRef.current = null;
      tileFetchInFlightWindowRef.current = null;
      viewportTileWindowRef.current = null;
      pendingSocketPlacementParentKeysRef.current.clear();
      optimisticPlacementIndexRef.current.clear();
      if (postMutationRefreshTimeoutRef.current) {
        clearTimeout(postMutationRefreshTimeoutRef.current);
        postMutationRefreshTimeoutRef.current = null;
      }
      if (territoryCaptureRefreshTimeoutRef.current) {
        clearTimeout(territoryCaptureRefreshTimeoutRef.current);
        territoryCaptureRefreshTimeoutRef.current = null;
      }
      if (borderPreviewDebounceRef.current) {
        clearTimeout(borderPreviewDebounceRef.current);
        borderPreviewDebounceRef.current = null;
      }
      setIsOptimizingHive(false);
      setIsPickingHiveTarget(false);
      setHiveCandidates([]);
      setSelectedHiveCandidateIndex(-1);
      setIsTileWindowLoading(false);
      return;
    }
    if (tileFetchAbortControllerRef.current) {
      tileFetchAbortControllerRef.current.abort();
      tileFetchAbortControllerRef.current = null;
    }
    pendingDrawingCreateHistoryRef.current = [];
    loadedTileWindowRef.current = null;
    tileFetchInFlightWindowRef.current = null;
    setTerritoryCaptureRows([]);
    setTerritoryCaptureContested(createEmptyTerritoryCaptureContested());
    pendingSocketPlacementParentKeysRef.current.clear();
    optimisticPlacementIndexRef.current.clear();
    if (postMutationRefreshTimeoutRef.current) {
      clearTimeout(postMutationRefreshTimeoutRef.current);
      postMutationRefreshTimeoutRef.current = null;
    }
    if (territoryCaptureRefreshTimeoutRef.current) {
      clearTimeout(territoryCaptureRefreshTimeoutRef.current);
      territoryCaptureRefreshTimeoutRef.current = null;
    }
    if (borderPreviewDebounceRef.current) {
      clearTimeout(borderPreviewDebounceRef.current);
      borderPreviewDebounceRef.current = null;
    }
    setIsOptimizingHive(false);
    setIsPickingHiveTarget(false);
    setHiveCandidates([]);
    setSelectedHiveCandidateIndex(-1);
    fetchUpdatedTileDataRef.current?.({ force: true });
    fetchDrawingDataRef.current?.({ force: true });
    fetchBorderDataRef.current?.({ force: true });
  }, [selectedNationId, selectedPlanId]);

  useEffect(() => {
    if (!isBorderMenuVisible || !isTerritoryCaptureMetricsEnabled) {
      return;
    }
    if (!selectedNationId || !selectedPlanId) {
      return;
    }
    fetchBorderDataRef.current?.({ force: false });
  }, [isBorderMenuVisible, isTerritoryCaptureMetricsEnabled, selectedNationId, selectedPlanId]);

  useEffect(() => () => {
    if (tileFetchAbortControllerRef.current) {
      tileFetchAbortControllerRef.current.abort();
      tileFetchAbortControllerRef.current = null;
    }
    if (postMutationRefreshTimeoutRef.current) {
      clearTimeout(postMutationRefreshTimeoutRef.current);
      postMutationRefreshTimeoutRef.current = null;
    }
    if (territoryCaptureRefreshTimeoutRef.current) {
      clearTimeout(territoryCaptureRefreshTimeoutRef.current);
      territoryCaptureRefreshTimeoutRef.current = null;
    }
    if (borderPreviewDebounceRef.current) {
      clearTimeout(borderPreviewDebounceRef.current);
      borderPreviewDebounceRef.current = null;
    }
  }, []);

  // Debounced viewport-driven tile window refresh while panning/zooming.
  useEffect(() => {
    if (!selectedNationId || !selectedPlanId) {
      return;
    }
    const timerId = setTimeout(() => {
      fetchUpdatedTileData({ force: false });
    }, TILE_WINDOW_FETCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timerId);
    };
  }, [offset.x, offset.y, zoom, selectedNationId, selectedPlanId]);

  useEffect(() => {
    setSelectedParentTileKeys((prev) => prev.filter((key) => parentTilesByKey.has(key)));
  }, [parentTilesByKey]);

  useEffect(() => {
    tilesRef.current = tiles;
  }, [tiles]);

  useEffect(() => {
    clearMultiSelectionState();
    setIsMultiSelectLocked(false);
    setIsCopyMode(false);
    setIsCutMode(false);
    setIsDrawingMode(false);
    clearDrawingState();
    setIsQuickAddPanelOpen(false);
    clearQuickAddState();
    clearCopyModeState();
    clearCutModeState();
    setIsOptimizePanelOpen(false);
    setOptimizeStatusMessage('');
    setOptimizationPreviewFlags([]);
    setOptimizationJobId(null);
    setIsOptimizingTerritory(false);
    setBorderInteractionMode('idle');
    setBorderDraftPoints([]);
    setBorderHoverGridPoint(null);
    setBorderDragVertexIndex(-1);
    setBorderPreviewMetrics(null);
    setIsBorderPreviewLoading(false);
    setCursorPreviewGrid(null);
    setPlacedBorderAlliances([]);
    setUndoStack([]);
    setRedoStack([]);
    historySequenceRef.current = 0;
    if (optimizationWatchdogRef.current) {
      clearTimeout(optimizationWatchdogRef.current);
      optimizationWatchdogRef.current = null;
    }
    if (optimizationGhostIntervalRef.current) {
      clearInterval(optimizationGhostIntervalRef.current);
      optimizationGhostIntervalRef.current = null;
    }
    if (borderPreviewDebounceRef.current) {
      clearTimeout(borderPreviewDebounceRef.current);
      borderPreviewDebounceRef.current = null;
    }
    loadedTileWindowRef.current = null;
  }, [clearCopyModeState, clearCutModeState, clearDrawingState, clearMultiSelectionState, clearQuickAddState, selectedNation, selectedPlan]);

  useEffect(() => {
    if ((!canWriteTiles || !canDeleteTiles) && (isCopyMode || isCutMode)) {
      setIsCopyMode(false);
      setIsCutMode(false);
      clearCopyModeState();
      clearCutModeState();
    }
    if (!canWriteTiles && isQuickAddMode) {
      clearQuickAddState();
      setIsQuickAddPanelOpen(false);
    }
    if (!canWriteTiles && isBorderInteractionActive) {
      setBorderInteractionMode('idle');
      setBorderDraftPoints([]);
      setBorderHoverGridPoint(null);
      setBorderDragVertexIndex(-1);
      setBorderPreviewMetrics(null);
      setIsBorderPreviewLoading(false);
    }
  }, [canWriteTiles, canDeleteTiles, clearCopyModeState, clearCutModeState, clearQuickAddState, isCopyMode, isCutMode, isQuickAddMode, isBorderInteractionActive]);

  useEffect(() => {
    if (!(isCopyMode || isCutMode || isQuickAddMode)) {
      setCursorPreviewGrid(null);
    }
  }, [isCopyMode, isCutMode, isQuickAddMode]);

  useEffect(() => {
    const handleHotkeyEditCapture = (event) => {
      if (!hotkeyEditState) return;
      event.preventDefault();

      const key = String(event.key || '').toLowerCase();
      if (!key) return;
      if (key === 'escape') {
        setHotkeyEditState(null);
        return;
      }

      if (hotkeyEditState.kind === 'tile') {
        if (key === 'backspace' || key === 'delete') {
          clearTileHotkey(hotkeyEditState.target);
          setHotkeyEditState(null);
          return;
        }
        if (!TILE_HOTKEY_KEYS.includes(key)) {
          showError(`Tile hotkeys must use: ${TILE_HOTKEY_KEYS.join(' ')}`);
          return;
        }
        const assigned = assignTileHotkey(hotkeyEditState.target, key);
        if (assigned) {
          setHotkeyEditState(null);
        }
        return;
      }

      if (hotkeyEditState.kind === 'tool') {
        if (key === 'backspace' || key === 'delete') {
          clearToolHotkey(hotkeyEditState.target);
          setHotkeyEditState(null);
          return;
        }
        const combo = normalizeHotkeyCombo(event);
        if (!combo) {
          return;
        }
        const assigned = assignToolHotkey(hotkeyEditState.target, combo);
        if (assigned) {
          setHotkeyEditState(null);
        }
      }
    };

    window.addEventListener('keydown', handleHotkeyEditCapture);
    return () => {
      window.removeEventListener('keydown', handleHotkeyEditCapture);
    };
  }, [assignTileHotkey, assignToolHotkey, clearTileHotkey, clearToolHotkey, hotkeyEditState, showError]);

  useEffect(() => {
    const handleGlobalHotkeys = (event) => {
      if (hotkeyEditState) return;
      if (isTextInputTarget(event.target)) return;

      const key = String(event.key || '').toLowerCase();
      const combo = normalizeHotkeyCombo(event);

      if (isBorderInteractionActive && borderInteractionMode === 'drawing' && key === 'enter') {
        event.preventDefault();
        if (activeBorderPoints.length < 3) {
          showError('Border requires at least 3 points.');
          return;
        }
        saveBorderDraft(activeBorderPoints);
        return;
      }

      if (!isBorderInteractionActive && !event.ctrlKey && !event.metaKey && !event.altKey && tileHotkeys[key]) {
        if (!canWriteTiles) return;
        event.preventDefault();
        const tileType = tileHotkeys[key];
        setSelectedTileAddType(tileType);
        setIsQuickAddPanelOpen(true);
        setIsCopyMode(false);
        setIsCutMode(false);
        clearCopyModeState();
        clearCutModeState();
        setIsMultiSelectLocked(false);
        clearMultiSelectionState();
        setIsTileDetailsModalOpen(false);
        return;
      }

      if (combo && combo === toolHotkeys.cancelModes) {
        event.preventDefault();
        cancelAllMapModes();
        return;
      }

      if (combo && combo === toolHotkeys.multiSelect) {
        event.preventDefault();
        setIsMultiSelectLocked((prev) => {
          const nextMode = !prev;
          if (nextMode) {
            setMultiSelectTool('square');
            setIsCopyMode(false);
            setIsCutMode(false);
            clearCopyModeState();
            clearCutModeState();
            clearMultiSelectionState();
            clearQuickAddState();
            setIsTileDetailsModalOpen(false);
          } else {
            clearMultiSelectionState();
          }
          return nextMode;
        });
        return;
      }

      if (!canWriteTiles) return;

      if (combo && combo === toolHotkeys.undo) {
        event.preventDefault();
        handleUndo();
        return;
      }
      if (combo && combo === toolHotkeys.redo) {
        event.preventDefault();
        handleRedo();
        return;
      }
      if (combo && combo === toolHotkeys.copy) {
        event.preventDefault();
        handleToggleCopyMode();
        return;
      }
      if (combo && combo === toolHotkeys.cut) {
        event.preventDefault();
        if (!canUseCutMode) return;
        handleToggleCutMode();
      }
    };

    window.addEventListener('keydown', handleGlobalHotkeys);
    return () => {
      window.removeEventListener('keydown', handleGlobalHotkeys);
    };
  }, [activeBorderPoints, borderInteractionMode, canUseCutMode, canWriteTiles, cancelAllMapModes, clearCopyModeState, clearCutModeState, clearMultiSelectionState, clearQuickAddState, handleRedo, handleToggleCopyMode, handleToggleCutMode, handleUndo, hotkeyEditState, isBorderInteractionActive, saveBorderDraft, showError, tileHotkeys, toolHotkeys]);

  useEffect(() => {
    return () => {
      if (optimizationWatchdogRef.current) {
        clearTimeout(optimizationWatchdogRef.current);
      }
      if (optimizationGhostIntervalRef.current) {
        clearInterval(optimizationGhostIntervalRef.current);
      }
      if (borderPreviewDebounceRef.current) {
        clearTimeout(borderPreviewDebounceRef.current);
      }
    };
  }, []);

  const [touchStartPosition, setTouchStartPosition] = useState(null);

  const handleTouchStart = (event) => {
    if (isBorderInteractionActive && event.touches.length === 1) {
      const touch = event.touches[0];
      const borderConsumed = handleBorderPointerDown(touch.clientX, touch.clientY);
      if (borderConsumed) {
        event.preventDefault();
        setDragging(false);
        return;
      }
    }

    if (isDrawingMode) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        handleDrawingPointerStart(touch.clientX, touch.clientY);
        event.preventDefault();
        return;
      }
    }

    if (isMultiSelectMode && event.touches.length === 1) {
      const touch = event.touches[0];
      handleBeginMultiSelectDrag(touch.clientX, touch.clientY);
      event.preventDefault();
      return;
    }

    if (event.touches.length === 1) {
      // Single-touch: Initialize dragging
      const touch = event.touches[0];
      setTouchStartPosition({ x: touch.clientX, y: touch.clientY });
      setDragging(true);
    } else if (event.touches.length === 2) {
      // Two-touch: Initialize pinch-to-zoom
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      // Calculate the initial pinch distance
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      // Initialize pinch distance and zoom
      initialPinchDistanceRef.current = distance;
      initialZoomRef.current = zoom;

      // Disable dragging during pinch zoom
      setDragging(false);
    }
  };

  const handleTouchMove = (event) => {
    if (isBorderInteractionActive && event.touches.length === 1) {
      const touch = event.touches[0];
      const borderConsumed = handleBorderPointerMove(touch.clientX, touch.clientY);
      if (borderConsumed) {
        event.preventDefault();
        return;
      }
    }

    if (isDrawingMode) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        handleDrawingPointerMove(touch.clientX, touch.clientY);
        event.preventDefault();
        return;
      }
    }

    if (isMultiSelectMode) {
      if (event.touches.length === 1 && isMultiSelectDragging) {
        const touch = event.touches[0];
        handleUpdateMultiSelectDrag(touch.clientX, touch.clientY);
        event.preventDefault();
        return;
      }
      if (event.touches.length !== 2) {
        return;
      }
    }

    if (event.touches.length === 1 && dragging) {
      // Single-touch: Handle dragging
      const touch = event.touches[0];
      const deltaX = touch.clientX - touchStartPosition.x;
      const deltaY = touch.clientY - touchStartPosition.y;
  
      // Update the offset for dragging
      setOffset((prevOffset) => ({
        x: prevOffset.x + deltaX,
        y: prevOffset.y + deltaY,
      }));
  
      // Update the starting position for the next move event
      setTouchStartPosition({ x: touch.clientX, y: touch.clientY });
    } else if (event.touches.length === 2) {
      // Two-touch: Handle pinch-to-zoom
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
  
      // Calculate the current distance between touch points
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
  
      if (initialPinchDistanceRef.current) {
        const zoomFactor = currentDistance / initialPinchDistanceRef.current;
        const newZoom = initialZoomRef.current * zoomFactor; // Clamp zoom
  
        // Calculate the center point between the two touch points
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const centerX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
        const centerY = (touch1.clientY + touch2.clientY) / 2 - rect.top;
  
        const mouseX = (centerX - canvas.width / 2 - offset.x) / zoom;
        const mouseY = (centerY - canvas.height / 2 - offset.y) / zoom;
  
        // Adjust the offset to keep the center point stable
        const newOffsetX = offset.x - mouseX * (newZoom - zoom);
        const newOffsetY = offset.y - mouseY * (newZoom - zoom);
  
        // Update zoom and offset
        if (newZoom < BASE_GRID_AUTO_HIDE_ZOOM_THRESHOLD && layers.baseGrid) {
          toggleLayer("baseGrid")
        }
        if (newZoom > BASE_GRID_AUTO_HIDE_ZOOM_THRESHOLD && !layers.baseGrid) {
          toggleLayer("baseGrid")
        }
        setZoom(newZoom);
        setOffset({ x: newOffsetX, y: newOffsetY });
      }
    }
  };
  

  const handleTouchEnd = (event) => {
    if (isBorderInteractionActive) {
      const borderConsumed = handleBorderPointerUp();
      if (borderConsumed) {
        setDragging(false);
        setTouchStartPosition(null);
        return;
      }
    }

    if (isDrawingMode) {
      const touch = event.changedTouches?.[0];
      if (touch) {
        handleDrawingPointerEnd(touch.clientX, touch.clientY);
      } else {
        clearDrawingState();
      }
      setDragging(false);
      setTouchStartPosition(null);
      return;
    }

    if (isMultiSelectMode) {
      const touch = event.changedTouches?.[0];
      if (touch && isMultiSelectDragging) {
        handleFinalizeMultiSelectDrag(touch.clientX, touch.clientY);
      } else {
        clearMultiSelectDragState();
      }
      setDragging(false);
      setTouchStartPosition(null);
      return;
    }

    setDragging(false);
    setTouchStartPosition(null);
  };


  // Add event listeners for touch controls
  useEffect(() => {
    const canvas = canvasRef.current;

    // Mouse Events
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // Touch Events
    const touchOptions = { passive: false };
    canvas.addEventListener('touchstart', handleTouchStart, touchOptions);
    canvas.addEventListener('touchmove', handleTouchMove, touchOptions);
    canvas.addEventListener('touchend', handleTouchEnd, touchOptions);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseLeave);

      canvas.removeEventListener('touchstart', handleTouchStart, touchOptions);
      canvas.removeEventListener('touchmove', handleTouchMove, touchOptions);
      canvas.removeEventListener('touchend', handleTouchEnd, touchOptions);
    };
  }, [clearDrawingState, clearMultiSelectDragState, copiedTileTemplates, cutTileTemplates, dragging, handleBeginMultiSelectDrag, handleBorderPointerDown, handleBorderPointerMove, handleBorderPointerUp, handleDrawingPointerEnd, handleDrawingPointerMove, handleDrawingPointerStart, handleFinalizeMultiSelectDrag, handleUpdateMultiSelectDrag, isBorderInteractionActive, isCopyMode, isCutMode, isDrawingMode, isMultiSelectDragging, isMultiSelectMode, isQuickAddMode, layers.baseGrid, offset, selectedTileAddType, touchStartPosition, zoom]);


  const drawGrid = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    const rect = canvas.getBoundingClientRect();
    var minVisibleX = screenToGrid({
      clientX: rect.left,
      clientY: rect.bottom,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    }).gridX
    var maxVisibleX = screenToGrid({
      clientX: rect.right,
      clientY: rect.top,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    }).gridX
    var minVisibleY = screenToGrid({
      clientX: rect.right,
      clientY: rect.bottom,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    }).gridY
    var maxVisibleY = screenToGrid({
      clientX: rect.left,
      clientY: rect.top,
      canvasRect: rect,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      offsetX: offset.x,
      offsetY: offset.y,
      zoom,
      tileSize,
    }).gridY

    if (minVisibleX < 0) {
      minVisibleX = 0
    }
    if (minVisibleY < 0) {
      minVisibleY = 0
    }
    if (maxVisibleX > gridSize) {
      maxVisibleX = gridSize
    }
    if (maxVisibleY > gridSize) {
      maxVisibleY = gridSize
    }

    if (layers.baseGrid) {
      for (let i = minVisibleX; i < maxVisibleX; i++) {
        for (let j = minVisibleY; j < maxVisibleY; j++) {
          const { x, y } = gridToScreen({
            gridX: i,
            gridY: j,
            tileSize,
            zoom,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            offsetX: offset.x,
            offsetY: offset.y,
          });
          drawBaseTile(context, x, y, "grid", tileSize, zoom, gap, i, j);
        }
      }
    }

    drawResourceDistributionOverlay(context);
    drawBorderOverlay(context);

    // Draw territory tiles first
    tiles.forEach((tile) => {
      if ((tile.type === 'flag' || tile.type === 'allianceHall' || tile.type === 'fort') && layers.territory) {
        const territoryTile = (isConfirmedOverlayEnabled && tile?.confirmed)
          ? {
            ...tile,
            additionalTileInfo: {
              ...(tile.additionalTileInfo || {}),
              alliance: {
                ...(tile.additionalTileInfo?.alliance || {}),
                territoryColor: getConfirmedTerritoryColor(tile.additionalTileInfo?.alliance?.territoryColor)
              }
            }
          }
          : tile;
        const { x, y } = gridToScreen({
          gridX: tile.x,
          gridY: tile.y,
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });
        drawTerritory(context, x, y, territoryTile, tileSize, zoom, gap);
      }
    });

    // Draw all other tiles on top of territory
    const showOnlyBaseTerrainDeadSpace = zoom <= DEAD_SPACE_BASE_TERRAIN_ONLY_ZOOM_THRESHOLD;
    tiles.forEach((tile) => {
      const { x, y } = gridToScreen({
        gridX: tile.x,
        gridY: tile.y,
        tileSize,
        zoom,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
      });
      if (layers[tile.type]) {
        if (tile.type !== "deadSpace") {
          drawTile(context, x, y, tile, tileSize, zoom, gap, loadedImages);
        } else {
          if (showOnlyBaseTerrainDeadSpace && tile.isBaseTerrainDeadSpace !== true) {
            return;
          }
          drawBaseTile(context, x, y, "deadSpace", tileSize, zoom, gap);
        }
      }
    });

    // Draw benefit subtype overlays above tile images for at-a-glance subtype visibility.
    if (isBenefitOverlayEnabled && layers.benefit) {
      tiles.forEach((tile) => {
        const baseType = getBaseTileType(tile.type);
        if (baseType !== 'benefit') {
          return;
        }

        const parentX = tile.parentTile?.x ?? tile.x;
        const parentY = tile.parentTile?.y ?? tile.y;
        if (tile.x !== parentX || tile.y !== parentY) {
          return;
        }

        const subType = tile.additionalTileInfo?.benefitSubType || DEFAULT_BENEFIT_SUBTYPE;
        if (!benefitOverlayFilters[subType]) {
          return;
        }

        const overlayColor = BENEFIT_OVERLAY_COLORS[subType] || '#0ea5e9';
        const overlayStroke = BENEFIT_OVERLAY_COLORS[subType] || '#0284c7';
        const size = tileInformation.benefit?.size || 2;
        drawFilledTileOverlay(context, tile.x, tile.y, size, overlayColor, overlayStroke);
      });
    }

    if (isConfirmedOverlayEnabled) {
      parentTilesByKey.forEach((tile) => {
        const baseType = getBaseTileType(tile?.type || '');
        if (!isConfirmableLocationType(baseType)) {
          return;
        }
        if (!layers[baseType]) {
          return;
        }
        if (!tile?.confirmed) {
          return;
        }

        const size = tileInformation[baseType]?.size || 1;
        drawFilledTileOverlay(
          context,
          tile.x,
          tile.y,
          size,
          CONFIRMED_BUILDING_OVERLAY_FILL,
          CONFIRMED_BUILDING_OVERLAY_STROKE,
          0.28,
          1,
          2
        );
      });
    }

    drawTerritoryCaptureContestedOverlay(context);
    drawImportantCoordinateOverlay(context);

    if (layers.drawings) {
      drawings.forEach((drawing) => {
        drawSingleDrawing(context, drawing);
      });
    }
    if (drawingDraft) {
      drawSingleDrawing(context, drawingDraft, { isDraft: true });
    }

    if (optimizationPreviewFlags.length) {
      optimizationPreviewFlags.forEach((previewFlag) => {
        if (!Number.isFinite(previewFlag?.x) || !Number.isFinite(previewFlag?.y)) {
          return;
        }
        context.save();
        context.globalAlpha = 0.72;
        drawSelectionOutline(context, previewFlag.x, previewFlag.y, 1, '#f59e0b', 2);
        context.restore();
      });
    }

    if (optimizeMode === 'hive' && hivePreviewCandidates.length) {
      hivePreviewCandidates.forEach((candidate) => {
        const originX = Number(candidate?.origin?.x);
        const originY = Number(candidate?.origin?.y);
        if (!Number.isFinite(originX) || !Number.isFinite(originY)) {
          return;
        }

        context.save();
        context.globalAlpha = candidate.isSelected ? 0.86 : 0.62;
        drawSelectionOutline(
          context,
          originX,
          originY,
          HIVE_FOOTPRINT_SIZE,
          candidate.isSelected ? '#f59e0b' : '#38bdf8',
          candidate.isSelected ? 3 : 2
        );
        context.restore();

        const labelPoint = gridToScreen({
          gridX: originX + ((HIVE_FOOTPRINT_SIZE - 1) / 2),
          gridY: originY + ((HIVE_FOOTPRINT_SIZE - 1) / 2),
          tileSize,
          zoom,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          offsetX: offset.x,
          offsetY: offset.y,
        });

        context.save();
        context.font = `${Math.max(12, Math.round(12 * zoom))}px Arial`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = candidate.isSelected ? '#fbbf24' : '#bae6fd';
        context.strokeStyle = '#0f172a';
        context.lineWidth = 3;
        const label = `#${candidate.rank}`;
        context.strokeText(label, labelPoint.x, labelPoint.y);
        context.fillText(label, labelPoint.x, labelPoint.y);
        context.restore();
      });
    }

    if (isMultiSelectMode || selectedParentTileKeys.length > 0) {
      selectedParentTiles.forEach((tile) => {
        const baseType = getBaseTileType(tile.type);
        const size = tileInformation[baseType]?.size || 1;
        drawSelectionOutline(context, tile.x, tile.y, size, '#ff3333', 4);
      });
      drawPerimeterSelection(context);
      drawLassoSelectionPreview(context);
    }

    if (isCopyMode && copiedSourceParentKeys.length) {
      copiedSourceParentKeys.forEach((parentKey) => {
        const sourceTile = parentTilesByKey.get(parentKey);
        if (!sourceTile) {
          return;
        }
        const sourceType = getBaseTileType(sourceTile.type);
        const sourceSize = tileInformation[sourceType]?.size || 1;
        drawSelectionOutline(context, sourceTile.x, sourceTile.y, sourceSize, '#1d4ed8', 4);
      });
    }

    if (isCutMode && cutSourceParentKeys.length) {
      cutSourceParentKeys.forEach((parentKey) => {
        const sourceTile = parentTilesByKey.get(parentKey);
        if (!sourceTile) {
          return;
        }
        const sourceType = getBaseTileType(sourceTile.type);
        const sourceSize = tileInformation[sourceType]?.size || 1;
        drawSelectionOutline(context, sourceTile.x, sourceTile.y, sourceSize, '#f97316', 4);
      });
    }

    if ((isCopyMode || isCutMode || isQuickAddMode) && cursorPreviewGrid) {
      const activeTemplate = isQuickAddMode
        ? {
          type: selectedTileAddType,
          size: tileInformation[selectedTileAddType]?.size || 1,
          additionalTileInfo: selectedTileAddType === 'benefit'
            ? { benefitSubType: quickAddBenefitSubType || DEFAULT_BENEFIT_SUBTYPE }
            : {}
        }
        : (isCutMode ? cutTileTemplates : copiedTileTemplates);
      const activeTemplates = Array.isArray(activeTemplate)
        ? activeTemplate
        : (activeTemplate ? [activeTemplate] : []);
      const previewTemplates = isQuickAddMode
        ? normalizeParentTemplates(activeTemplates.map((template) => ({
          ...template,
          x: cursorPreviewGrid.gridX,
          y: cursorPreviewGrid.gridY
        })))
        : translateClipboardTemplates(activeTemplates, cursorPreviewGrid.gridX, cursorPreviewGrid.gridY);
      if (previewTemplates.length) {
        let ignoredIds = null;
        if (isCutMode) {
          const normalizedSource = normalizeParentTemplates(cutTileTemplates);
          const { tileIds } = getTileIdsForParentTemplates(normalizedSource);
          ignoredIds = new Set(tileIds.map((id) => String(id)));
        }

        const hasConflict = previewTemplates.some((template) => (
          checkConflict(
            template.x,
            template.y,
            template.type,
            ignoredIds
          )
        ));

        previewTemplates.forEach((template) => {
          const previewType = getBaseTileType(template.type);
          const previewSize = tileInformation[previewType]?.size || template.size || 1;
          const previewTile = {
            x: template.x,
            y: template.y,
            type: previewType,
            additionalTileInfo: template.additionalTileInfo || {},
          };

          const { x, y } = gridToScreen({
            gridX: template.x,
            gridY: template.y,
            tileSize,
            zoom,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            offsetX: offset.x,
            offsetY: offset.y,
          });

          const hasTerritoryPreview = (tileInformation[previewType]?.territorySize || 0) > 0;
          if (hasTerritoryPreview) {
            context.save();
            // Keep territory visible but subtle so placement sprite remains readable.
            context.globalAlpha = hasConflict ? 0.4 : 0.75;
            drawTerritory(context, x, y, previewTile, tileSize, zoom, gap);
            context.restore();
          }

          context.save();
          context.globalAlpha = hasConflict ? 0.35 : 0.62;
          drawTile(context, x, y, previewTile, tileSize, zoom, gap, loadedImages);
          context.restore();

          drawSelectionOutline(
            context,
            template.x,
            template.y,
            previewSize,
            hasConflict ? '#ef4444' : (isQuickAddMode ? '#38bdf8' : '#22c55e'),
            3
          );
        });
      }
    }

    if (pendingQuickAddPlacement) {
      const pendingType = getBaseTileType(pendingQuickAddPlacement.type);
      const pendingTile = {
        x: pendingQuickAddPlacement.gridX,
        y: pendingQuickAddPlacement.gridY,
        type: pendingType,
        additionalTileInfo: {}
      };
      const { x, y } = gridToScreen({
        gridX: pendingQuickAddPlacement.gridX,
        gridY: pendingQuickAddPlacement.gridY,
        tileSize,
        zoom,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
      });

      context.save();
      context.globalAlpha = 0.74;
      if ((tileInformation[pendingType]?.territorySize || 0) > 0) {
        drawTerritory(context, x, y, pendingTile, tileSize, zoom, gap);
      }
      drawTile(context, x, y, pendingTile, tileSize, zoom, gap, loadedImages);
      context.restore();
      drawSelectionOutline(context, pendingQuickAddPlacement.gridX, pendingQuickAddPlacement.gridY, tileInformation[pendingType]?.size || 1, '#0ea5e9', 3);
    }

    // Check if this tile is the highlighted one
    if (highlightedTile) {
      const { x, y } = gridToScreen({
        gridX: highlightedTile.gridX,
        gridY: highlightedTile.gridY,
        tileSize,
        zoom,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        offsetX: offset.x,
        offsetY: offset.y,
      });
      drawBaseTile(context, x, y, "navigateHighlight", tileSize, zoom, gap);
    }
    updateCenterCoords();
  };

  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const stage = canvas.parentElement;
    if (!stage) return;

    const canvasWidth = Math.max(1, Math.floor(stage.clientWidth));
    const canvasHeight = Math.max(1, Math.floor(stage.clientHeight));

    // Keep canvas drawing buffer in sync with rendered size to avoid pointer drift.
    if (canvas.width !== canvasWidth) {
      canvas.width = canvasWidth;
    }
    if (canvas.height !== canvasHeight) {
      canvas.height = canvasHeight;
    }
    setMapStageHeight((prev) => (prev === stage.clientHeight ? prev : stage.clientHeight));

    // Redraw the grid
    drawGrid();
  };



  useEffect(() => {
    window.addEventListener('resize', handleResize);

    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  useEffect(() => {
    window.addEventListener('resize', handleResize);

    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [tiles, drawings, drawingDraft, drawSingleDrawing, zoom, offset, layers, highlightedTile, isSubMenuVisible, isMultiSelectMode, isCopyMode, isCutMode, isQuickAddMode, selectedTileAddType, quickAddBenefitSubType, pendingQuickAddPlacement, selectedParentTileKeys, multiSelectDragStart, multiSelectDragCurrent, multiSelectLassoPath, isMultiSelectDragging, multiSelectTool, copiedSourceParentKeys, cutSourceParentKeys, copiedTileTemplates, cutTileTemplates, parentTilesByKey, optimizationPreviewFlags, optimizeMode, hivePreviewCandidates, cursorPreviewGrid, isBenefitOverlayEnabled, isConfirmedOverlayEnabled, isResourceDistributionOverlayEnabled, isImportantCoordinateOverlayEnabled, benefitOverlayFilters, importantCoordinateOverlayFilters, borderRows, borders, isBorderOverlayEnabled, borderVisibilityByAlliance, isBorderInteractionActive, borderInteractionMode, activeBorderPoints, borderHoverGridPoint, borderDragVertexIndex, isTerritoryCaptureMetricsEnabled, territoryCaptureContested]);

  // Trigger handleResize when window resizes
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(); // Initial resize on mount

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Trigger handleResize when the sub-menu visibility changes
  useEffect(() => {
    handleResize();
  }, [isSubMenuVisible, isPlanMenuVisible]);

  const toggleSubMenu = () => {
    setIsSubMenuVisible((prev) => {
      const shouldOpen = !prev;
      if (shouldOpen) {
        setIsPlanMenuVisible(false);
      }
      return shouldOpen;
    });
  };

  const togglePlanMenu = () => {
    setIsPlanMenuVisible((prev) => {
      const shouldOpen = !prev;
      if (shouldOpen) {
        setIsSubMenuVisible(false);
      }
      return shouldOpen;
    });
  };

  const toggleOverlayMenu = () => {
    setIsOverlayMenuVisible((prev) => !prev);
  };

  const toggleBorderMenu = () => {
    setIsBorderMenuVisible((prev) => !prev);
  };

  const toggleBenefitOverlayMode = () => {
    setIsBenefitOverlayEnabled((prev) => !prev);
  };

  const toggleConfirmedOverlayMode = () => {
    setIsConfirmedOverlayEnabled((prev) => !prev);
  };

  const toggleResourceDistributionOverlayMode = () => {
    setIsResourceDistributionOverlayEnabled((prev) => !prev);
  };

  const toggleImportantCoordinateOverlayMode = () => {
    setIsImportantCoordinateOverlayEnabled((prev) => !prev);
  };

  const toggleBorderOverlayMode = () => {
    setIsBorderOverlayEnabled((prev) => !prev);
  };

  const toggleTerritoryCaptureMetricsMode = () => {
    setIsTerritoryCaptureMetricsEnabled((prev) => !prev);
  };

  const handleBordersPanelWheel = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const panel = event.currentTarget;
    if (!panel) return;
    panel.scrollTop += event.deltaY;
  }, []);

  const toggleBorderAllianceVisibility = (allianceId) => {
    const normalizedAllianceId = String(allianceId || '');
    if (!normalizedAllianceId) return;
    setBorderVisibilityByAlliance((previous) => ({
      ...previous,
      [normalizedAllianceId]: previous[normalizedAllianceId] === false
    }));
  };

  const toggleBenefitOverlaySubtype = (subTypeKey) => {
    setBenefitOverlayFilters((prev) => ({
      ...prev,
      [subTypeKey]: !prev[subTypeKey]
    }));
  };

  const toggleImportantCoordinateOverlayFilter = (filterKey) => {
    setImportantCoordinateOverlayFilters((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  const formatHotkeyLabel = (combo = '') => {
    if (!combo) return 'Unassigned';
    return combo
      .split('+')
      .map((part) => {
        if (part === 'mod') return 'Ctrl/Cmd';
        if (part === 'shift') return 'Shift';
        if (part === 'alt') return 'Alt';
        if (part === 'escape') return 'Esc';
        if (part === 'space') return 'Space';
        return part.toUpperCase();
      })
      .join(' + ');
  };

  const totalSelectedTiles = selectedParentTiles.length;
  const copyClipboardCount = copiedTileTemplates.length;
  const cutClipboardCount = cutTileTemplates.length;
  const copyClipboardSummary = copyClipboardCount === 1
    ? (copiedTileTemplates[0]?.type || 'object')
    : `${copyClipboardCount} objects`;
  const cutClipboardSummary = cutClipboardCount === 1
    ? (cutTileTemplates[0]?.type || 'object')
    : `${cutClipboardCount} objects`;
  const isSuperUser = user?.role === 'superUser';
  const hasOptimizationContext = !!selectedNation?._id && !!selectedPlan?._id;
  const isOptimizationBusy = isOptimizingTerritory || isOptimizingHive;
  const isAnyOverlayEnabled = isBenefitOverlayEnabled
    || isConfirmedOverlayEnabled
    || isResourceDistributionOverlayEnabled
    || isImportantCoordinateOverlayEnabled;
  const borderPanelStyle = useMemo(() => {
    if (!Number.isFinite(mapStageHeight) || mapStageHeight <= 0) {
      return undefined;
    }
    const maxHeight = Math.max(180, Math.floor(mapStageHeight - 92));
    return { maxHeight: `${maxHeight}px` };
  }, [mapStageHeight]);
  const allowCrossNationSelection = user?.role !== 'superUser'
    && user?.role !== 'supporter'
    && (mergerPlanningScope?.allowedNationIds?.length || 0) > 1;

  const controlActions = (
    <div className="button-container">
      {canWriteTiles && (
        <>
          <button
            className="action-button"
            onClick={handleUndo}
            disabled={!undoStack.length}
            title={`Undo (${formatHotkeyLabel(toolHotkeys.undo)})`}
          >
            <FaUndo className="button-icon" />
            <span>Undo</span>
          </button>
          <button
            className="action-button"
            onClick={handleRedo}
            disabled={!redoStack.length}
            title={`Redo (${formatHotkeyLabel(toolHotkeys.redo)})`}
          >
            <FaRedo className="button-icon" />
            <span>Redo</span>
          </button>
        </>
      )}
      <button
        className={`action-button ${isMultiSelectMode || totalSelectedTiles > 0 ? 'active-action-button' : ''}`}
        onClick={handleMultiSelectControlClick}
        title={`Toggle Multi Select (${formatHotkeyLabel(toolHotkeys.multiSelect)})`}
      >
        <FaObjectGroup className="button-icon" />
        <span>{isMultiSelectMode || totalSelectedTiles > 0 ? 'Exit Multi Select' : 'Multi Select'}</span>
      </button>
      {canWriteTiles && (
        <button
          className={`action-button ${isCopyMode ? 'active-action-button' : ''}`}
          onClick={handleToggleCopyMode}
          title={`Copy Mode (${formatHotkeyLabel(toolHotkeys.copy)})`}
        >
          <FaCopy className="button-icon" />
          <span>{isCopyMode ? 'Exit Copy' : 'Copy'}</span>
        </button>
      )}
      {canUseCutMode && (
        <button
          className={`action-button ${isCutMode ? 'active-action-button' : ''}`}
          onClick={handleToggleCutMode}
          title={`Cut Mode (${formatHotkeyLabel(toolHotkeys.cut)})`}
        >
          <FaCut className="button-icon" />
          <span>{isCutMode ? 'Exit Cut' : 'Cut'}</span>
        </button>
      )}
      {canWriteTiles && (
        <button
          className={`action-button ${isDrawingMode ? 'active-action-button' : ''}`}
          onClick={handleToggleDrawingMode}
          title="Draw annotations"
        >
          <FaPen className="button-icon" />
          <span>{isDrawingMode ? 'Exit Draw' : 'Draw'}</span>
        </button>
      )}
      {isSuperUser && (
        <button
          className={`action-button ${isOptimizePanelOpen ? 'active-action-button' : ''}`}
          onClick={() => {
            setIsOptimizePanelOpen((prev) => {
              const next = !prev;
              if (!next) {
                setIsPickingHiveTarget(false);
              }
              return next;
            });
          }}
        >
          <FaMagic className="button-icon" />
          <span>{isOptimizePanelOpen ? 'Hide Optimize' : 'Optimize'}</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="isometric-grid-container">
      <div className={`map-stage ${isSubMenuVisible ? 'tools-open' : ''} ${isPlanMenuVisible ? 'plans-open' : ''} ${isHotkeyPanelOpen ? 'hotkeys-open' : ''}`}>
        {isTileWindowLoading && (
          <div className="map-window-loading-indicator" role="status" aria-live="polite">
            Loading visible tiles...
          </div>
        )}
        <div className="map-overlay-toolbar">
          <div className="map-overlay-top-row">
            <div className="map-overlay-pill-buttons">
              <button
                className={`map-tools-toggle ${isSubMenuVisible ? 'active' : ''}`}
                onClick={toggleSubMenu}
              >
                <FaTools className="map-tools-icon" />
                {isSubMenuVisible ? 'Hide Tools' : 'Tools'}
              </button>
              <button
                className={`map-plans-toggle ${isPlanMenuVisible ? 'active' : ''}`}
                onClick={togglePlanMenu}
              >
                {isPlanMenuVisible ? 'Hide Plans' : 'Plans'}
              </button>
              <button
                className={`map-overlays-toggle ${(isAnyOverlayEnabled || isOverlayMenuVisible) ? 'active' : ''}`}
                onClick={toggleOverlayMenu}
                title={isOverlayMenuVisible ? 'Hide overlays menu' : 'Show overlays menu'}
              >
                {isAnyOverlayEnabled ? 'Overlays On' : 'Overlays'}
              </button>
              <button
                className={`map-borders-toggle ${(isBorderMenuVisible || isBorderOverlayEnabled) ? 'active' : ''}`}
                onClick={toggleBorderMenu}
                title={isBorderMenuVisible ? 'Hide borders menu' : 'Show borders menu'}
              >
                {isBorderOverlayEnabled ? 'Borders On' : 'Borders'}
              </button>
            </div>
            {showReadOnlyEditReason && (
              <div className="map-readonly-badge" role="status" aria-live="polite">
                Read-only: role/scope does not permit edits for selected nation
              </div>
            )}
          </div>
          {isSubMenuVisible && (
            <div className="map-overlay-dropdown map-tools-dropdown">
              <SubMenuBar
                layers={layers}
                toggleLayer={toggleLayer}
                nations={nations}
                selectedNation={selectedNation}
                setSelectedNation={setSelectedNation}
                plans={plans}
                selectedPlan={selectedPlan}
                setPlans={setPlans}
                setSelectedPlan={setSelectedPlan}
                allowCrossNationSelection={allowCrossNationSelection}
                actions={controlActions}
                showPlanning={false}
              />
            </div>
          )}
          {isPlanMenuVisible && (
            <div className="map-overlay-dropdown map-plans-pill-panel">
              <PlanningMode
                plans={plans}
                selectedPlan={selectedPlan}
                setPlans={setPlans}
                setSelectedPlan={setSelectedPlan}
                selectedNation={selectedNation}
                canEdit={canEditPlans}
              />
            </div>
          )}
          {isOverlayMenuVisible && (
            <div className="map-overlay-dropdown map-overlays-pill-panel">
              <div className="map-overlays-header">
                <h4>Overlays</h4>
                <button
                  type="button"
                  className="map-overlays-close"
                  onClick={() => setIsOverlayMenuVisible(false)}
                >
                  Hide
                </button>
              </div>
              <button
                type="button"
                className={`action-button ${isBenefitOverlayEnabled ? 'active-action-button' : ''}`}
                onClick={toggleBenefitOverlayMode}
              >
                Benefit Subtypes
              </button>
              {isBenefitOverlayEnabled && (
                <div className="map-overlays-legend">
                  {BENEFIT_SUBTYPE_KEYS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`map-overlay-subtype-toggle ${benefitOverlayFilters[key] ? 'active' : ''}`}
                      onClick={() => toggleBenefitOverlaySubtype(key)}
                    >
                      <span
                        className="map-overlay-color-dot"
                        style={{ backgroundColor: BENEFIT_OVERLAY_COLORS[key] || '#0ea5e9' }}
                      />
                      <span>{BENEFIT_SUBTYPE_LABELS[key] || key}</span>
                      <span className="map-overlay-count-badge">{benefitSubtypeCounts[key] || 0}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className={`action-button ${isConfirmedOverlayEnabled ? 'active-action-button' : ''}`}
                onClick={toggleConfirmedOverlayMode}
              >
                Confirmed Locations
              </button>
              <button
                type="button"
                className={`action-button ${isImportantCoordinateOverlayEnabled ? 'active-action-button' : ''}`}
                onClick={toggleImportantCoordinateOverlayMode}
              >
                Important Coordinates
              </button>
              {isImportantCoordinateOverlayEnabled && (
                <div className="map-overlays-legend">
                  {IMPORTANT_COORDINATE_OVERLAY_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`map-overlay-subtype-toggle ${importantCoordinateOverlayFilters[option.key] ? 'active' : ''}`}
                      onClick={() => toggleImportantCoordinateOverlayFilter(option.key)}
                    >
                      <span
                        className="map-overlay-color-dot"
                        style={{ backgroundColor: option.color }}
                      />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className={`action-button ${isResourceDistributionOverlayEnabled ? 'active-action-button' : ''}`}
                onClick={toggleResourceDistributionOverlayMode}
              >
                Resource Distribution
              </button>
              {isResourceDistributionOverlayEnabled && (
                <div className="map-resource-distribution-legend">
                  {RESOURCE_DISTRIBUTION_BANDS.map((band) => (
                    <div key={band.key} className="map-resource-distribution-row">
                      <span
                        className="map-overlay-color-dot map-resource-distribution-dot"
                        style={{ backgroundColor: band.legendColor }}
                      />
                      <span>{band.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isBorderMenuVisible && (
            <div
              className="map-overlay-dropdown map-borders-pill-panel"
              style={borderPanelStyle}
              onWheel={handleBordersPanelWheel}
            >
              <div className="map-overlays-header">
                <h4>Borders</h4>
                <button
                  type="button"
                  className="map-overlays-close"
                  onClick={() => setIsBorderMenuVisible(false)}
                >
                  Hide
                </button>
              </div>
              <button
                type="button"
                className={`action-button ${isBorderOverlayEnabled ? 'active-action-button' : ''}`}
                onClick={toggleBorderOverlayMode}
              >
                {isBorderOverlayEnabled ? 'Hide Border Overlay' : 'Show Border Overlay'}
              </button>
              <button
                type="button"
                className={`action-button ${isTerritoryCaptureMetricsEnabled ? 'active-action-button' : ''}`}
                onClick={toggleTerritoryCaptureMetricsMode}
              >
                {isTerritoryCaptureMetricsEnabled ? 'Territory Capture: On' : 'Territory Capture: Off'}
              </button>

              <label className="optimize-territory-label">
                Alliance
                <select
                  className="optimize-territory-select"
                  value={selectedBorderAllianceId}
                  onChange={(event) => setSelectedBorderAllianceId(event.target.value)}
                >
                  {borderAllianceOptions.length === 0 && (
                    <option value="">No placed alliances in plan</option>
                  )}
                  {borderAllianceOptions.map((alliance) => (
                    <option key={alliance._id} value={alliance._id}>
                      N{alliance.nationNumber || '-'} [{alliance.acronym || '---'}] {alliance.name}
                    </option>
                  ))}
                </select>
              </label>

              {canWriteTiles && (
                <div className="border-actions-row">
                  <button
                    type="button"
                    className={`action-button ${borderInteractionMode === 'drawing' ? 'active-action-button' : ''}`}
                    onClick={startBorderDrawing}
                    disabled={!selectedBorderAllianceId}
                  >
                    Draw Border
                  </button>
                  <button
                    type="button"
                    className={`action-button ${borderInteractionMode === 'editing' ? 'active-action-button' : ''}`}
                    onClick={() => startBorderEditing(selectedBorderAllianceId)}
                    disabled={!selectedBorder || borderInteractionMode === 'drawing'}
                  >
                    Edit Border
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={saveBorderDraft}
                    disabled={!isBorderInteractionActive || activeBorderPoints.length < 3}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={cancelBorderInteraction}
                    disabled={!isBorderInteractionActive}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => handleDeleteBorder(selectedBorderAllianceId)}
                    disabled={!selectedBorder}
                  >
                    Delete
                  </button>
                </div>
              )}

              {isBorderInteractionActive && (
                <div className="border-instruction-card">
                  <p>
                    {borderInteractionMode === 'drawing'
                      ? 'Click to add vertices. Click near the first point to close; press Enter or Save to persist.'
                      : 'Drag a vertex handle to edit. Press Save to commit changes.'}
                  </p>
                  <p>Draft points: {activeBorderPoints.length}</p>
                  {isBorderPreviewLoading && <p>Previewing metrics...</p>}
                  {isBorderOverlayEnabled && borderPreviewMetrics && (
                    <>
                      <div className="border-elite-summary">
                        {[...ELITE_MINE_LEVELS].reverse().map((level) => (
                          <span key={`preview-elite-${level}`} className="border-elite-chip">
                            L{level}: {borderPreviewMetrics?.eliteMineLevels?.[level] || 0}
                          </span>
                        ))}
                      </div>
                      <div className="border-metrics-grid">
                      {BENEFIT_SUBTYPE_KEYS.map((subTypeKey) => (
                        <div key={`preview-benefit-${subTypeKey}`} className="border-metric-row">
                          <span>{BENEFIT_SUBTYPE_LABELS[subTypeKey] || subTypeKey}</span>
                          <span>{borderPreviewMetrics?.benefitSubTypes?.[subTypeKey] || 0}</span>
                        </div>
                      ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {isBorderOverlayEnabled && (
                <div className="border-alliance-list">
                  {borderRows.length === 0 && (
                    <p className="selection-meta">No alliance borders created yet.</p>
                  )}
                  {borderRows.map((row) => {
                    const allianceId = String(row.allianceId || '');
                    const metrics = row.border?.metrics || {};
                    const visible = borderVisibilityByAlliance[allianceId] !== false;
                    return (
                      <div key={row.border?._id || allianceId} className="border-alliance-card">
                        <div className="border-alliance-card-header">
                          <button
                            type="button"
                            className={`map-overlay-subtype-toggle ${visible ? 'active' : ''}`}
                            onClick={() => toggleBorderAllianceVisibility(allianceId)}
                          >
                            <span
                              className="map-overlay-color-dot"
                              style={{
                                backgroundColor: (() => {
                                  const parsed = parseAllianceTerritoryColor(row.territoryColor);
                                  return `rgb(${parsed.r}, ${parsed.g}, ${parsed.b})`;
                                })()
                              }}
                            />
                            <span>N{row.nationNumber || '-'} [{row.acronym || '---'}] {row.name}</span>
                          </button>
                          <div className="border-card-actions">
                            <button
                              type="button"
                              className="action-button"
                              onClick={() => {
                                setSelectedBorderAllianceId(allianceId);
                                startBorderEditing(allianceId);
                              }}
                              disabled={!canWriteTiles}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="action-button"
                              onClick={() => handleDeleteBorder(allianceId)}
                              disabled={!canWriteTiles}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="border-elite-summary">
                          {[...ELITE_MINE_LEVELS].reverse().map((level) => (
                            <span key={`row-${allianceId}-elite-${level}`} className="border-elite-chip">
                              L{level}: {metrics?.eliteMineLevels?.[level] || 0}
                            </span>
                          ))}
                        </div>
                        <div className="border-metrics-grid">
                          {BENEFIT_SUBTYPE_KEYS.map((subTypeKey) => (
                            <div key={`row-${allianceId}-benefit-${subTypeKey}`} className="border-metric-row">
                              <span>{BENEFIT_SUBTYPE_LABELS[subTypeKey] || subTypeKey}</span>
                              <span>{metrics?.benefitSubTypes?.[subTypeKey] || 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!isBorderOverlayEnabled && (
                <p className="selection-meta">Border overlay is hidden. Enable it to view border metrics.</p>
              )}

              {isTerritoryCaptureMetricsEnabled && (
                <div className="territory-capture-section">
                  <div className="territory-capture-header">
                    <h5>Territory Capture Metrics</h5>
                    <span className="territory-capture-note">Contested tiles are highlighted in red.</span>
                  </div>
                  <div className="border-alliance-list">
                    {territoryCaptureRowsView.length === 0 && (
                      <p className="selection-meta">No alliances with flags/halls/forts in this plan.</p>
                    )}
                    {territoryCaptureRowsView.map((row) => {
                      const allianceId = String(row.allianceId || '');
                      const metrics = row.metrics || {};
                      return (
                        <div key={`territory-capture-${allianceId}`} className="border-alliance-card territory-capture-card">
                          <div className="border-alliance-card-header">
                            <div className="map-overlay-subtype-toggle active territory-capture-title">
                              <span
                                className="map-overlay-color-dot"
                                style={{
                                  backgroundColor: (() => {
                                    const parsed = parseAllianceTerritoryColor(row.territoryColor);
                                    return `rgb(${parsed.r}, ${parsed.g}, ${parsed.b})`;
                                  })()
                                }}
                              />
                              <span>N{row.nationNumber || '-'} [{row.acronym || '---'}] {row.name}</span>
                            </div>
                            <span className="territory-capture-count">T: {row.territoryBuildingCount}</span>
                          </div>
                          <div className="border-elite-summary">
                            {[...ELITE_MINE_LEVELS].reverse().map((level) => (
                              <span key={`capture-${allianceId}-elite-${level}`} className="border-elite-chip">
                                L{level}: {metrics?.eliteMineLevels?.[level] || 0}
                              </span>
                            ))}
                          </div>
                          <div className="border-metrics-grid">
                            {BENEFIT_SUBTYPE_KEYS.map((subTypeKey) => (
                              <div key={`capture-${allianceId}-benefit-${subTypeKey}`} className="border-metric-row">
                                <span>{BENEFIT_SUBTYPE_LABELS[subTypeKey] || subTypeKey}</span>
                                <span>{metrics?.benefitSubTypes?.[subTypeKey] || 0}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <button
          className={`map-hotkeys-toggle ${isHotkeyPanelOpen ? 'active' : ''}`}
          onClick={() => {
            setIsHotkeyPanelOpen((prev) => !prev);
            setHotkeyEditState(null);
          }}
          title={isHotkeyPanelOpen ? 'Hide Hotkeys' : 'Configure Hotkeys'}
        >
          <FaKeyboard className="button-icon" />
          <span>{isHotkeyPanelOpen ? 'Close Hotkeys' : 'Hotkeys'}</span>
        </button>
        {isHotkeyPanelOpen && (
          <div className="map-hotkeys-panel">
            <h4>Hotkey Configuration</h4>
            {hotkeyEditState && (
              <p className="map-hotkeys-editing">
                Press a key{hotkeyEditState.kind === 'tool' ? ' combo' : ''}...
                (Esc to cancel, Backspace/Delete to clear)
              </p>
            )}
            <div className="map-hotkeys-section">
              <h5>Tile Hotkeys</h5>
              {tileHotkeyRows.map((row) => (
                <div key={row.tileType} className="map-hotkeys-row">
                  <span className="map-hotkeys-label">{row.label}</span>
                  <span className="map-hotkeys-value">{row.hotkey ? row.hotkey.toUpperCase() : 'Unassigned'}</span>
                  <button
                    type="button"
                    className={`map-hotkeys-action ${hotkeyEditState?.kind === 'tile' && hotkeyEditState?.target === row.tileType ? 'editing' : ''}`}
                    onClick={() => setHotkeyEditState({ kind: 'tile', target: row.tileType })}
                    title="Edit hotkey"
                  >
                    <FaPen />
                  </button>
                  <button
                    type="button"
                    className="map-hotkeys-action"
                    onClick={() => clearTileHotkey(row.tileType)}
                    title="Clear hotkey"
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              ))}
            </div>
            <div className="map-hotkeys-section">
              <h5>Tool Hotkeys</h5>
              {toolHotkeyRows.map((row) => (
                <div key={row.action} className="map-hotkeys-row">
                  <span className="map-hotkeys-label">{row.label}</span>
                  <span className="map-hotkeys-value">{formatHotkeyLabel(toolHotkeys[row.action])}</span>
                  <button
                    type="button"
                    className={`map-hotkeys-action ${hotkeyEditState?.kind === 'tool' && hotkeyEditState?.target === row.action ? 'editing' : ''}`}
                    onClick={() => setHotkeyEditState({ kind: 'tool', target: row.action })}
                    title="Edit hotkey"
                  >
                    <FaPen />
                  </button>
                  <button
                    type="button"
                    className="map-hotkeys-action"
                    onClick={() => clearToolHotkey(row.action)}
                    title="Clear hotkey"
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="canvas-bottom-actions">
          <button
            className="canvas-navigate-button"
            onClick={() => setIsNavigationModalOpen(true)}
          >
            <FaMapMarkedAlt className="button-icon" />
            <span>Navigate ({centerCoords.gridX}, {centerCoords.gridY})</span>
          </button>
          <button
            className="canvas-find-button"
            onClick={() => setIsFindCommanderModalOpen(true)}
          >
            <FaSearch className="button-icon" />
            <span>Find Commander</span>
          </button>
        </div>
        {canWriteTiles && (
          <>
            <button
              className={`map-quick-add-toggle ${isQuickAddPanelOpen ? 'active' : ''}`}
              onClick={handleQuickAddPanelToggle}
              title={isQuickAddPanelOpen ? 'Hide Quick Add' : 'Show Quick Add'}
            >
              {isQuickAddPanelOpen ? <FaTimes className="button-icon" /> : <FaPlus className="button-icon" />}
              <span>{isQuickAddPanelOpen ? 'Close Add' : 'Add Tile'}</span>
            </button>
            {isQuickAddPanelOpen && (
              <div className="map-quick-add-panel">
                <div className="map-quick-add-header">
                  <div className="map-quick-add-header-top">
                    <h4>Quick Add</h4>
                    <button
                      type="button"
                      className="map-quick-add-hide"
                      onClick={() => setIsQuickAddPanelOpen(false)}
                    >
                      Hide
                    </button>
                  </div>
                  <p>Select tile type, then click map to place</p>
                </div>
                {quickAddTileGroups.map((group) => (
                  <div key={group.category} className="map-quick-add-group">
                    <div className="map-quick-add-group-label">{group.label}</div>
                    <div className="map-quick-add-buttons">
                      {group.options.map((option) => (
                        <button
                          key={option.key}
                          className={`map-quick-add-button ${selectedTileAddType === option.key ? 'active' : ''}`}
                          onClick={() => handleQuickAddTypeToggle(option.key)}
                          title={tileHotkeyByType[option.key]
                            ? `${option.label} (${tileHotkeyByType[option.key]})`
                            : option.label}
                        >
                          <img
                            src={loadedImages[`${option.key}.png`]?.src}
                            alt={option.label}
                            className="map-quick-add-image"
                          />
                          <span>{option.label}</span>
                          {tileHotkeyByType[option.key] && (
                            <span className="map-quick-add-hotkey">{tileHotkeyByType[option.key]}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {isOptimizePanelOpen && isSuperUser && (
          <div className="optimize-territory-panel">
            <h4>Optimizer</h4>
            <div className="optimize-mode-toggle" role="tablist" aria-label="Optimizer mode">
              <button
                type="button"
                className={`optimize-mode-button ${optimizeMode === 'territory' ? 'active' : ''}`}
                onClick={() => setOptimizeMode('territory')}
                disabled={isOptimizationBusy}
              >
                Territory Flags
              </button>
              <button
                type="button"
                className={`optimize-mode-button ${optimizeMode === 'hive' ? 'active' : ''}`}
                onClick={() => setOptimizeMode('hive')}
                disabled={isOptimizationBusy}
              >
                Hive Finder
              </button>
            </div>

            {optimizeMode === 'territory' && (
              <>
                <label className="optimize-territory-label">
                  Alliance
                  <select
                    className="optimize-territory-select"
                    value={optimizeAllianceId}
                    onChange={(event) => setOptimizeAllianceId(event.target.value)}
                    disabled={isOptimizationBusy}
                  >
                    {optimizeAlliances.length === 0 && (
                      <option value="">No placed alliance hall/flag available</option>
                    )}
                    {optimizeAlliances.map((alliance) => (
                      <option key={alliance._id} value={alliance._id}>
                        [{alliance.acronym}] {alliance.name}
                        {Number.isFinite(Number(alliance?.nationNumber)) && Number(alliance.nationNumber) > 0
                          ? ` (${alliance.nationNumber})`
                          : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="optimize-territory-label">
                  Max Flags (1-50)
                  <input
                    type="number"
                    min="1"
                    max="50"
                    className="optimize-territory-input"
                    value={optimizeFlagCount}
                    onChange={(event) => setOptimizeFlagCount(event.target.value)}
                    disabled={isOptimizationBusy}
                  />
                </label>
                <button
                  className="optimize-territory-advanced-toggle"
                  onClick={() => setShowOptimizeAdvanced((prev) => !prev)}
                  disabled={isOptimizationBusy}
                >
                  {showOptimizeAdvanced ? 'Hide Weights' : 'Show Weights'}
                </button>
                {showOptimizeAdvanced && (
                  <div className="optimize-territory-advanced">
                    <label className="optimize-territory-label">
                      Elite Mine Weight
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        className="optimize-territory-input"
                        value={optimizerWeights.eliteMine}
                        onChange={(event) => setOptimizerWeights((prev) => ({
                          ...prev,
                          eliteMine: event.target.value
                        }))}
                        disabled={isOptimizationBusy}
                      />
                    </label>
                    {Object.entries(BENEFIT_SUBTYPE_LABELS).map(([key, label]) => (
                      <label key={key} className="optimize-territory-label">
                        {label} Weight
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className="optimize-territory-input"
                          value={optimizerWeights.benefits[key] ?? ''}
                          onChange={(event) => setOptimizerWeights((prev) => ({
                            ...prev,
                            benefits: {
                              ...prev.benefits,
                              [key]: event.target.value
                            }
                          }))}
                          disabled={isOptimizationBusy}
                        />
                      </label>
                    ))}
                    <label className="optimize-territory-label">
                      Territory Weight
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="optimize-territory-input"
                        value={optimizerWeights.territory}
                        onChange={(event) => setOptimizerWeights((prev) => ({
                          ...prev,
                          territory: event.target.value
                        }))}
                        disabled={isOptimizationBusy}
                      />
                    </label>
                    <label className="optimize-territory-label">
                      Reach Weight
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="optimize-territory-input"
                        value={optimizerWeights.reach}
                        onChange={(event) => setOptimizerWeights((prev) => ({
                          ...prev,
                          reach: event.target.value
                        }))}
                        disabled={isOptimizationBusy}
                      />
                    </label>
                    <button
                      className="optimize-territory-reset"
                      onClick={() => setOptimizerWeights(OPTIMIZER_DEFAULTS)}
                      disabled={isOptimizationBusy}
                    >
                      Reset Defaults
                    </button>
                  </div>
                )}
                <button
                  className="optimize-territory-submit"
                  onClick={handleOptimizeTerritory}
                  disabled={isOptimizationBusy || !hasOptimizationContext || !optimizeAllianceId}
                >
                  {isOptimizingTerritory ? 'Running...' : 'Run Optimization'}
                </button>
              </>
            )}

            {optimizeMode === 'hive' && (
              <>
                <div className="optimize-territory-label">
                  Hive Target
                  <div className="hive-target-row">
                    <input
                      type="number"
                      className="optimize-territory-input"
                      placeholder="X"
                      value={hiveTargetX}
                      onChange={(event) => setHiveTargetX(event.target.value)}
                      disabled={isOptimizationBusy || isPickingHiveTarget}
                    />
                    <input
                      type="number"
                      className="optimize-territory-input"
                      placeholder="Y"
                      value={hiveTargetY}
                      onChange={(event) => setHiveTargetY(event.target.value)}
                      disabled={isOptimizationBusy || isPickingHiveTarget}
                    />
                  </div>
                </div>
                <button
                  className="optimize-territory-advanced-toggle"
                  onClick={() => setIsPickingHiveTarget((prev) => !prev)}
                  disabled={isOptimizationBusy}
                >
                  {isPickingHiveTarget ? 'Cancel Map Target' : 'Use Map Click As Target'}
                </button>
                <div className="optimize-territory-advanced">
                  <label className="optimize-territory-label">
                    Target Distance Weight
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="optimize-territory-input"
                      value={hiveWeights.targetDistance}
                      onChange={(event) => setHiveWeights((prev) => ({
                        ...prev,
                        targetDistance: event.target.value
                      }))}
                      disabled={isOptimizationBusy}
                    />
                  </label>
                  <label className="optimize-territory-label">
                    Center Distance Weight
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="optimize-territory-input"
                      value={hiveWeights.centerDistance}
                      onChange={(event) => setHiveWeights((prev) => ({
                        ...prev,
                        centerDistance: event.target.value
                      }))}
                      disabled={isOptimizationBusy}
                    />
                  </label>
                  <button
                    className="optimize-territory-reset"
                    onClick={() => setHiveWeights(HIVE_WEIGHT_DEFAULTS)}
                    disabled={isOptimizationBusy}
                  >
                    Reset Hive Weights
                  </button>
                </div>
                <button
                  className="optimize-territory-submit"
                  onClick={handleOptimizeHiveSearch}
                  disabled={isOptimizationBusy || !hasOptimizationContext}
                >
                  {isOptimizingHive ? 'Searching...' : 'Find Hive Locations'}
                </button>
                {hivePreviewCandidates.length > 0 && (
                  <div className="hive-candidate-list">
                    {hivePreviewCandidates.map((candidate, index) => (
                      <button
                        key={`hive-candidate-${candidate.origin?.x}-${candidate.origin?.y}-${index}`}
                        type="button"
                        className={`hive-candidate-item ${candidate.isSelected ? 'active' : ''}`}
                        onClick={() => focusHiveCandidate(candidate, index)}
                        disabled={isOptimizationBusy}
                      >
                        <span>#{candidate.rank}</span>
                        <span>
                          ({candidate.origin.x}, {candidate.origin.y})
                        </span>
                        <span>score {Number(candidate?.metrics?.score || 0).toFixed(4)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {!!optimizeStatusMessage && (
              <p className="optimize-territory-status">{optimizeStatusMessage}</p>
            )}
          </div>
        )}
        {(isMultiSelectMode || totalSelectedTiles > 0) && (
          <div className="multi-select-panel">
            <div className="multi-select-column">
              <h4>Select Filters</h4>
              <div className="multi-select-tool-row">
                <button
                  type="button"
                  className={`multi-select-tool-button ${multiSelectTool === 'square' ? 'active' : ''}`}
                  onClick={() => setMultiSelectTool('square')}
                >
                  Square
                </button>
                <button
                  type="button"
                  className={`multi-select-tool-button ${multiSelectTool === 'lasso' ? 'active' : ''}`}
                  onClick={() => setMultiSelectTool('lasso')}
                >
                  Lasso
                </button>
              </div>
              <p className="selection-meta">
                {multiSelectTool === 'lasso'
                  ? 'Drag to draw a lasso. Release to toggle objects inside.'
                  : 'Drag to draw a square. Click to toggle a single object.'}
              </p>
              {TILE_ENTITY_OPTIONS.map((option) => (
                <React.Fragment key={option.key}>
                  <label className="multi-select-item">
                    <input
                      type="checkbox"
                      checked={multiSelectFilters[option.key]}
                      onChange={() => toggleMultiSelectFilter(option.key)}
                    />
                    {option.label}
                  </label>
                  {option.key === 'benefit' && (
                    <div className="multi-select-subtype-list">
                      {BENEFIT_SUBTYPE_KEYS.map((subTypeKey) => (
                        <div key={`filter-benefit-${subTypeKey}`} className="multi-select-subtype-row">
                          <span>{BENEFIT_SUBTYPE_LABELS[subTypeKey] || subTypeKey}</span>
                          <span>{benefitSubtypeCounts[subTypeKey] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {option.key === 'eliteMine' && (
                    <div className="multi-select-subtype-list">
                      {ELITE_MINE_LEVELS.map((level) => (
                        <div key={`filter-elite-mine-level-${level}`} className="multi-select-subtype-row">
                          <span>Level {level}</span>
                          <span>{eliteMineLevelCounts[level] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="multi-select-column">
              <h4>Current Selection</h4>
              <p className="selection-meta">Total Selected: {totalSelectedTiles}</p>
              {TILE_ENTITY_OPTIONS.map((option) => (
                <React.Fragment key={`selected-${option.key}`}>
                  <div className="multi-select-count-item">
                    <span>{option.label}</span>
                    <span>{selectedTileTypeCounts[option.key] || 0}</span>
                  </div>
                  {option.key === 'benefit' && (
                    <div className="multi-select-subtype-list">
                      {BENEFIT_SUBTYPE_KEYS.map((subTypeKey) => (
                        <div key={`selected-benefit-${subTypeKey}`} className="multi-select-subtype-row">
                          <span>{BENEFIT_SUBTYPE_LABELS[subTypeKey] || subTypeKey}</span>
                          <span>{selectedBenefitSubtypeCounts[subTypeKey] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {option.key === 'eliteMine' && (
                    <div className="multi-select-subtype-list">
                      {ELITE_MINE_LEVELS.map((level) => (
                        <div key={`selected-elite-mine-level-${level}`} className="multi-select-subtype-row">
                          <span>Level {level}</span>
                          <span>{selectedEliteMineLevelCounts[level] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
              <button
                type="button"
                className="multi-select-clear-button"
                onClick={clearMultiSelectionState}
                disabled={!totalSelectedTiles && !isMultiSelectDragging}
              >
                Clear Selection
              </button>
              {totalSelectedTiles > 0 && (
                <button className="multi-select-delete-button" onClick={handlePendingDeleteSelection}>
                  Delete
                </button>
              )}
            </div>
          </div>
        )}
        {(isCopyMode || isCutMode) && (
          <div className={`copy-mode-panel ${isCutMode ? 'cut-mode-panel' : ''}`}>
            <h4>{isCutMode ? 'Cut Mode' : 'Copy Mode'}</h4>
            <p className="copy-mode-meta">
              {isCutMode
                ? (cutClipboardCount ? `Source: ${cutClipboardSummary}` : 'Click a populated tile to cut')
                : (copyClipboardCount ? `Source: ${copyClipboardSummary}` : 'Click a populated tile to copy')}
            </p>
            <p className="copy-mode-meta">
              {isCutMode ? 'Then click a destination tile to move the selection.' : 'Then click a destination tile to place.'}
            </p>
          </div>
        )}
        {isQuickAddMode && (
          <div className="quick-add-mode-panel">
            <h4>Quick Add Mode</h4>
            <p className="copy-mode-meta">
              In hand: {tileInformation[selectedTileAddType]?.niceName || selectedTileAddType}
              {selectedTileAddType === 'benefit' ? ` (${BENEFIT_SUBTYPE_LABELS[quickAddBenefitSubType] || quickAddBenefitSubType})` : ''}
            </p>
            <p className="copy-mode-meta">
              Click an empty tile to place.
            </p>
            {selectedTileAddType === 'benefit' && (
              <>
                <div className="benefit-subtype-cycle-row">
                  <button
                    type="button"
                    className="benefit-subtype-cycle-button"
                    onClick={() => cycleQuickAddBenefitSubType(-1)}
                    disabled={!canCycleBenefitSubTypes}
                  >
                    Prev
                  </button>
                  <span className="benefit-subtype-cycle-value">
                    {BENEFIT_SUBTYPE_LABELS[quickAddBenefitSubType] || quickAddBenefitSubType}
                  </span>
                  <button
                    type="button"
                    className="benefit-subtype-cycle-button"
                    onClick={() => cycleQuickAddBenefitSubType(1)}
                    disabled={!canCycleBenefitSubTypes}
                  >
                    Next
                  </button>
                </div>
                <p className="copy-mode-meta">
                  Shift + Scroll (desktop) or tap Prev/Next.
                </p>
              </>
            )}
          </div>
        )}
        {isDrawingMode && (
          <div className={`drawing-mode-panel ${isDrawingPanelCollapsed ? 'collapsed' : ''}`}>
            <div className="drawing-panel-header">
              <h4>Drawing Mode</h4>
              <button
                type="button"
                className="drawing-panel-collapse"
                onClick={() => setIsDrawingPanelCollapsed((prev) => !prev)}
              >
                {isDrawingPanelCollapsed ? 'Expand' : 'Collapse'}
              </button>
            </div>

            {isDrawingPanelCollapsed ? (
              <p className="copy-mode-meta">Mode: {drawingInteractionMode === 'select' ? 'Select/Edit' : drawingInteractionMode === 'erase' ? 'Erase' : 'Draw'}</p>
            ) : (
              <>
                <div className="drawing-interaction-row">
                  <button
                    type="button"
                    className={`drawing-interaction-button ${drawingInteractionMode === 'draw' ? 'active' : ''}`}
                    onClick={() => {
                      setDrawingInteractionMode('draw');
                      clearDrawingState();
                    }}
                  >
                    Draw
                  </button>
                  <button
                    type="button"
                    className={`drawing-interaction-button ${drawingInteractionMode === 'select' ? 'active' : ''}`}
                    onClick={() => {
                      setDrawingInteractionMode('select');
                      setDrawingDraft(null);
                    }}
                  >
                    Select/Edit
                  </button>
                  <button
                    type="button"
                    className={`drawing-interaction-button ${drawingInteractionMode === 'erase' ? 'active' : ''}`}
                    disabled={!canDeleteTiles}
                    onClick={() => {
                      setDrawingInteractionMode('erase');
                      clearDrawingState();
                    }}
                  >
                    Erase
                  </button>
                </div>

                {drawingInteractionMode === 'draw' && (
                  <>
                    <div className="drawing-tool-row">
                      {DRAWING_TOOL_OPTIONS.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          className={`drawing-tool-button ${activeDrawingTool === option.key ? 'active' : ''}`}
                          onClick={() => {
                            setActiveDrawingTool(option.key);
                            setDrawingDraft(null);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {activeDrawingTool !== 'text' && (
                      <label className="drawing-control-row">
                        <span>Color</span>
                        <input
                          type="color"
                          value={drawingColor}
                          onChange={(event) => setDrawingColor(event.target.value)}
                        />
                      </label>
                    )}
                    <label className="drawing-control-row">
                      <span>Width</span>
                      <input
                        type="range"
                        min="1"
                        max="8"
                        step="1"
                        value={drawingStrokeWidth}
                        onChange={(event) => setDrawingStrokeWidth(Number(event.target.value))}
                      />
                      <span>{drawingStrokeWidth}px</span>
                    </label>
                    {activeDrawingTool === 'text' && (
                      <>
                        <label className="drawing-control-row">
                          <span>Text</span>
                          <input
                            type="text"
                            className="drawing-text-input"
                            placeholder="Enter annotation..."
                            value={drawingTextValue}
                            maxLength={DRAWING_TEXT_MAX_LENGTH}
                            onChange={(event) => setDrawingTextValue(clampDrawingText(event.target.value))}
                          />
                        </label>
                        <label className="drawing-control-row">
                          <span>Text Color</span>
                          <input
                            type="color"
                            value={drawingTextColor}
                            onChange={(event) => setDrawingTextColor(event.target.value)}
                          />
                        </label>
                        <label className="drawing-control-row">
                          <span>Background</span>
                          <input
                            type="color"
                            value={drawingTextBackgroundColor}
                            onChange={(event) => setDrawingTextBackgroundColor(event.target.value)}
                          />
                        </label>
                        <p className="copy-mode-meta">{drawingTextValue.length}/{DRAWING_TEXT_MAX_LENGTH} characters</p>
                      </>
                    )}
                    <p className="copy-mode-meta">
                      {activeDrawingTool === 'text'
                        ? 'Drag on map to draw a text box.'
                        : 'Drag on map to draw.'}
                    </p>
                  </>
                )}

                {drawingInteractionMode === 'select' && (
                  <>
                    <p className="copy-mode-meta">Tap a drawing to select it, then drag to move.</p>
                    {selectedDrawing ? (
                      <div className="drawing-selected-card">
                        <p className="copy-mode-meta">
                          Selected: {selectedDrawing.kind === 'text' ? 'Text Box' : selectedDrawing.kind === 'free' ? 'Free Line' : 'Straight Line'}
                        </p>
                        {selectedDrawing.kind === 'text' && (
                          <>
                            <label className="drawing-control-row">
                              <span>Text</span>
                              <input
                                type="text"
                                className="drawing-text-input"
                                maxLength={DRAWING_TEXT_MAX_LENGTH}
                                value={selectedDrawingTextValue}
                                onChange={(event) => setSelectedDrawingTextValue(clampDrawingText(event.target.value))}
                              />
                            </label>
                            <label className="drawing-control-row">
                              <span>Text Color</span>
                              <input
                                type="color"
                                value={selectedDrawingTextColor}
                                onChange={(event) => setSelectedDrawingTextColor(event.target.value)}
                              />
                            </label>
                            <label className="drawing-control-row">
                              <span>Background</span>
                              <input
                                type="color"
                                value={selectedDrawingTextBackgroundColor}
                                onChange={(event) => setSelectedDrawingTextBackgroundColor(event.target.value)}
                              />
                            </label>
                            <p className="copy-mode-meta">{selectedDrawingTextValue.length}/{DRAWING_TEXT_MAX_LENGTH} characters</p>
                            <button
                              type="button"
                              className="drawing-secondary-action"
                              onClick={persistSelectedTextDrawingChanges}
                            >
                              Apply Text Changes
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="copy-mode-meta">No drawing selected.</p>
                    )}
                  </>
                )}

                {drawingInteractionMode === 'erase' && (
                  <p className="copy-mode-meta">Tap any drawing on the map to delete it.</p>
                )}
              </>
            )}
          </div>
        )}
        {benefitQuickAddTipPosition && (
          <div
            className="benefit-quick-add-tip"
            style={{
              left: `${benefitQuickAddTipPosition.left}px`,
              top: `${benefitQuickAddTipPosition.top}px`
            }}
          >
            Shift + Scroll to change
          </div>
        )}
        <canvas
          ref={canvasRef}
          onWheel={handleWheel}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      <AddCityModal
        selectedNationId={selectedNation?._id}
        selectedPlanId={selectedPlan?._id}
        commanderNationOptions={addCityCommanderNationOptions}
        isOpen={isAddCityModalOpen}
        onRequestClose={handleCancelPendingQuickAddPlacement}
        cityPlacementByCommanderId={cityPlacementByCommanderId}
        onSelectCommander={(commander, options = {}) => {
          const additionalTileInfo = { commander };
          handleConfirmPendingQuickAddPlacement(additionalTileInfo, {
            allowRelocateExisting: !!options.allowRelocateExisting
          });
        }}
      />
      <AddAllianceBuildingModal
        selectedNationId={selectedNation?._id}
        allianceNationOptions={addCityCommanderNationOptions}
        isOpen={isAddAllianceBuildingModalOpen}
        selectedTileAddType={selectedTileAddType}
        alliancePlacementByType={alliancePlacementByType}
        onRequestClose={handleCancelPendingQuickAddPlacement}
        onSelectAlliance={(alliance, options = {}) => {
          const additionalTileInfo = { alliance };
          handleConfirmPendingQuickAddPlacement(additionalTileInfo, {
            allowRelocateExisting: !!options.allowRelocateExisting
          });
        }}
      />
      <AddEliteMineModal
        isOpen={isAddEliteMineModalOpen}
        onRequestClose={handleCancelPendingQuickAddPlacement}

        onAddEliteMine={(level) => {
          const additionalTileInfo = { "level": level }
          handleConfirmPendingQuickAddPlacement(additionalTileInfo)
        }}

      />
      <AddBenefitBuildingModal
        isOpen={isAddBenefitBuildingModalOpen}
        onRequestClose={handleCancelPendingQuickAddPlacement}
        onAddBenefitBuilding={(subtype) => {
          handleConfirmPendingQuickAddPlacement(subtype)
        }}

      />
      <TileDetailsModal
        isOpen={isTileDetailsModalOpen}
        selectedTile={selectedTile}
        onRequestClose={() => setIsTileDetailsModalOpen(false)}
        onDeleteTile={handleDeleteTile}
        onDeleteAllianceTerritory={handleDeleteAllianceTerritory}
        canWriteTiles={canWriteTiles}
        onToggleConfirmedLocation={handleToggleTileConfirmed}
      />
      <NavigationModal
        isOpen={isNavigationModalOpen}
        onRequestClose={() => setIsNavigationModalOpen(false)}
        onNavigate={handleNavigate}
      />
      <FindCommanderModal
        isOpen={isFindCommanderModalOpen}
        onRequestClose={() => setIsFindCommanderModalOpen(false)}
        onSearch={handleSearchCommanderLocations}
        onNavigate={(result) => {
          const x = Number(result?.x);
          const y = Number(result?.y);
          if (Number.isFinite(x) && Number.isFinite(y)) {
            handleNavigate(x, y);
          }
        }}
      />
    </div >
  );

};

export default IsometricGrid;
